import type { FileMetadataMap } from '@Core/lib/tasks/utils/fileTypes';
import type {
  PageRouteSettings as CorePageRouteSettings,
  TaskBrandLogoOptions,
  TaskClassValue,
  TaskClipboardCopyOptions,
  TaskConfettiOptions,
  TaskDateAddUnit,
  TaskDateBoundaryUnit,
  TaskLocationFormatOptions,
  TaskLocationValue,
  TaskMoney,
  TaskMoneyFormatOptions,
  TaskMoneyInput,
  TaskNavigateOptions,
  TaskNotifyToastOptions,
  TaskNotifyVariant,
  TaskNumberFormatOptions,
  TaskPhoneFormatOptions,
  TaskApiErrorBody,
  TaskRouter,
  TaskRouterVisitOptions,
  TaskTextGenerateOptions,
  TaskTimeAddUnit,
  TaskTimeBoundaryUnit,
  TaskTiptapParseInput,
  TaskUtils,
  TaskWorkflowTarget,
  TaskWorkspaceFields,
  WorkflowRouteSettings as CoreWorkflowRouteSettings
} from '@Core/lib/tasks/utils/types';
import type { RelationVisibility } from '@Data/hooks/resolveRecordFormField';
import type { FormDataType } from '@inertiajs/core';
import type { InertiaFormProps } from '@inertiajs/react';
import type { CalendarBackgroundTime } from '@Pages/pageRuntime/components/calendar/calendarTypes';
import type {
  UseCalendarEventsOptions,
  UseCalendarEventsResult
} from '@Pages/pageRuntime/components/calendar/useCalendarEvents';
import type {
  UseCalendarSelectionOptions,
  UseCalendarSelectionResult
} from '@Pages/pageRuntime/components/calendar/useCalendarSelection';
import type { UseCalendarViewResult } from '@Pages/pageRuntime/components/calendar/useCalendarView';
import type {
  UseNewCalendarEventOptions,
  UseNewCalendarEventResult
} from '@Pages/pageRuntime/components/calendar/useNewCalendarEvent';
import type { componentRegistry } from '@Pages/pageRuntime/components/registry';
import type { CreateResourceRecordFormResource } from '@Pages/pageRuntime/hooks/useCreateResourceRecordForm';
import type {
  UseDataTableOptions,
  UseDataTableResult
} from '@Pages/pageRuntime/hooks/useDataTable';
import type {
  DeleteResourceRecordResource,
  DeleteResourceRecordState
} from '@Pages/pageRuntime/hooks/useDeleteResourceRecord';
import type {
  QueryParams,
  QueryParamSchema,
  QueryParamsFromSchema,
  SetQueryParams
} from '@Pages/pageRuntime/hooks/useQueryParams';
import type {
  ResourceDataTableResource,
  UseResourceDataTableOptions,
  UseResourceDataTableResult
} from '@Pages/pageRuntime/hooks/useResourceDataTable';
import type {
  RestoreResourceRecordResource,
  RestoreResourceRecordState
} from '@Pages/pageRuntime/hooks/useRestoreResourceRecord';
import type {
  SharedFiltersApi,
  UseSharedFiltersOptions as SdkUseSharedFiltersOptions
} from '@Pages/pageRuntime/hooks/useSharedFilters';
import type { UseStepperOptions, UseStepperResult } from '@Pages/pageRuntime/hooks/useStepper';
import type { UseThemeResult } from '@Pages/pageRuntime/hooks/useTheme';
import type { UpdateResourceRecordFormResource } from '@Pages/pageRuntime/hooks/useUpdateResourceRecordForm';
import type { AxiosRequestConfig } from 'axios';
import type React from 'react';

import {
  DynamicDisplayType,
  DynamicFieldType,
  type RecordCollapse,
  type RecordScope
} from '@/types/enums';

export type ComponentRegistry = typeof componentRegistry;

export type CustomComponentRegistry = Record<string, React.ComponentType>;

declare const sdkResourceRelationCatalog: unique symbol;
declare const sdkResourceRecordCatalog: unique symbol;

/**
 * Field & resource shapes for the page SDK.
 *
 * The field shapes (`SdkResourceField` and its settings) mirror the backend
 * `Diagonal.Pages.Data.PageFieldData` shape — identical snake_case attribute
 * names, no bespoke camelCase or `sdkKey`. They are declared locally rather than
 * aliased to the generated `Diagonal.Pages.Data.*` types because the author-facing
 * SDK `.dts` flattener degrades every `Diagonal.*` reference to `any`, which would
 * strip `resource.fields.*` typing in the editor. The PHP DTOs are the single
 * source of truth for the wire shape; keep these in lockstep with them.
 */
export type SdkResourceFieldSettingsUi = {
  field_type?: DynamicFieldType;
  display_type?: DynamicDisplayType;
  /** Display-specific format. Number fields accept `grouped` or `plain`; year-like
   * field keys default to `plain`. */
  display_format?: string;
  options?: unknown[];
  placeholder?: string;
  language?: string;
  show_on_creating?: boolean;
  show_on_updating?: boolean;
  show_formatting_toolbar?: boolean;
  toolbar_placement?: 'floating' | 'fixed';
  allow_inline_create_on_creating?: boolean;
  allow_inline_attach_on_creating?: boolean;
  allow_inline_create_on_updating?: boolean;
  allow_inline_attach_on_updating?: boolean;
  /**
   * Runtime-only: resolved inline-create/attach booleans injected client-side by
   * `resolveRecordFormField` in the record-form hooks. Not part of the backend
   * `PageFieldSettingsUiData` wire shape — populated after the catalog is built and
   * read by `FieldInput` as `resolvedValues` for relation fields.
   */
  injections?: Record<string, unknown>;
};

export type SdkResourceFieldSettings = {
  currency?: string;
  integer?: boolean;
  minimum?: number | null;
  maximum?: number | null;
  multi_line?: boolean;
  placeholder?: string;
  yes_label?: string;
  no_label?: string;
  accept?: string[];
  visibility?: string;
  options?: Record<string, unknown>[];
  sort_order?: number | null;
  /** Item definition for list fields. */
  schema?: SdkListItemField | SdkListItemField[];
  /** Marks a Markdown field accepts. Absent allows every mark. */
  allowed_marks?: string[];
  /** Blocks a Markdown field accepts. Absent allows every block. */
  allowed_blocks?: string[];
  /** Relation pointer — the SDK dereferences `resource_id` against the flat catalog. */
  resource_id?: string;
  type?: string;
  foreign_key?: string;
  max_connections?: number | null;
  /** Field id of the child's back-reference that a has_many or has_one stores under. */
  inverse_of?: string;
  ui?: SdkResourceFieldSettingsUi;
};

export type SdkListItemField = {
  key: string;
  type: string;
  label?: string | null;
  description?: string | null;
  guard?: unknown[] | null;
  settings?: SdkResourceFieldSettings;
};

export type SdkResourceField = {
  id: string | null;
  key: string;
  label: string | null;
  type: string;
  resource_id: string;
  policy_scope: number | null;
  is_system_field: boolean;
  is_system_managed: boolean;
  /** Whether the field is the resource's configured roles or permissions relation. */
  is_authorization_relation: boolean;
  is_virtual_field: boolean;
  is_exportable: boolean;
  is_required: boolean;
  guard: Record<string, unknown> | null;
  /** The field's configured description — render it as help text under inputs. */
  description: string | null;
  settings: SdkResourceFieldSettings;
  /** Dotted accessor path; equals `key` at the top level, `parent.key` once nested. */
  path: string;
  /** A Boolean field's configured default; form checkboxes start from it. */
  default_value?: boolean | null;
  /** Relation-only decoration, filled lazily by the SDK builder from the flat catalog. */
  display_field_key?: string | null;
  column_label?: string | null;
  fields?: Record<string, SdkResourceField> | null;
};

/**
 * Resolved field mappings for a single capability on a resource.
 * Keys are camelCase CapabilityFieldReference names (e.g. `eventScheduleField`);
 * values are the corresponding resolved fields. Only mappings that were configured
 * and whose field is visible in the catalog are present.
 */
export type SdkResourceCapabilityFieldMappings = Record<string, SdkResourceField>;

/**
 * A single resource entry as sent by PHP in the `resources` prop. Derived from the
 * generated `Diagonal.Pages.Data.PageResourceData` so the scalar envelope
 * (`id`/`name`/`capabilities`/…) stays in lockstep with the PHP DTO. Only `fields` is
 * overridden: the generated DTO keys fields by `key` as `Diagonal.Pages.Data.PageFieldData`,
 * but the SDK needs the local {@link SdkResourceField} — it survives the author-facing
 * `.dts` flattener (which widens every `Diagonal.*` reference to `any`) and carries the
 * SDK's runtime-only field decorations. The SDK builder turns this into a {@link SdkResource}.
 */
export type PageResourceData = Omit<Diagonal.Pages.Data.PageResourceData, 'fields' | 'slug'> & {
  slug?: string;
  fields: Record<string, SdkResourceField>;
};

/**
 * A single-column sort as a field path plus direction. Saved views store an ordered
 * list of these (`useSharedFilters().sorts`). A descriptor or a list of descriptors is
 * accepted anywhere a sort input is.
 */
export type ResourceSortDescriptor = { key: string; direction: 'asc' | 'desc' };

/** {@link ResourceSortDescriptor} with `field` in place of `key`. */
export type ResourceSortFieldDescriptor = {
  field: string;
  direction: 'asc' | 'desc';
};

/** A direction value in an explicit `$sort` map. */
export type ResourceSortDirection = 1 | -1 | 'asc' | 'desc';

/**
 * Sort input accepted by `list`/`search`: a field key (`'name'`), a `-`-prefixed key
 * for descending (`'-created_at'`), an array of keys (`['status', '-created_at']`), a
 * {@link ResourceSortDescriptor} (`{ key: 'created_at', direction: 'desc' }`, or `field`
 * in place of `key`), an
 * ordered list of descriptors (`[{ key: 'status', direction: 'asc' }, { key: 'title',
 * direction: 'desc' }]`), or an explicit direction map (`{ created_at: -1 }`). The SDK
 * normalizes all six forms into the ordered `$sort` direction map before calling the
 * records API. Earlier entries sort first.
 */
export type ResourceSortInput =
  | string
  | string[]
  | ResourceSortDescriptor
  | ResourceSortFieldDescriptor
  | (ResourceSortDescriptor | ResourceSortFieldDescriptor)[]
  | Record<string, ResourceSortDirection>;

/**
 * Params for {@link SdkResource.list}, sent as query-string parameters. The SDK
 * renames `perPage` → `per_page` before sending.
 *
 * The list endpoint evaluates pagination, `relations`, and `$scope` only — it has no
 * filtering or sorting. Passing `filter`, `$filter`, `sort`, or `$sort` throws rather
 * than quietly returning an unfiltered page; use {@link SdkResource.search} (or
 * `Sdk.hooks.useRecords`) for filtered or sorted reads.
 */
export type ResourceListParams<TRelations extends readonly string[] = readonly string[]> = {
  page?: number;
  perPage?: number;
  per_page?: number;
  relations?: TRelations;
  /** Soft-delete subset to read from. Defaults to active records only. */
  $scope?: RecordScope;
  [key: string]: unknown;
};

/**
 * Body for {@link SdkResource.search}. The SDK renames the convenience keys before
 * POSTing — `filter` → `$filter`, `sort` → `$sort` (any {@link ResourceSortInput}
 * form), `perPage` → `per_page` — and the API rejects unknown body keys with a 422.
 *
 * `filter` is a **rename only**: the value must already be a structured filter tree of
 * the form `{ operator: 'and' | 'or' | 'nor', conditions: [...] }`, where each
 * condition is a leaf (`{ field, operator, value }`) or another nested group, and a
 * bare array of conditions is treated as an implicit `and` group. Flat maps such as
 * `{ status: 'paid' }` are never translated and are rejected with a 422. Condition
 * operators are the symbol or snake_case enum values — `=`, `!=`, `>`, `>=`, `<`,
 * `<=`, `contains`, `starts_with`, `ends_with`, `in`, `not_in`, `between`, `is_null`,
 * `is_not_null`, `exists`, `search` — word forms like `eq` are rejected.
 *
 * @example
 * ```ts
 * await Sdk.resource('invoices').search({
 *   filter: {
 *     operator: 'and',
 *     conditions: [
 *       { field: 'status', operator: '=', value: 'paid' },
 *       {
 *         operator: 'or',
 *         conditions: [
 *           { field: 'total', operator: '>=', value: 100 },
 *           { field: 'priority', operator: 'in', value: ['high', 'urgent'] }
 *         ]
 *       }
 *     ]
 *   },
 *   sort: '-created_at',
 *   perPage: 25
 * });
 * ```
 */
export type ResourceSearchBody<TRelations extends readonly string[] = readonly string[]> = {
  /** Exact field/value matches sent as `$match`. */
  $match?: Record<string, unknown>;
  /** Full-text search term sent as `$search`. */
  $search?: string;
  /** Structured `$filter` tree (renamed, not translated — flat maps are rejected). */
  filter?: Record<string, unknown>;
  /** Pre-built structured filter tree, sent as-is. */
  $filter?: Record<string, unknown>;
  /** Convenience sort input, normalized to `$sort` — see {@link ResourceSortInput}. */
  sort?: ResourceSortInput;
  /** Explicit `$sort` direction map, sent as-is. */
  $sort?: Record<string, string | number>;
  /** Soft-delete subset to read from; renamed to `$scope`. */
  scope?: RecordScope;
  /** Explicit backend record scope, sent as-is. */
  $scope?: RecordScope;
  /** One record for each repeating event; renamed to `$collapse`. See {@link RecordCollapse}. */
  collapse?: RecordCollapse;
  /** Explicit backend collapse, sent as-is. */
  $collapse?: RecordCollapse;
  /** Page number to return. Defaults to 1. */
  page?: number;
  /** Records per page (maximum 500, defaults to 15); renamed to `per_page`. */
  perPage?: number;
  /** Records per page (maximum 500, defaults to 15). */
  per_page?: number;
  /** Relation paths to load alongside each record. */
  relations?: TRelations;
  [key: string]: unknown;
};

/** Options for the server-side record count endpoint. */
export type ResourceCountOptions = {
  /** Structured filter tree, JSON-encoded so boolean and null values reach the server unchanged. */
  filter?: Record<string, unknown>;
  /** Pre-built structured filter tree, sent as-is. */
  $filter?: Record<string, unknown>;
  /** Soft-delete subset to count. Defaults to active records only. */
  scope?: RecordScope;
  /** Explicit backend scope alias, sent as-is. */
  $scope?: RecordScope;
};

/**
 * Options for {@link SdkResource.searchAll}. `maxRecords` is deliberately required:
 * this helper is for bounded client-side aggregates over a known filtered slice, not
 * for rendering an unbounded collection.
 */
export type ResourceSearchAllOptions<TRelations extends readonly string[] = readonly string[]> = {
  $match?: Record<string, unknown>;
  $search?: string;
  filter?: Record<string, unknown>;
  $filter?: Record<string, unknown>;
  sort?: ResourceSortInput;
  $sort?: Record<string, string | number>;
  relations?: TRelations;
  scope?: RecordScope;
  $scope?: RecordScope;
  collapse?: RecordCollapse;
  $collapse?: RecordCollapse;
  perPage?: number;
  per_page?: number;
  /** Maximum records to return (1–5,000). A warning is logged when matches exceed it. */
  maxRecords: number;
};

/** Pagination metadata returned by the record list and search endpoints. */
export type ResourcePagination = {
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
  from: number;
  to: number;
  first_page_url: string;
  last_page_url: string;
  next_page_url: string | null;
  prev_page_url: string | null;
};

/** Parsed response body returned by record list and search requests. */
export type ResourceCollectionResult<TRecord extends ResourceRecord = ResourceRecord> = {
  data: TRecord[];
  pagination: ResourcePagination;
  files?: FileMetadataMap;
};

/** Parsed response body returned by collection mutations such as bulk updates. */
export type ResourceRecordsResult<TRecord extends ResourceRecord = ResourceRecord> = {
  data: TRecord[];
};

/** Parsed response body returned by singular record operations. */
export type ResourceRecordResult<TRecord extends ResourceRecord = ResourceRecord> = {
  data: TRecord;
  /**
   * `series` is set on a save that changed a repeating series, and `booking` on a create on a
   * Booking resource that returned the first booking of a retry. Both are absent otherwise.
   */
  meta?: { series?: SdkSeriesChangeResult; booking?: SdkBookingCreateResult };
};

export type ResourceRecordPayload<TRecord extends ResourceRecord = ResourceRecord> =
  Partial<TRecord> & Record<string, unknown>;

export type ResourceBulkUpdateRecord<TRecord extends ResourceRecord = ResourceRecord> =
  ResourceRecordPayload<TRecord> & { id: string };

export type ResourceDeletePayload = {
  /**
   * Capability-specific operation options. Recurring eventable deletes accept
   * `{ eventable: { scope: 'this' | 'this_and_future' | 'all' } }`.
   */
  $operation?: Record<string, Record<string, unknown>>;
};

export type ResourceOperationRecord<TRecord extends ResourceRecord = ResourceRecord> = TRecord;

/**
 * The data API endpoint URLs for a resource. Collection-scoped actions
 * (`index`, `search`, `count`, `store`, `bulk`) are ready-to-use URL strings; record-scoped
 * actions (`show`, `update`, `delete`, `restore`) and the field-schema URL (`field`)
 * are builders that take an id and return the URL. Pair these with
 * {@link SdkContract.api} for requests the bundled handle methods don't cover.
 */
export type ResourceUrls = {
  /** `GET` list records — `/api/data/resources/{id}/records`. */
  index: string;
  /** `POST` search records — `/api/data/resources/{id}/record/search`. */
  search: string;
  /** `GET` count records — `/api/data/resources/{id}/records/count`. */
  count: string;
  /** `POST` create a record — `/api/data/resources/{id}/records`. */
  store: string;
  /** `PUT` update multiple records — `/api/data/resources/{id}/records/bulk`. */
  bulk: string;
  /** `GET` one record — `/api/data/resources/{id}/records/{record}`. */
  show(recordId: string): string;
  /** `PUT` update a record — `/api/data/resources/{id}/records/{record}`. */
  update(recordId: string): string;
  /** `DELETE` soft-delete a record — `/api/data/resources/{id}/records/{record}`. */
  delete(recordId: string): string;
  /** `POST` restore a soft-deleted record — `/api/data/resources/{id}/records/{record}/restore`. */
  restore(recordId: string): string;
  /**
   * `PUT` update a field's schema — `/api/data/resources/{id}/fields/{field}`.
   * Requires the `manage_fields` permission; prefer
   * {@link SdkResourceOperations.updateField} over calling this directly.
   */
  field(fieldId: string): string;
};

/**
 * Payload for {@link SdkResourceOperations.updateField}. Send only the attributes
 * being changed. `settings` replaces the field's stored settings wholesale rather
 * than merging, so build it from the field's current `settings` and include every
 * key that should survive. `type` is required by the endpoint for type-specific
 * settings (e.g. select `options`); `updateField` fills it in from the field's
 * current type when omitted.
 */
export type ResourceFieldUpdatePayload = {
  type?: string;
  label?: string | null;
  description?: string | null;
  guard?: unknown;
  settings?: Record<string, unknown>;
} & Record<string, unknown>;

export type SdkDndIdentifier = string | number;

export type SdkDndSortableItem = {
  id: SdkDndIdentifier;
};

export type SdkDndItemIdentifier<TItem> = TItem extends {
  id: infer TIdentifier;
}
  ? Extract<TIdentifier, SdkDndIdentifier>
  : SdkDndIdentifier;

export type SdkDndSortableOptions<TItem> = {
  /** Return the stable, unique drag id for an item. Items with an `id` do not need this option. */
  getItemId: (item: TItem) => SdkDndIdentifier;
};

export type SdkDndReorderChange<TItem> = {
  id: SdkDndItemIdentifier<TItem>;
  item: TItem;
  previousIndex: number;
  index: number;
};

export type SdkDndReorderResult<TItem> = {
  /** The complete array after the move. */
  items: TItem[];
  /** Only items whose array index changed. */
  changes: SdkDndReorderChange<TItem>[];
};

export type SdkDndSortableRenderState = {
  index: number;
  isDragging: boolean;
};

export type SdkDndLocation = {
  index: number;
};

export type SdkDndGroupedLocation = SdkDndLocation & {
  groupId: SdkDndIdentifier;
};

export type SdkDndMoveContext<TItem, TLocation extends SdkDndLocation = SdkDndLocation> = {
  item: TItem;
  source: TLocation;
  destination: TLocation;
  /** Items in the source collection when the drag began, including the active item. */
  sourceItems: readonly TItem[];
  /** Items currently in the destination collection, excluding the active item. */
  destinationItems: readonly TItem[];
};

export type SdkDndCanDrag<TItem, TLocation extends SdkDndLocation = SdkDndLocation> = (
  item: TItem,
  source: TLocation
) => boolean;

export type SdkDndCanDrop<TItem, TLocation extends SdkDndLocation = SdkDndLocation> = (
  context: SdkDndMoveContext<TItem, TLocation>
) => boolean;

export type SdkDndSortableListCommonProps<TItem> = {
  items: readonly TItem[];
  onError?: (error: unknown) => void;
  renderItem: (item: TItem, state: SdkDndSortableRenderState) => React.ReactNode;
  /** Optional for objects with an `id`; required at runtime for every other item shape. */
  getItemId?: (item: TItem) => SdkDndIdentifier;
  /** Return a nonempty accessible label for each item. Omit the callback for positional labels. */
  getItemLabel?: (item: TItem) => string;
  /** Return false to prevent an item from being picked up. */
  canDrag?: SdkDndCanDrag<TItem>;
  /** Return false to reject a proposed destination index. */
  canDrop?: SdkDndCanDrop<TItem>;
  ariaLabel?: string;
  layout?: 'vertical' | 'horizontal' | 'grid';
  className?: string;
  itemClassName?: string;
  handleClassName?: string;
  disabled?: boolean;
};

export type SdkDndCustomPersistence<TItem> = {
  /** Receives the complete reordered list immediately after a successful drop. */
  onItemsChange: (items: TItem[]) => void;
  resource?: never;
  sortField?: never;
  /**
   * Receives only changed positions plus the full result. Returning a rejected promise
   * rolls the optimistic list back and calls `onError`.
   */
  onReorder?: (
    changes: SdkDndReorderChange<TItem>[],
    result: SdkDndReorderResult<TItem>
  ) => unknown | Promise<unknown>;
};

export type SdkDndResourcePersistence<TItem> = {
  /**
   * Optional observer for the complete reordered list. Resource mode owns its optimistic
   * order and reconciles refreshed resource records whether this callback is present or not.
   */
  onItemsChange?: (items: TItem[]) => void;
  /** Resource handle whose existing bulk-update endpoint persists changed positions. */
  resource: Pick<SdkResourceOperations, 'updateMany'>;
  /** Stored Number field key or field handle used for ordering this list. */
  sortField: SdkFieldSelector;
  onReorder?: never;
};

export type SdkDndSortableListProps<TItem> = SdkDndSortableListCommonProps<TItem> &
  (SdkDndCustomPersistence<TItem> | SdkDndResourcePersistence<TItem>);

type SdkDndResourceItem<TResource extends SdkResourceType = SdkResourceType> =
  ResourceRecordFrom<TResource> & { id: string };

type SdkDndNumberFieldKey<TItem extends ResourceRecord> = string extends keyof TItem
  ? string
  : Extract<
      {
        [TKey in keyof TItem]-?: NonNullable<TItem[TKey]> extends number ? TKey : never;
      }[keyof TItem],
      string
    >;

export type SdkDndSortableListResourceProps<
  TResource extends SdkResourceType & Pick<SdkResource, 'fields' | 'updateMany'>,
  TItem extends SdkDndResourceItem<NoInfer<TResource>> = SdkDndResourceItem<TResource>
> = SdkDndSortableListCommonProps<TItem> & {
  onItemsChange?: (items: TItem[]) => void;
  resource: TResource;
  sortField: SdkFieldSelector<
    Extract<
      SdkDndNumberFieldKey<ResourceRecordFrom<NoInfer<TResource>>>,
      keyof NoInfer<TResource>['fields']
    >
  >;
  onReorder?: never;
};

export type SdkDndSortableListComponent = {
  <
    TResource extends SdkResourceType & Pick<SdkResource, 'fields' | 'updateMany'>,
    TItem extends SdkDndResourceItem<NoInfer<TResource>>
  >(
    props: SdkDndSortableListResourceProps<TResource, TItem>
  ): React.ReactNode;
  <TItem>(
    props: SdkDndSortableListCommonProps<TItem> & SdkDndCustomPersistence<TItem>
  ): React.ReactNode;
};

export type SdkDndSortableGroupRenderState<TItem> = {
  id: SdkDndIdentifier;
  items: readonly TItem[];
  isOver: boolean;
  children: React.ReactNode;
};

export type SdkDndGroupMoveChange<TItem> = {
  id: SdkDndItemIdentifier<TItem>;
  item: TItem;
  source: SdkDndGroupedLocation;
  destination: SdkDndGroupedLocation;
};

export type SdkDndGroupMoveResult<TItem> = {
  /** All items flattened in group order after the move. */
  items: TItem[];
  /** Only items whose group or index changed. */
  changes: SdkDndGroupMoveChange<TItem>[];
};

export type SdkDndSortableGroupsCommonProps<TItem, TGroup> = {
  items: readonly TItem[];
  groups: readonly TGroup[];
  getGroupId: (group: TGroup) => SdkDndIdentifier;
  renderGroup: (group: TGroup, state: SdkDndSortableGroupRenderState<TItem>) => React.ReactNode;
  renderItem: (item: TItem, state: SdkDndSortableRenderState) => React.ReactNode;
  /** Optional for objects with an `id`; required at runtime for every other item shape. */
  getItemId?: (item: TItem) => SdkDndIdentifier;
  /** Return a nonempty accessible label for each item. Omit the callback for positional labels. */
  getItemLabel?: (item: TItem) => string;
  getGroupLabel?: (group: TGroup) => string;
  canDrag?: SdkDndCanDrag<TItem, SdkDndGroupedLocation>;
  canDrop?: SdkDndCanDrop<TItem, SdkDndGroupedLocation>;
  onError?: (error: unknown) => void;
  ariaLabel?: string;
  className?: string;
  groupClassName?: string;
  itemsClassName?: string;
  itemClassName?: string;
  handleClassName?: string;
  disabled?: boolean;
};

export type SdkDndSortableGroupsCustomPersistence<TItem, TGroup> = {
  getItemGroupId: (item: TItem) => SdkDndIdentifier;
  setItemGroupId: (item: TItem, groupId: SdkDndIdentifier, group: TGroup) => TItem;
  onItemsChange: (items: TItem[]) => void;
  resource?: never;
  groupField?: never;
  sortField?: never;
  /**
   * Receives only changed group positions plus the complete result. A rejected promise
   * rolls the optimistic groups back and calls `onError`.
   */
  onMove?: (
    changes: SdkDndGroupMoveChange<TItem>[],
    result: SdkDndGroupMoveResult<TItem>
  ) => unknown | Promise<unknown>;
};

export type SdkDndSortableGroupsResourcePersistence<TItem, TGroup> = {
  /** Override how a stored group-field value maps to a group id. */
  getItemGroupId?: (item: TItem) => SdkDndIdentifier;
  /**
   * Optionally build the optimistic item value for a destination group. Use this when
   * presentation groups are not the stored relation records. The SDK still persists the
   * destination group id and owns the bulk update, rollback, and cache refresh.
   */
  setItemGroupId?: (item: TItem, groupId: SdkDndIdentifier, group: TGroup) => TItem;
  onItemsChange?: (items: TItem[]) => void;
  resource: Pick<SdkResourceOperations, 'updateMany'>;
  /** Stored field key or handle that assigns an item to a group. */
  groupField: SdkFieldSelector;
  /** Stored Number field key or handle used for ordering within each group. */
  sortField: SdkFieldSelector;
  onMove?: never;
};

export type SdkDndSortableGroupsProps<TItem, TGroup> = SdkDndSortableGroupsCommonProps<
  TItem,
  TGroup
> &
  (
    | SdkDndSortableGroupsCustomPersistence<TItem, TGroup>
    | SdkDndSortableGroupsResourcePersistence<TItem, TGroup>
  );

export type SdkDndSortableGroupsResourceProps<
  TResource extends SdkResourceType & Pick<SdkResource, 'fields' | 'updateMany'>,
  TItem extends SdkDndResourceItem<NoInfer<TResource>>,
  TGroup
> = SdkDndSortableGroupsCommonProps<TItem, TGroup> & {
  getItemGroupId?: (item: TItem) => SdkDndIdentifier;
  setItemGroupId?: (item: TItem, groupId: SdkDndIdentifier, group: TGroup) => TItem;
  onItemsChange?: (items: TItem[]) => void;
  resource: TResource;
  groupField: SdkFieldSelector<Extract<keyof NoInfer<TResource>['fields'], string>>;
  sortField: SdkFieldSelector<
    Extract<
      SdkDndNumberFieldKey<ResourceRecordFrom<NoInfer<TResource>>>,
      keyof NoInfer<TResource>['fields']
    >
  >;
  onMove?: never;
};

export type SdkDndSortableGroupsComponent = {
  <
    TResource extends SdkResourceType & Pick<SdkResource, 'fields' | 'updateMany'>,
    TItem extends SdkDndResourceItem<NoInfer<TResource>>,
    TGroup
  >(
    props: SdkDndSortableGroupsResourceProps<TResource, TItem, TGroup>
  ): React.ReactNode;
  <TItem, TGroup>(
    props: SdkDndSortableGroupsCommonProps<TItem, TGroup> &
      SdkDndSortableGroupsCustomPersistence<TItem, TGroup>
  ): React.ReactNode;
};

export type SdkDndReorder = {
  <TItem extends SdkDndSortableItem>(
    items: readonly TItem[],
    activeId: SdkDndIdentifier,
    overId: SdkDndIdentifier | null,
    options?: SdkDndSortableOptions<TItem>
  ): SdkDndReorderResult<TItem>;
  <TItem>(
    items: readonly TItem[],
    activeId: SdkDndIdentifier,
    overId: SdkDndIdentifier | null,
    options: SdkDndSortableOptions<TItem>
  ): SdkDndReorderResult<TItem>;
};

/**
 * Drag-and-drop helpers exposed under `Sdk.Dnd`. Use `SortableList` for one ordered
 * collection and `SortableGroups` for movement within and between ordered groups; the
 * remaining dnd-kit primitives support fully custom interactions.
 */
export type SdkDnd = {
  /**
   * Accessible sortable list with drag handles and keyboard controls. For resource records,
   * pass `resource` and `sortField`; the list owns optimistic ordering, calls `updateMany`,
   * refreshes record queries, and rolls back failures without page-authored state or effects.
   * `onItemsChange` is required only for non-resource lists and optional when a page needs
   * to observe a resource list's new order.
   *
   * @remarks Resource mode keeps `onItemsChange?: (items) => void` optional. Both modes
   * accept `canDrag?: (item, source) => boolean` and
   * `canDrop?: (context) => boolean` constraints.
   *
   * @example
   * ```tsx
   * const students = Sdk.resource('students');
   *
   * return function StudentRanking() {
   *   const { data, isLoading, isError } = Sdk.hooks.useRecords(students, {
   *     sort: 'rank',
   *     perPage: 100
   *   });
   *
   *   if (isLoading) return <Sdk.Components.Skeleton />;
   *   if (isError) return <Sdk.Components.Alert>Could not load students.</Sdk.Components.Alert>;
   *
   *   return (
   *     <Sdk.Dnd.SortableList
   *       items={data}
   *       resource={students}
   *       sortField={students.fields.rank}
   *       renderItem={(student) => <span>{student.name}</span>}
   *       getItemLabel={(student) => student.name ?? 'Unnamed student'}
   *       ariaLabel="Students by rank"
   *     />
   *   );
   * }
   * ```
   */
  SortableList: SdkDndSortableListComponent;

  /**
   * Accessible sorting across multiple ordered groups. Groups are presentation-agnostic:
   * use them for statuses, positions, categories, lanes, or a Kanban layout. Resource mode
   * persists the grouping field and per-group Number order in one `updateMany` request.
   *
   * @remarks Resource mode keeps `onItemsChange?: (items) => void` optional. Both modes
   * accept `canDrag?: (item, source) => boolean` and
   * `canDrop?: (context) => boolean` for movement constraints between any group shapes.
   * When presentation groups differ from stored relation records, use
   * `setItemGroupId?: (item, groupId, group) => item` to shape the optimistic item; the
   * SDK still owns persistence and sends the destination group id.
   *
   * @example
   * ```tsx
   * const tasks = Sdk.resource('tasks');
   * const statuses = [
   *   { id: 'todo', label: 'To do' },
   *   { id: 'in_progress', label: 'In progress' },
   *   { id: 'done', label: 'Done' }
   * ];
   *
   * return function TaskBoard() {
   *   const { data } = Sdk.hooks.useRecords(tasks, { sort: ['status', 'rank'], perPage: 100 });
   *
   *   return (
   *     <Sdk.Dnd.SortableGroups
   *       items={data}
   *       groups={statuses}
   *       getGroupId={(status) => status.id}
   *       getGroupLabel={(status) => status.label}
   *       resource={tasks}
   *       groupField={tasks.fields.status}
   *       sortField={tasks.fields.rank}
   *       renderGroup={(status, { children }) => (
   *         <section><h2>{status.label}</h2>{children}</section>
   *       )}
   *       renderItem={(task) => <span>{task.title}</span>}
   *     />
   *   );
   * }
   * ```
   */
  SortableGroups: SdkDndSortableGroupsComponent;

  /**
   * Pure move calculation for custom drag-and-drop UI. Returns the full reordered array and
   * only items whose zero-based indices changed. `SortableList` already calls this internally.
   *
   * @example
   * ```ts
   * const result = Sdk.Dnd.reorder(todos, active.id, over?.id ?? null);
   * setTodos(result.items);
   * await saveChangedTodos(result.changes);
   * ```
   */
  reorder: SdkDndReorder;
} & Pick<
  typeof import('@dnd-kit/core'),
  | 'applyModifiers'
  | 'AutoScrollActivator'
  | 'closestCenter'
  | 'closestCorners'
  | 'defaultAnnouncements'
  | 'defaultCoordinates'
  | 'defaultDropAnimation'
  | 'defaultDropAnimationSideEffects'
  | 'defaultKeyboardCoordinateGetter'
  | 'defaultScreenReaderInstructions'
  | 'DndContext'
  | 'DragOverlay'
  | 'getClientRect'
  | 'getFirstCollision'
  | 'getScrollableAncestors'
  | 'KeyboardCode'
  | 'KeyboardSensor'
  | 'MeasuringFrequency'
  | 'MeasuringStrategy'
  | 'MouseSensor'
  | 'PointerSensor'
  | 'pointerWithin'
  | 'rectIntersection'
  | 'TouchSensor'
  | 'TraversalOrder'
  | 'useDndContext'
  | 'useDndMonitor'
  | 'useDraggable'
  | 'useDroppable'
  | 'useSensor'
  | 'useSensors'
> &
  Pick<
    typeof import('@dnd-kit/sortable'),
    | 'arrayMove'
    | 'arraySwap'
    | 'defaultAnimateLayoutChanges'
    | 'defaultNewIndexGetter'
    | 'hasSortableData'
    | 'horizontalListSortingStrategy'
    | 'rectSortingStrategy'
    | 'rectSwappingStrategy'
    | 'sortableKeyboardCoordinates'
    | 'SortableContext'
    | 'useSortable'
    | 'verticalListSortingStrategy'
  > &
  Pick<
    typeof import('@dnd-kit/modifiers'),
    | 'createSnapModifier'
    | 'restrictToFirstScrollableAncestor'
    | 'restrictToHorizontalAxis'
    | 'restrictToParentElement'
    | 'restrictToVerticalAxis'
    | 'restrictToWindowEdges'
    | 'snapCenterToCursor'
  > &
  Pick<typeof import('@dnd-kit/utilities'), 'CSS'>;

/**
 * Calendar invite links for a record on an eventable resource. Each value is a
 * relative URL on the workspace subdomain — use as an `<a href>` or link-button
 * target, **not** with `Sdk.api` (the endpoint redirects to the calendar provider
 * or returns an `.ics` download; fetching it via `Sdk.api` would follow the redirect
 * instead of sending the user to their calendar).
 *
 * Only present on resources with the `eventable` capability configured — see
 * {@link SdkResource.calendarLinks}.
 *
 * @example
 * ```tsx
 * const lessons = Sdk.resource('<lessons-resource-id>'); // eventable resource
 * const links = lessons.calendarLinks(record.id);
 * <a href={links.google}>Google Calendar</a>
 * <a href={links.apple}>Download .ics</a>
 * <a href={links.outlook}>Outlook</a>
 * ```
 */
export type CalendarLinks = {
  /** Redirects to Google Calendar's "add event" URL. */
  google: string;
  /** Downloads an `.ics` calendar file. */
  apple: string;
  /** Redirects to Outlook's "add event" URL. */
  outlook: string;
};

/**
 * A single aggregate metric requested from {@link SdkResourceAnalytics.kpi} or
 * {@link SdkResourceAnalytics.timeSeries}. The `name` is the key the computed value
 * appears under in the response, so pick stable, readable names (`total_revenue`,
 * `avg_score`); it must match `alpha_dash` — letters, numbers, dashes, and underscores.
 *
 * `operation` selects the aggregation. `count` tallies matching records and must NOT
 * carry a `field`; every other operation requires a `field` (a field key on the
 * resource). `sum`, `avg`, `min`, and `max` need a numeric or money field; `distinct`
 * counts unique values of any aggregatable field and is accepted by `kpi` only
 * ({@link SdkResourceAnalytics.timeSeries} rejects it).
 */
export type AnalyticsMetric = {
  /** Response key for this metric's value. `alpha_dash` (letters, numbers, `-`, `_`). */
  name: string;
  /** Aggregation to apply. `count` takes no `field`; every other operation requires one. */
  operation: 'count' | 'sum' | 'avg' | 'min' | 'max' | 'distinct';
  /** Field key to aggregate. Omit for `count`; required for every other operation. */
  field?: string;
};

/**
 * A metric for {@link SdkResourceAnalytics.timeSeries}. Same shape as
 * {@link AnalyticsMetric} minus `distinct` — the time-series endpoint restricts
 * `operation` to `count`, `sum`, `avg`, `min`, and `max`.
 */
export type AnalyticsTimeSeriesMetric = {
  /** Response key for this metric's value. `alpha_dash` (letters, numbers, `-`, `_`). */
  name: string;
  /** Aggregation to apply. `count` takes no `field`; every other operation requires one. */
  operation: 'count' | 'sum' | 'avg' | 'min' | 'max';
  /** Field key to aggregate. Omit for `count`; required for every other operation. */
  field?: string;
};

/** Time-bucket size for {@link SdkResourceAnalytics.timeSeries}. */
export type AnalyticsGranularity = 'hour' | 'day' | 'week' | 'month' | 'quarter' | 'year';

/**
 * A single aggregated value in an analytics result: a plain `number`, or a money
 * object `{ amount, currency }` when the aggregated field is money-typed.
 */
export type AnalyticsValue = number | { amount: number; currency: string };

/**
 * The result of {@link SdkResourceAnalytics.kpi}: one object keyed by the metric
 * `name`s you requested, each holding an {@link AnalyticsValue}. Empty (`{}`) when no
 * records match the filter.
 */
export type AnalyticsKpiResult<TMetrics extends readonly AnalyticsMetric[] = AnalyticsMetric[]> = {
  [TMetric in TMetrics[number] as TMetric['name']]?: AnalyticsValue;
};

/**
 * One row of a {@link SdkResourceAnalytics.timeSeries}, {@link SdkResourceAnalytics.leaderboard},
 * or {@link SdkResourceAnalytics.distribution} result. Row keys are the grouping field key plus
 * the metric names produced by the endpoint, so the shape is dynamic — read the keys you asked
 * for (`row[params.entity_field]`, `row.count`, …).
 */
export type AnalyticsRow = Record<string, unknown>;

/**
 * Body for {@link SdkResourceAnalytics.kpi}. Sent to the endpoint unchanged, so it uses the
 * endpoint's own keys: unknown keys are rejected with a 422.
 */
export type AnalyticsKpiParams<TMetrics extends readonly AnalyticsMetric[] = AnalyticsMetric[]> = {
  /** 1–25 metrics to compute across matching records. */
  metrics: TMetrics;
  /** Structured filter tree scoping which records are aggregated — same `$filter` shape as {@link ResourceSearchBody}. */
  $filter?: Record<string, unknown>;
};

/**
 * Body for {@link SdkResourceAnalytics.timeSeries}. Sent to the endpoint unchanged; unknown keys
 * are rejected with a 422.
 */
export type AnalyticsTimeSeriesParams = {
  /** Date/datetime field key (or `created_at` / `updated_at`) to bucket rows by. */
  date_field: string;
  /** Bucket size — see {@link AnalyticsGranularity}. */
  granularity: AnalyticsGranularity;
  /** 1–25 metrics to compute per bucket. */
  metrics: AnalyticsTimeSeriesMetric[];
  /** Max buckets to return (1–1000, default 100), oldest first. */
  limit?: number;
  /** Structured filter tree — same `$filter` shape as {@link ResourceSearchBody}. */
  $filter?: Record<string, unknown>;
};

/**
 * Body for {@link SdkResourceAnalytics.leaderboard}. Sent to the endpoint unchanged; unknown keys
 * are rejected with a 422.
 */
export type AnalyticsLeaderboardParams = {
  /** Field key that identifies the entity each ranked row represents. */
  entity_field: string;
  /** How to score each entity. `count` ranks by record count and takes no `value_field`. */
  operation: 'count' | 'sum' | 'avg';
  /** Numeric/money field to `sum`/`avg`; required for those, omit for `count`. */
  value_field?: string;
  /** Response key for the score (`alpha_dash`, default `value`). */
  metric_name?: string;
  /** Top-N rows to return (1–100, default 10). */
  limit?: number;
  /** Structured filter tree — same `$filter` shape as {@link ResourceSearchBody}. */
  $filter?: Record<string, unknown>;
};

/**
 * Body for {@link SdkResourceAnalytics.distribution}. Sent to the endpoint unchanged; unknown keys
 * are rejected with a 422.
 */
export type AnalyticsDistributionParams = {
  /** Field key to group the distribution by. */
  category_field: string;
  /** Optional numeric/money field summed into a `total` per category. */
  value_field?: string;
  /** Max categories to return (1–50, default 10), largest first. */
  limit?: number;
  /** Structured filter tree — same `$filter` shape as {@link ResourceSearchBody}. */
  $filter?: Record<string, unknown>;
};

export type SdkUseAnalyticsOptions = {
  /** Set `false` to hold the request until the page has what the aggregation needs. */
  enabled?: boolean;
};

export type SdkAnalyticsHandle<TResult> = {
  data: TResult | undefined;
  isLoading: boolean;
  isError: boolean;
  invalidate: () => Promise<void>;
};

/**
 * Aggregate analytics for a resource, reachable via {@link SdkResource.analytics}. Each method
 * POSTs to a per-resource analytics endpoint and returns the computed aggregation — the same
 * KPI / time-series / leaderboard / distribution shapes the workflow chart tasks produce,
 * queryable directly from a page instead of building a workflow per KPI.
 *
 * Every method accepts a structured `$filter` tree (identical to {@link ResourceSearchBody}'s
 * `$filter`) to scope which records are aggregated, and respects the resource's read policies for
 * the acting identity. Field references (`field`, `date_field`, `entity_field`, `category_field`,
 * `value_field`) are field keys on the resource; a key of the wrong type is rejected with a 422.
 *
 * @example
 * ```tsx
 * const invoices = Sdk.resource('invoices');
 * const kpis = await invoices.analytics.kpi({
 *   metrics: [
 *     { name: 'total', operation: 'sum', field: 'amount' },
 *     { name: 'paid_count', operation: 'count' }
 *   ],
 *   $filter: {
 *     operator: 'and',
 *     conditions: [{ field: 'status', operator: '=', value: 'paid' }]
 *   }
 * });
 * // kpis.total -> { amount: 1234500, currency: 'usd' }, kpis.paid_count -> 42
 * ```
 */
export type SdkResourceAnalytics = {
  /**
   * Compute one or more aggregate metrics across matching records, returning a single
   * {@link AnalyticsKpiResult} keyed by your metric `name`s. Use for headline numbers — total
   * revenue, active count, average score. Supports `distinct`, unlike the other analytics methods.
   *
   * @example
   * ```ts
   * const stats = await Sdk.resource('...').analytics.kpi({
   *   metrics: [
   *     { name: 'revenue', operation: 'sum', field: 'amount' },
   *     { name: 'customers', operation: 'distinct', field: 'client_id' }
   *   ]
   * });
   * ```
   */
  kpi<const TMetrics extends readonly AnalyticsMetric[]>(
    params: AnalyticsKpiParams<TMetrics>
  ): Promise<AnalyticsKpiResult<TMetrics>>;

  /**
   * Bucket records into a time series by `date_field` at the chosen `granularity`, computing each
   * metric per bucket. Buckets are returned oldest-first as an array of {@link AnalyticsRow}s, each
   * carrying the `date_field` key (`2026-07` for month, `2026-W28` for week, `2026-07-09` for day,
   * …) plus your metric names. Ideal for line/bar charts over time.
   *
   * @example
   * ```ts
   * const monthly = await Sdk.resource('...').analytics.timeSeries({
   *   date_field: 'created_at',
   *   granularity: 'month',
   *   metrics: [{ name: 'signups', operation: 'count' }]
   * });
   * // [{ created_at: '2026-05', signups: 12 }, { created_at: '2026-06', signups: 31 }, ...]
   * ```
   */
  timeSeries(params: AnalyticsTimeSeriesParams): Promise<AnalyticsRow[]>;

  /**
   * Rank entities by a single aggregation, highest first. Each {@link AnalyticsRow} carries the
   * `entity_field` key, the score under `metric_name` (default `value`), and a `count` companion
   * when `operation` is not `count`. Great for "top N" lists.
   *
   * @example
   * ```ts
   * const topReps = await Sdk.resource('...').analytics.leaderboard({
   *   entity_field: 'sales_rep',
   *   operation: 'sum',
   *   value_field: 'amount',
   *   metric_name: 'revenue',
   *   limit: 5
   * });
   * // [{ sales_rep: 'Ada', revenue: { amount: 90000, currency: 'usd' }, count: 9 }, ...]
   * ```
   */
  leaderboard(params: AnalyticsLeaderboardParams): Promise<AnalyticsRow[]>;

  /**
   * Group records by `category_field` and return a `count` per category (largest first), plus a
   * summed `total` when `value_field` is given. Each row is an {@link AnalyticsRow} with the
   * `category_field` key, `count`, and optional `total`. Use for pie/donut breakdowns and category
   * tables.
   *
   * @example
   * ```ts
   * const byStatus = await Sdk.resource('...').analytics.distribution({
   *   category_field: 'status',
   *   value_field: 'amount'
   * });
   * // [{ status: 'paid', count: 120, total: {...} }, { status: 'open', count: 45, total: {...} }]
   * ```
   */
  distribution(params: AnalyticsDistributionParams): Promise<AnalyticsRow[]>;
};

/** Operations accepted by {@link SdkResourceOperations.can}. */
export type SdkResourceOperation =
  | 'list'
  | 'search'
  | 'get'
  | 'create'
  | 'createMany'
  | 'update'
  | 'delete'
  | 'restore'
  | 'count'
  | 'analytics'
  | 'validate';

export type SdkResourceOperations<TResource extends SdkResourceType = SdkResourceType> = {
  /** Capability field mappings resolved to fields — see {@link SdkResourceCapabilityFieldMappings}. */
  capabilities?: Record<string, SdkResourceCapabilityFieldMappings>;
  /** Record API endpoint URLs for this resource — see {@link ResourceUrls}. */
  urls: ResourceUrls;
  /**
   * Check whether the acting identity can perform an operation on this resource.
   * This reads the resource's server-resolved `policy_scope`; it is intended for
   * conditionally rendering page controls, while the API remains authoritative.
   *
   * @example
   * ```tsx
   * const invoices = Sdk.resource('...');
   * return invoices.can('create') ? <Sdk.Components.Button>New invoice</Sdk.Components.Button> : null;
   * ```
   */
  can(operation: SdkResourceOperation): boolean;
  /**
   * Page through every record the acting identity can read. Pagination, `relations`,
   * and `$scope` only — the list endpoint has no filtering or sorting, so passing
   * `filter`/`$filter`/`sort`/`$sort` throws instead of silently returning an
   * unfiltered page. Use {@link SdkResourceOperations.search} for those.
   */
  list<const TOptions extends ResourceListParams = ResourceListParams<readonly []>>(
    params?: TOptions & ValidateSdkResourceRelationOptions<TResource, NoInfer<TOptions>>
  ): Promise<
    ResourceCollectionResult<
      SdkResourceRecord<TResource, SdkResourceRelationsFromOptions<TResource, TOptions>>
    >
  >;
  search<const TOptions extends ResourceSearchBody>(
    body: TOptions & ValidateSdkResourceRelationOptions<TResource, NoInfer<TOptions>>
  ): Promise<
    ResourceCollectionResult<
      SdkResourceRecord<TResource, SdkResourceRelationsFromOptions<TResource, TOptions>>
    >
  >;
  /**
   * Fetch a bounded filtered slice across all of its pages. `maxRecords` is required
   * and capped at 5,000; the SDK logs a warning when more rows match than it returns.
   * Use this only when a client-side aggregate needs actual row values. Prefer
   * {@link SdkResourceOperations.count} for totals and `analytics` for server-side
   * numeric aggregates. Use `useInfiniteRecords` for scrolling collection UIs.
   */
  searchAll<
    const TRelations extends readonly SdkResourceRelationPath<NoInfer<TResource>>[] = readonly []
  >(
    options: ResourceSearchAllOptions<TRelations>
  ): Promise<SdkResourceRecord<TResource, TRelations>[]>;
  /**
   * Return the exact server-computed number of readable matching records without
   * fetching record rows. Accepts the same structured `filter` tree and `scope` as
   * the record hooks.
   */
  count(options?: ResourceCountOptions): Promise<number>;
  /**
   * Fetch one record. Rejects with {@link SdkApiError}; classify policy-denied and
   * missing reads with `Sdk.utils.error.isForbidden` / `isNotFound`. This matters
   * on public pages, where a guest denied by policy must not be shown a false
   * "record not found" state.
   */
  get(
    recordId: string
  ): Promise<ResourceRecordResult<ResourceOperationRecord<ResourceRecordFrom<TResource>>>>;
  /**
   * Create a record. On success, cached record and analytics queries are invalidated
   * across resources so reactive hooks (`useResourceDataTable`, `useRecord`,
   * `useAnalytics`, …) refresh automatically — no manual remount or reload is needed.
   *
   * On a Booking resource, a booking that does not fit rejects with a 422 whose errors hold
   * the message under `$capacity`, such as "This time is outside open hours." Show it to
   * the visitor. The first date of a repeating booking must fit like any booking. When it fits,
   * the create resolves, and `meta.series.waiting` lists each later date that waits for
   * rescheduling, with its `record_id` and `reason`, so the page can offer an action for each
   * date.
   *
   * @example
   * ```ts
   * try {
   *   const { meta } = await lessons.create(values);
   *   const waiting = meta?.series?.waiting ?? [];
   *   if (waiting.length > 0) {
   *     setNotice(`${waiting.length} dates need rescheduling`);
   *   }
   * } catch (error) {
   *   const capacity = Sdk.utils.error.extractApiErrorBody(error)?.errors?.$capacity?.[0];
   *   setError(capacity ?? Sdk.utils.error.apiErrorMessage(error, 'The booking was not saved.'));
   * }
   * ```
   */
  create(
    payload: ResourceRecordPayload<ResourceRecordFrom<TResource>>
  ): Promise<ResourceRecordResult<ResourceOperationRecord<ResourceRecordFrom<TResource>>>>;
  /**
   * Update a record. On success, cached record and analytics queries are invalidated
   * across resources so reactive hooks (`useResourceDataTable`, `useRecord`,
   * `useAnalytics`, …) refresh automatically — no manual remount or reload is needed.
   * On a Booking resource, read a refusal and `meta.series` as `create` describes.
   */
  update(
    recordId: string,
    payload: ResourceRecordPayload<ResourceRecordFrom<TResource>>
  ): Promise<ResourceRecordResult<ResourceOperationRecord<ResourceRecordFrom<TResource>>>>;
  /**
   * Update multiple records with the existing bulk-update endpoint. Empty arrays are a
   * client-side no-op. On success, cached record and analytics queries are invalidated.
   */
  updateMany(
    records: readonly ResourceBulkUpdateRecord<ResourceRecordFrom<TResource>>[]
  ): Promise<ResourceRecordsResult<ResourceOperationRecord<ResourceRecordFrom<TResource>>>>;
  /**
   * Delete a record. On success, cached record and analytics queries are invalidated
   * across resources so reactive hooks refresh automatically (the deleted record's
   * own per-record queries are left alone — refetching them would 404).
   */
  delete(
    recordId: string,
    payload?: ResourceDeletePayload
  ): Promise<ResourceRecordResult<ResourceOperationRecord<ResourceRecordFrom<TResource>>>>;
  /**
   * Restore a soft-deleted record. On success, cached record and analytics data is
   * refreshed across resources the same way `delete` refreshes it, and the restored
   * record is written straight into the subject's own cache entries.
   *
   * @example
   * ```ts
   * await Sdk.resource('...').restore(recordId);
   * ```
   */
  restore(
    recordId: string
  ): Promise<ResourceRecordResult<ResourceOperationRecord<ResourceRecordFrom<TResource>>>>;
  /**
   * Update a field's schema on this resource — its label, guard, or `settings`
   * (for a select field, `settings.options` is the editable option list). Accepts
   * the field's key or its handle from `resource.fields`. The acting identity must
   * hold the `manage_fields` permission; without it the endpoint responds 403 —
   * this is a schema operation, so `can(...)` (which reads record policy scope)
   * says nothing about it. `settings` replaces the stored settings — send the
   * complete object, not a diff. On success the handle's own `fields` metadata is
   * refreshed in place, so UI built from it reflects the change without a reload.
   *
   * @example
   * ```ts
   * const businesses = Sdk.resource('<businesses-resource-id>');
   * const stage = businesses.fields.pipeline_stage;
   * await businesses.updateField(stage, {
   *   settings: {
   *     ...stage.settings,
   *     options: [
   *       ...(stage.settings.options ?? []),
   *       { value: 'proposal', label: 'Proposal', color: 'blue' }
   *     ]
   *   }
   * });
   * ```
   */
  updateField(
    field: string | SdkResourceField,
    payload: ResourceFieldUpdatePayload
  ): Promise<SdkResourceField>;
  /**
   * Refresh every cached record and analytics query on the page across resources —
   * lists, single records, relation pickers, calendars, and dashboard aggregations.
   * Cross-resource invalidation is intentional because relation-backed displays,
   * filters, and aggregates can depend on records from another resource. Reach for it
   * after a write the SDK didn't make itself: a workflow run, a websocket event, or a
   * raw `Sdk.api` call against a custom endpoint. Records mutated through
   * `create`/`update`/`delete`/`restore` already refresh on their own.
   *
   * @example
   * ```ts
   * const invoices = Sdk.resource('...');
   * await Sdk.api.post('/api/custom/invoices/reconcile');
   * await invoices.invalidate();
   * ```
   */
  invalidate(): Promise<void>;

  /**
   * Aggregate analytics for this resource — `kpi`, `timeSeries`, `leaderboard`, and
   * `distribution`; see {@link SdkResourceAnalytics}. Always present, and scoped by the resource's
   * read policies for the acting identity.
   *
   * @example
   * ```ts
   * const revenue = await Sdk.resource('...').analytics.kpi({
   *   metrics: [{ name: 'total', operation: 'sum', field: 'amount' }]
   * });
   * ```
   */
  analytics: SdkResourceAnalytics;

  /**
   * Build calendar invite links for a record on this resource. Returns relative
   * URLs for Google Calendar, Apple/ICS download, and Outlook — use as `<a href>`
   * targets, not with `Sdk.api`.
   *
   * Only present at runtime on resources with the `eventable` capability configured.
   * On non-eventable resources this property is `undefined`.
   *
   * @example
   * ```tsx
   * const lessons = Sdk.resource('<lessons-resource-id>');
   * const links = lessons.calendarLinks(record.id);
   * <a href={links.google}>Add to Google Calendar</a>
   * <a href={links.apple}>Download .ics</a>
   * <a href={links.outlook}>Add to Outlook</a>
   * ```
   */
  calendarLinks?(recordId: string): CalendarLinks;
};

export type SdkResource<
  TFields extends Record<string, SdkResourceField> = Record<string, SdkResourceField>,
  TRecord extends ResourceRecord = ResourceRecord,
  TRelations extends ResourceRelationCatalog = ResourceRelationCatalog,
  TWorkflowInputs extends SdkResourceWorkflowInputs = SdkResourceWorkflowInputs
> = Omit<PageResourceData, 'capabilities' | 'fields'> & {
  fields: TFields;
  /**
   * Declared inputs of the active published RecordEvent workflows targeting this
   * resource, grouped by trigger event (`created`, `updated`, `deleted`,
   * `restored`, `saved`) and keyed by input key. Resource-record forms surface
   * the applicable events as a flat `workflowInputs` map in their render state.
   */
  workflow_inputs: TWorkflowInputs;
  readonly [sdkResourceRecordCatalog]?: TRecord;
  readonly [sdkResourceRelationCatalog]?: TRelations;
} & SdkResourceOperations<SdkResourceType<TRecord, TRelations>>;

export type ResourceRelationCatalog = Record<string, Record<string, unknown>>;

export interface ResourceCatalog {}

/**
 * Augmented per workspace with `resource id/slug literal -> record type` entries
 * (buildResourceCatalogTypes.ts), so `Sdk.ResourceRecordCatalog['<resource-id>']`
 * derives a resource's record type from its id. Prefer
 * `Sdk.ResourceRecordFrom<typeof resource>` when a resource handle is in scope,
 * or `Sdk.RecordOf<'<slug>'>` for slug lookups.
 */
export interface ResourceRecordCatalog {}

/**
 * The record type of one resource, by slug or id: `Sdk.RecordOf<'students'>`.
 * The key is constrained to the workspace's catalog, so a typo lists the valid
 * slugs and ids instead of resolving to `any`.
 */
export type SdkRecordOf<TKey extends keyof ResourceRecordCatalog> = ResourceRecordCatalog[TKey];

export interface WorkflowCatalog {}

export type WorkflowReference =
  | Extract<keyof WorkflowCatalog, string>
  | (string & Record<never, never>);

/**
 * Augmented per workspace by the page editor with one property per page name
 * (buildPageAliasCatalogTypes.ts), so `Sdk.page('` completes page names. The
 * editor writes the page id in place of the name before the source is stored.
 */
export interface PageAliasCatalog {}

/** A page id, or in the editor a page name that the editor stores as its id. */
export type PageReference =
  | Extract<keyof PageAliasCatalog, string>
  | (string & Record<never, never>);

/**
 * A page of the workspace, from `Sdk.page()`. The handle always exists; a page
 * that is not published has no route, and its URL helpers return null after a
 * warning, exactly like `Sdk.utils.page.url` and `Sdk.utils.page.navigate`.
 */
export type SdkPageHandle = {
  /** The page's stable id. */
  readonly id: string;
  /** The published route pattern, e.g. `/invoices/{invoice}`, or null when the page is not published. */
  readonly route: string | null;
  /**
   * The page's URL with `params` filled into the route, or null when the page
   * is not published or a route parameter is missing.
   *
   * @example
   * const href = Sdk.page(INVOICE_DETAIL_PAGE_ID).url({ invoice: row.id });
   * return href ? <a href={href}>View</a> : null;
   */
  url(params?: Record<string, unknown>): string | null;
  /**
   * Navigate to the page with `params` filled into the route. Unpublished
   * pages and missing route params are ignored after a warning.
   *
   * @example
   * Sdk.page(STUDENTS_PAGE_ID).navigate();
   * @example
   * Sdk.page(INVOICE_DETAIL_PAGE_ID).navigate({ invoice: row.id }, { openInNewTab: true });
   */
  navigate(params?: Record<string, unknown>, options?: TaskNavigateOptions): void;
};

export type SdkResourceType<
  TRecord extends ResourceRecord = ResourceRecord,
  TRelations extends ResourceRelationCatalog = ResourceRelationCatalog
> = {
  readonly [sdkResourceRecordCatalog]?: TRecord;
  readonly [sdkResourceRelationCatalog]?: TRelations;
};

export type ResourceRecordFrom<TResource extends SdkResourceType> = TResource extends {
  readonly [sdkResourceRecordCatalog]?: infer TRecord extends ResourceRecord;
}
  ? TRecord
  : ResourceRecord;

export type ResourceRelationsFrom<TResource extends SdkResourceType> = TResource extends {
  readonly [sdkResourceRelationCatalog]?: infer TRelations extends ResourceRelationCatalog;
}
  ? TRelations
  : ResourceRelationCatalog;

type UnionToIntersection<TValue> = (
  TValue extends unknown ? (value: TValue) => void : never
) extends (value: infer TIntersection) => void
  ? TIntersection
  : never;

export type SdkResourceRelationPath<TResource extends SdkResourceType> = Extract<
  keyof ResourceRelationsFrom<TResource>,
  string
>;

type SdkResourceRelationsFromOptions<
  TResource extends SdkResourceType,
  TOptions extends { relations?: readonly string[] }
> = TOptions extends { relations: infer TRelations extends readonly string[] }
  ? TRelations extends readonly SdkResourceRelationPath<TResource>[]
    ? TRelations
    : readonly []
  : readonly [];

type ValidateSdkResourceRelationOptions<
  TResource extends SdkResourceType,
  TOptions extends { relations?: readonly string[] }
> = TOptions extends { relations: infer TRelations extends readonly string[] }
  ? string extends TRelations[number]
    ? unknown
    : TRelations extends readonly SdkResourceRelationPath<TResource>[]
      ? unknown
      : never
  : unknown;

type KeysOfUnion<TValue> = TValue extends unknown ? keyof TValue : never;

type ValueOfUnionKey<TUnion, TKey extends PropertyKey> = TUnion extends unknown
  ? TKey extends keyof TUnion
    ? TUnion[TKey]
    : never
  : never;

type ArrayElementOfUnion<TValue> = TValue extends readonly (infer TElement)[] ? TElement : never;

type IsAny<TValue> = 0 extends 1 & TValue ? true : false;

type RelationKeysRequiringMerge<TUnion> = {
  [TKey in KeysOfUnion<TUnion>]: IsAny<ValueOfUnionKey<TUnion, TKey>> extends true
    ? never
    : [ValueOfUnionKey<TUnion, TKey>] extends [readonly unknown[]]
      ? TKey
      : null extends ValueOfUnionKey<TUnion, TKey>
        ? [Exclude<ValueOfUnionKey<TUnion, TKey>, null | undefined>] extends [object]
          ? [
              RelationKeysRequiringMerge<Exclude<ValueOfUnionKey<TUnion, TKey>, null | undefined>>
            ] extends [never]
            ? never
            : TKey
          : never
        : never;
}[KeysOfUnion<TUnion>];

type MergeResourceRelationValue<TValue> =
  IsAny<TValue> extends true
    ? TValue
    : [TValue] extends [readonly unknown[]]
      ? MergeResourceRelationFragments<ArrayElementOfUnion<TValue>>[]
      :
          | MergeResourceRelationFragments<Exclude<TValue, null | undefined>>
          | (null extends TValue ? null : never)
          | (undefined extends TValue ? undefined : never);

type MergeResourceRelationFragments<TUnion> = [RelationKeysRequiringMerge<TUnion>] extends [never]
  ? UnionToIntersection<TUnion>
  : // Generated relation keys are always required and mutable, so rebuilding them does not drop modifiers.
    Omit<UnionToIntersection<TUnion>, RelationKeysRequiringMerge<TUnion>> & {
      [TKey in RelationKeysRequiringMerge<TUnion>]: MergeResourceRelationValue<
        ValueOfUnionKey<TUnion, TKey>
      >;
    };

/**
 * When an inferred `relations` tuple holds an invalid path, TypeScript reports the path
 * and then substitutes the parameter constraint for the tuple: an unsized array of every
 * relation path. Merging every relation type takes tens of seconds on a dense catalog.
 * An authored selection is always a sized tuple, so an unsized array that names every
 * path is that substitution and selects nothing.
 */
type IsRelationConstraintFallback<
  TRelationCatalog extends ResourceRelationCatalog,
  TRelations extends readonly string[]
> = number extends TRelations['length']
  ? [Exclude<keyof TRelationCatalog, TRelations[number]>] extends [never]
    ? true
    : false
  : false;

type SelectedResourceRelations<
  TRelationCatalog extends ResourceRelationCatalog,
  TRelations extends readonly Extract<keyof TRelationCatalog, string>[]
> = [TRelations[number]] extends [never]
  ? object
  : string extends keyof TRelationCatalog
    ? UnionToIntersection<TRelationCatalog[TRelations[number]]>
    : IsRelationConstraintFallback<TRelationCatalog, TRelations> extends true
      ? object
      : MergeResourceRelationFragments<TRelationCatalog[TRelations[number]]>;

export type SdkSelectedResourceRecord<
  TRecord extends ResourceRecord,
  TRelationCatalog extends ResourceRelationCatalog,
  TRelations extends readonly Extract<keyof TRelationCatalog, string>[] = readonly []
> = TRecord & SelectedResourceRelations<TRelationCatalog, TRelations>;

/**
 * The record shape of a resource handle with the selected relations merged in. Accepts a
 * nullable handle so `Sdk.ResourceRecordFrom<typeof Sdk.resourceOrNull('slug')>` works.
 */
export type SdkResourceRecord<
  TResource extends SdkResourceType | null | undefined,
  TRelations extends readonly SdkResourceRelationPath<NonNullable<TResource>>[] = readonly []
> = SdkSelectedResourceRecord<
  ResourceRecordFrom<NonNullable<TResource>>,
  ResourceRelationsFrom<NonNullable<TResource>>,
  TRelations
>;

/**
 * A resource reference a data hook accepts: a handle from `Sdk.resource()`, a resource id
 * or slug, or nothing. A string reference resolves against the viewer's accessible
 * resources at render time; when the viewer cannot access it the hook stays idle instead
 * of throwing, so the same page code serves guests and members.
 */
export type SdkResourceReference = SdkResourceHandle | string | null | undefined;

/**
 * A resource handle as a data hook reads it. The workspace catalog omits the methods of
 * the data API endpoints that the workspace turned off, so a hook accepts a handle
 * without its operations.
 */
export type SdkResourceHandle = Omit<SdkResource, keyof SdkResourceOperations>;

/** Query inputs include metadata handles and handles with only the required URLs. */
export type SdkResourceQueryReference<TUrl extends keyof ResourceUrls = 'show' | 'search'> =
  | SdkResourceQueryHandle<TUrl>
  | SdkResourceReference;

/** Resource metadata and endpoint URLs required by a record query. */
export type SdkResourceQueryHandle<TUrl extends keyof ResourceUrls = 'show' | 'search'> =
  SdkResourceType &
    Pick<SdkResource, 'id' | 'name' | 'disabled_data_api_endpoints'> & {
      urls: Pick<ResourceUrls, TUrl>;
    };

type ResourceTypeReference =
  | (SdkResourceType & Pick<SdkResource, 'id'>)
  | string
  | null
  | undefined;

/** A resource with the aggregate required by an analytics query. */
export type SdkAnalyticsResource<TKind extends keyof SdkResourceAnalytics> = Pick<
  SdkResource,
  'id'
> & {
  analytics: Pick<SdkResourceAnalytics, TKind>;
};

/**
 * Relation paths for a resource named by id or slug. Known catalog entries resolve to
 * their real relation paths; runtime-dynamic strings keep the permissive fallback.
 */
export type SdkCatalogRelationPath<TResourceId extends string> =
  TResourceId extends keyof ResourceCatalog
    ? SdkResourceRelationPath<ResourceCatalog[TResourceId]>
    : string;

/**
 * The record shape for a resource named by id or slug, with the selected relations
 * merged in. Known catalog entries are typed from the workspace catalog, so the type is
 * available at authoring time regardless of the viewer's runtime access.
 */
export type SdkCatalogRecord<
  TResourceId extends string,
  TRelations extends readonly SdkCatalogRelationPath<TResourceId>[] = readonly []
> = TResourceId extends keyof ResourceCatalog
  ? SdkResourceRecord<
      ResourceCatalog[TResourceId],
      Extract<TRelations, readonly SdkResourceRelationPath<ResourceCatalog[TResourceId]>[]>
    >
  : ResourceRecord;

/**
 * Relation paths a data hook accepts for a resource reference. A handle keeps its own
 * relation paths; a catalog id or slug resolves to the catalog's paths; a nullish or
 * runtime-dynamic reference keeps the permissive fallback.
 */
export type SdkReferenceRelationPath<TReference extends ResourceTypeReference> = [
  NonNullable<TReference>
] extends [never]
  ? string
  : NonNullable<TReference> extends SdkResourceType
    ? SdkResourceRelationPath<NonNullable<TReference>>
    : TReference extends string
      ? SdkCatalogRelationPath<TReference>
      : string;

/**
 * The record shape a data hook returns for a resource reference, with the selected
 * relations merged in. One signature covers handles and catalog ids or slugs, so an
 * invalid relation reports the expected paths instead of "no overload matches".
 */
export type SdkReferenceRecord<
  TReference extends ResourceTypeReference,
  TRelations extends readonly SdkReferenceRelationPath<TReference>[] = readonly []
> = [NonNullable<TReference>] extends [never]
  ? ResourceRecord
  : NonNullable<TReference> extends SdkResourceType
    ? TRelations extends readonly SdkResourceRelationPath<NonNullable<TReference>>[]
      ? SdkResourceRecord<NonNullable<TReference>, TRelations>
      : never
    : TReference extends string
      ? TRelations extends readonly SdkCatalogRelationPath<TReference>[]
        ? SdkCatalogRecord<TReference, TRelations>
        : never
      : ResourceRecord;

/**
 * Poll schedule for a data hook. A number is a fixed interval in milliseconds, clamped to
 * a 1000 ms minimum. `false` disables polling. A function is re-evaluated after every
 * fetch and receives the hook's current `data`, so polling can stop itself as soon as the
 * awaited change lands.
 */
export type SdkPollInterval<TData> = number | false | ((data: TData) => number | false);

export type SdkUsePollOptions = {
  /**
   * Start polling on mount. Defaults to `true`. Pass `false` to wait for a manual
   * `start()`, for example until the visitor submits something the page then waits on.
   */
  autoStart?: boolean;
  /**
   * Keep polling at full rate while the browser tab is hidden. Defaults to `false`, which
   * slows a hidden tab to one tick in ten rather than stopping it.
   */
  keepAlive?: boolean;
};

/** Manual control over a {@link SdkHooks.usePoll} schedule. */
export type SdkPollControls = {
  start: () => void;
  stop: () => void;
  /**
   * When the latest tick finished loading the page data, or `null` before the first
   * one. It changes on every successful tick, even when the data itself did not.
   */
  lastPolledAt: Date | null;
};

export type SdkUseRecordOptions<
  TRelations extends readonly string[] = readonly string[],
  TRecord extends ResourceRecord = ResourceRecord
> = {
  relations?: TRelations;
  enabled?: boolean;
  /** Soft-delete subset to fetch from. Defaults to active records only; pass
   * `RecordScope.ActiveOrArchived`/`Archived` to fetch (or restore) archived records. */
  scope?: RecordScope;
  /**
   * Refetch this record on an interval. The function form receives the current record
   * (`undefined` before the first fetch) and returns `false` to stop.
   *
   * @example
   * ```tsx
   * Sdk.hooks.useRecord(orders, orderId, {
   *   relations: ['tickets'],
   *   pollInterval: (order) => (order?.tickets?.length ? false : 3000)
   * });
   * ```
   */
  pollInterval?: SdkPollInterval<TRecord | undefined>;
};

export type SdkRecordHandle<TRecord extends ResourceRecord = ResourceRecord> = {
  data: TRecord | undefined;
  isLoading: boolean;
  isError: boolean;
  /**
   * The failed request, or `null`. On public pages, a guest denied by resource
   * policy receives a forbidden error, while an absent record receives not-found;
   * branch with `Sdk.utils.error` instead of treating missing `data` as not-found.
   */
  error: SdkApiError | null;
  /**
   * Whole seconds until the next automatic `pollInterval` request, rounded up, for
   * rendering a refresh countdown. `0` means a refresh is in flight right now. `null`
   * means no refresh is scheduled: before the first request settles, without a
   * `pollInterval`, once a function-form `pollInterval` returns `false`, or while the
   * browser tab is hidden and the poll is paused.
   */
  refreshIn: number | null;
  invalidate: () => Promise<void>;
};

export type SdkUseRecordsOptions<
  TRelations extends readonly string[] = readonly string[],
  TRecord extends ResourceRecord = ResourceRecord
> = {
  /**
   * Relation paths to load alongside each record, e.g. `['family']`. Filtering on a
   * nested path (`family.id`) does not load the relation: it still comes back as its
   * `{ id }` reference, so list it here to read any other related field.
   */
  relations?: TRelations;
  /**
   * Structured `$filter` tree — the same contract as {@link ResourceSearchBody}'s
   * `filter`: `{ operator: 'and' | 'or' | 'nor', conditions: [...] }`, never a flat
   * map like `{ status: 'paid' }`. `null` means unfiltered, same as omitting it, so
   * saved view state (e.g. `useSharedFilters().filter`) can be passed straight through.
   */
  filter?: Record<string, unknown> | null;
  /** Sort input in any {@link ResourceSortInput} form; normalized to `$sort`. `null` = default order. */
  sort?: ResourceSortInput | null;
  /** Free-text search across the resource's searchable fields, sent as `$search`. `null` or `''` = no search. */
  search?: string | null;
  /** Page number to fetch. Defaults to 1. */
  page?: number;
  /** Records per page (maximum 500, defaults to 15). `null` = the default page size. */
  perPage?: number | null;
  /** Soft-delete subset to read from. Defaults to active records only. */
  scope?: RecordScope;
  /**
   * `'series'` returns one record for each repeating event of an Eventable resource, sent as
   * `$collapse`: its first date under `sort` (default: the Event Schedule start) among the
   * records that pass every filter. A one-time record is its own entry, and
   * `pagination.total` counts events. `null` = every record. List the dates of one series
   * with a filter on `__meta.capabilities.eventable.series.series_id`.
   *
   * @example
   * ```tsx
   * const upcoming = Sdk.hooks.useInfiniteRecords(events, {
   *   collapse: 'series',
   *   sort: 'schedule.start',
   *   filter: { operator: 'and', conditions: [{ field: 'schedule.start', operator: '>=', value: now }] }
   * });
   *
   * const seriesId = event.__meta?.capabilities?.eventable?.series?.series_id;
   * const dates = Sdk.hooks.useRecords(events, {
   *   enabled: seriesId !== undefined,
   *   sort: 'schedule.start',
   *   filter: {
   *     operator: 'and',
   *     conditions: [
   *       { field: '__meta.capabilities.eventable.series.series_id', operator: '=', value: seriesId },
   *       { field: 'schedule.start', operator: '>=', value: now }
   *     ]
   *   }
   * });
   * ```
   */
  collapse?: RecordCollapse | null;
  /** Set `false` to hold the request until the page has what the query needs. */
  enabled?: boolean;
  /**
   * Refetch this page of records on an interval. The function form receives the current
   * records array (empty before the first fetch) and returns `false` to stop.
   *
   * @example
   * ```tsx
   * Sdk.hooks.useRecords(tickets, {
   *   filter: { operator: 'and', conditions: [{ field: 'order_id', operator: '=', value: orderId }] },
   *   pollInterval: (records) => (records.length > 0 ? false : 3000)
   * });
   * ```
   */
  pollInterval?: SdkPollInterval<TRecord[]>;
};

export type SdkRecordsHandle<TRecord extends ResourceRecord = ResourceRecord> = {
  /** Authorized file metadata for the records currently retained by this hook. */
  files: FileMetadataMap;
  /** The records on the current page — an empty array until the first fetch resolves. */
  data: TRecord[];
  /** Pagination metadata for the current page, `undefined` until the first fetch resolves. */
  pagination: ResourcePagination | undefined;
  isLoading: boolean;
  isError: boolean;
  /** The failed request, or `null`; classify it with `Sdk.utils.error`. */
  error: SdkApiError | null;
  /**
   * Whole seconds until the next automatic `pollInterval` request, rounded up. `0`
   * means a refresh is in flight. `null` means no refresh is scheduled.
   */
  refreshIn: number | null;
  invalidate: () => Promise<void>;
};

export type SdkUseInfiniteRecordsOptions<TRelations extends readonly string[] = readonly string[]> =
  Omit<SdkUseRecordsOptions<TRelations>, 'page' | 'pollInterval'>;

export type SdkInfiniteRecordsHandle<TRecord extends ResourceRecord = ResourceRecord> = {
  /** Authorized file metadata for the records currently retained by this hook. */
  files: FileMetadataMap;
  /** Up to the ten most recently loaded pages flattened into one record array. */
  data: TRecord[];
  /** Pagination metadata from the most recently loaded page. */
  pagination: ResourcePagination | undefined;
  isLoading: boolean;
  isError: boolean;
  isFetchingNextPage: boolean;
  hasNextPage: boolean;
  fetchNextPage: () => Promise<void>;
  invalidate: () => Promise<void>;
};

/** Shared mutation state returned by the reactive create/update record hooks. */
export type SdkRecordMutationState<TRecord extends ResourceRecord = ResourceRecord> = {
  /** The record returned by the most recent successful call. */
  data: TRecord | undefined;
  /** `true` while a request is in flight. */
  isPending: boolean;
  isError: boolean;
  /** The failed request's {@link SdkApiError} (with `status` and `body`), or `null`. */
  error: SdkApiError | null;
  /** Clears `data`, `error`, and `isError` back to their initial state. */
  reset: () => void;
};

export type SdkUseCreateRecordOptions<TRecord extends ResourceRecord = ResourceRecord> = {
  onSuccess?: (record: TRecord) => void;
  onError?: (error: SdkApiError) => void;
  /**
   * Make a retried booking create return the booking that the first attempt made, instead of a
   * second booking. Each create writes a key into the field that the resource's booking
   * capability names as its idempotency field. A retry with the same payload sends the same key;
   * a changed payload gets a new key. `create` throws when the resource has no idempotency field.
   * Defaults to `false`.
   */
  idempotent?: boolean;
};

export type SdkCreateRecordHandle<TRecord extends ResourceRecord = ResourceRecord> =
  SdkRecordMutationState<TRecord> & {
    /**
     * Create a record. Resolves with the created record, or `null` when the request
     * failed — it never rejects, so an un-awaited call from an event handler cannot
     * produce an unhandled rejection. Read `error` for the failure.
     */
    create: (payload: ResourceRecordPayload<TRecord>) => Promise<TRecord | null>;
  };

export type SdkUseUpdateRecordOptions<TRecord extends ResourceRecord = ResourceRecord> = {
  onSuccess?: (record: TRecord) => void;
  onError?: (error: SdkApiError) => void;
};

export type SdkUpdateRecordHandle<TRecord extends ResourceRecord = ResourceRecord> =
  SdkRecordMutationState<TRecord> & {
    /**
     * Update a record. Resolves with the updated record, or `null` when the request
     * failed — it never rejects, so an un-awaited call from an event handler cannot
     * produce an unhandled rejection. Read `error` for the failure.
     */
    update: (recordId: string, payload: ResourceRecordPayload<TRecord>) => Promise<TRecord | null>;
  };

/**
 * One recipient of an ad hoc email. In bulk mode `data` overrides `layout_data`
 * keys for that recipient only, and every bulk email can read `{{ recipient.email }}`.
 */
export type SdkEmailRecipient = {
  email: string;
  data?: Record<string, unknown>;
} & (
  | { resource_id?: never; record_id?: never }
  /** Supply both IDs to link this recipient's record to their email. */
  | { resource_id: string; record_id: string }
);

/** Request body of `POST /api/email-messages`, posted unchanged by `Sdk.hooks.useSendEmail`. */
export type SdkSendEmailInput = {
  /** Recipients. With `bulk`, each entry receives its own email. */
  to: SdkEmailRecipient[];
  /** Liquid subject template. */
  subject: string;
  /**
   * Layout variables keyed by name. Without `email_layout_id`, `content` is the whole
   * email body (HTML string or TipTap JSON document).
   */
  layout_data: Record<string, unknown>;
  /** Published email layout that wraps the body. */
  email_layout_id?: string | null;
  /** Not allowed with `bulk`. */
  cc?: string[];
  /** Not allowed with `bulk`. */
  bcc?: string[];
  reply_to?: string[];
  /** Records the email is about; they are linked to the stored message. */
  records?: Array<{ id: string; resource_id: string }>;
  /** File ids the caller can read. */
  attachments?: string[];
  /** Eventable records to attach as calendar invites; at most 5. An archived or cancelled record sends a cancellation. */
  calendar_events?: Array<{ resource_id: string; record_id: string }>;
  /** Send every recipient its own email instead of one email addressed to all. */
  bulk?: boolean;
};

/**
 * The `202` body of `POST /api/email-messages`. Mirrors the serialized
 * `Diagonal.Notifications.Data.SendEmailResponseData`; declared locally because the
 * SDK `.dts` flattener degrades `Diagonal.*` references to `any`. `subject` and
 * `content` are `null` for a bulk send, which renders per recipient in a queued job.
 */
export type SdkSendEmailResponse = {
  success: boolean;
  status_message: string;
  recipients: {
    to: string[];
    cc: string[];
    bcc: string[];
    reply_to: string[];
  };
  subject: string | null;
  content: string | null;
  attachments: Array<{ id: string; name: string; mime: string }>;
  bulk: boolean;
};

export type SdkUseSendEmailOptions = {
  onSuccess?: (response: SdkSendEmailResponse) => void;
  onError?: (error: SdkApiError) => void;
};

export type SdkSendEmailHandle = {
  /**
   * Queue the email. Resolves with the response, or `null` when the request failed —
   * it never rejects, so an un-awaited call from an event handler cannot produce an
   * unhandled rejection. Read `error` for the failure.
   */
  send: (input: SdkSendEmailInput) => Promise<SdkSendEmailResponse | null>;
  /** The response of the most recent successful call. */
  data: SdkSendEmailResponse | undefined;
  /** `true` while a request is in flight. */
  isPending: boolean;
  isError: boolean;
  /** The failed request's {@link SdkApiError} (with `status` and `body`), or `null`. */
  error: SdkApiError | null;
  /** Clears `data`, `error`, and `isError` back to their initial state. */
  reset: () => void;
};

/** A saved background send. Queued means accepted by the mail queue, not delivered. */
export type SdkBulkNotificationRun = {
  id: string;
  status: 'queued' | 'processing' | 'completed' | 'failed' | 'interrupted';
  total_count: number;
  processed_count: number;
  queued_count: number;
  failed_count: number;
  uncertain_count: number;
  /** Source records skipped because an earlier notification already used their idempotency key. */
  duplicate_count: number;
  failures: Array<{ record_id: string; errors: Record<string, string[]> }>;
  failures_truncated: boolean;
  error: string | null;
};

/** Each variable uses one literal value or one authorized record field path. */
export type SdkBulkNotificationVariable =
  | { literal: unknown; source_path?: never; recipient_path?: never }
  | { source_path: string; literal?: never; recipient_path?: never }
  | { recipient_path: string; literal?: never; source_path?: never };

export type SdkSendBulkNotificationsInput = {
  resource_id: string;
  notification_type_id: string;
  notification_template_id?: string | null;
  /** Empty string selects the source record; otherwise use a singular relation path. */
  recipient_path: string;
  variables: Record<string, SdkBulkNotificationVariable>;
  attachments?: string[];
  /**
   * Must contain `{source_record_id}`, for example `subscription-period:{source_record_id}:initial-notice`.
   * Each source record gets its own key, so it matches a single send that uses the same key.
   */
  idempotency_key?: string;
  /** Maximum 50,000 source records. Explicit empty filters select all accessible active records. */
  audience:
    | { mode: 'records'; record_ids: string[]; filters?: never }
    | {
        mode: 'filters';
        filters: Record<string, unknown> | unknown[];
        record_ids?: never;
      };
};

export type SdkSendBulkNotificationsHandle = {
  /** Save the audience and start sending. Returns null on request failure. Never automatically retries. */
  send: (input: SdkSendBulkNotificationsInput) => Promise<SdkBulkNotificationRun | null>;
  data: SdkBulkNotificationRun | undefined;
  isPending: boolean;
  isError: boolean;
  error: SdkApiError | null;
  reset: () => void;
};

/** A full recipient record returned by the data API. */
export type SdkNotificationRecipient = ResourceRecord & {
  id: string;
  resource_id: string;
};

export type SdkNotificationDeliveryChannel = 'email' | 'sms' | 'email_and_sms';

/**
 * A compatible template delivers email to explicit addresses or to a saved record. SMS
 * always goes to a saved record's mapped phone number, and only when that record has
 * opted in through `useNotificationConsent`.
 */
export type SdkSendNotificationInput = {
  notification_type_id: string;
  /** Omit to use the type's compatible current default template. */
  notification_template_id?: string | null;
  variables: Record<string, unknown>;
  /**
   * At most 200 characters, not blank. A send whose key an earlier notification in the workspace
   * used sends nothing and resolves with `replayed: true`. The response carries that notification's
   * IDs only when the viewer can read it; otherwise they are null and empty. Not allowed with `bulk`.
   */
  idempotency_key?: string;
} & (
  | (Omit<SdkSendEmailInput, 'subject' | 'layout_data' | 'email_layout_id'> & {
      delivery_channel?: 'email';
      recipient?: never;
    })
  | (Pick<SdkSendEmailInput, 'records' | 'attachments' | 'reply_to' | 'calendar_events'> & {
      delivery_channel?: 'email' | 'email_and_sms';
      recipient: SdkNotificationRecipient;
      to?: never;
      cc?: never;
      bcc?: never;
      bulk?: never;
    })
  | (Pick<SdkSendEmailInput, 'records'> & {
      delivery_channel: 'sms';
      recipient: SdkNotificationRecipient;
      to?: never;
      cc?: never;
      bcc?: never;
      bulk?: never;
      attachments?: never;
      reply_to?: never;
      calendar_events?: never;
    })
);

export type SdkSendNotificationResponse = SdkSendEmailResponse & {
  notification_history_id: string | null;
  /** Email attempt IDs available at response time. Queued workers can add attempts later. */
  email_message_ids: string[];
  sms_messages: { sms_message_id: string; status: string }[];
  /**
   * True when an earlier notification already used the idempotency key, so nothing was sent.
   * A replay names the earlier notification only when the viewer can read it.
   */
  replayed: boolean;
};

/** A notifiable record's consent for each notification channel. */
export type SdkNotificationConsentChannels = {
  email?: boolean;
  sms?: boolean;
};

export type SdkNotificationConsent = {
  resource_id: string;
  record_id: string;
  channels: { email: boolean; sms: boolean };
  /** When consent last changed, or `null` when the person has never been asked. */
  changed_at: string | null;
};

export type SdkUseNotificationConsentOptions = {
  /** The notifiable resource. Omit both IDs to manage the signed-in identity's own consent. */
  resourceId?: string | null;
  /** The record in that resource. */
  recordId?: string | null;
  /** Set `false` to skip the read, for example until a record is selected. */
  enabled?: boolean;
  onSuccess?: (consent: SdkNotificationConsent) => void;
  onError?: (error: SdkApiError) => void;
};

export type SdkNotificationConsentHandle = {
  /** The stored consent. A record that was never asked has email on and SMS off. */
  data: SdkNotificationConsent | undefined;
  isLoading: boolean;
  isError: boolean;
  /** The read failure, or `null`. */
  error: ApiError | null;
  /**
   * Save the given channels. An omitted channel keeps its stored value. Resolves with the
   * stored consent, or `null` when the request failed — it never rejects, so calling it
   * from an `onChange` is safe; read `updateError` for the failure.
   */
  update: (channels: SdkNotificationConsentChannels) => Promise<SdkNotificationConsent | null>;
  /** `true` while a save is in flight. */
  isPending: boolean;
  /** The save failure, or `null`. */
  updateError: ApiError | null;
  reset: () => void;
};

/**
 * Why `useCalendarConnections` has nothing to show: nobody is signed in, the workspace's
 * `calendar_sync` flag is off or the workspace is a branch, or the signed-in identity is
 * not a record of a Schedulable resource.
 */
export type SdkCalendarUnavailableReason = 'signed_out' | 'flag_off' | 'not_schedulable';

/**
 * `waiting_for_write_access`: the calendar blocks availability, but its share does not let
 * Diagonal add events. `broken`: Diagonal can no longer use the calendar.
 */
export type SdkCalendarLinkState = 'active' | 'waiting_for_write_access' | 'broken';

export type SdkCalendarAccessRole = 'freeBusyReader' | 'reader' | 'writer' | 'owner';

/** `disconnecting`: Diagonal is removing the account's events from Google before it disconnects. */
export type SdkGoogleCalendarLoginStatus =
  | 'connected'
  | 'reconnect_required'
  | 'disconnecting'
  | 'disconnected';

/**
 * What happens to the upcoming events Diagonal already wrote when it stops writing to a
 * calendar. `keep` leaves them in Google. `remove` deletes them.
 */
export type SdkCalendarEventsChoice = 'keep' | 'remove';

/** The staff record whose calendars the hook manages. */
export type SdkCalendarStaff = {
  resource_id: string;
  record_id: string;
  name: string;
  /** The staff record is the signed-in identity. */
  is_viewer: boolean;
};

/** One Google calendar linked to the staff record, as the signed-in viewer sees it. */
export type SdkCalendarLink = {
  id: string;
  calendar_id: string;
  calendar_name: string;
  /** The Google account that the link uses. Only its owner can reconnect it. */
  login_id: string;
  account: string | null;
  login_status: SdkGoogleCalendarLoginStatus | null;
  owner_name: string;
  viewer_owns_login: boolean;
  connected_by_name: string;
  receives_events: boolean;
  blocks_availability: boolean;
  state: SdkCalendarLinkState;
  access_role: SdkCalendarAccessRole | null;
  /** The explanation of the state for this viewer, the same text as the admin page. Null while active. */
  message: string | null;
  /** A reconnect queued an access check that has not run yet. */
  checking: boolean;
  /** When Diagonal last read the calendar's busy time. `null` when it blocks no availability or was not read yet. */
  busy_refreshed_at: string | null;
  /** The calendar blocks availability, but its busy time was not read for 6 hours. Bookings can overlap busy time. */
  busy_stale: boolean;
};

/** One of the signed-in viewer's own Google accounts. It never carries a credential. */
export type SdkCalendarLogin = {
  id: string;
  account: string;
  status: SdkGoogleCalendarLoginStatus;
  connected_at: string;
};

/** A calendar that one of the viewer's Google accounts can link. */
export type SdkCalendarChoice = {
  login_id: string;
  account: string;
  calendar_id: string;
  name: string;
  access_role: SdkCalendarAccessRole;
  /** The account's own calendar, which also holds the person's personal events. */
  primary: boolean;
  /** Its share lets Diagonal add events. */
  can_receive_events: boolean;
  /** It is already linked to the staff record. */
  linked: boolean;
  /** Diagonal created it for this workspace with `addLink({ newCalendar: true })`. Suggest it first. */
  created_by_diagonal: boolean;
};

export type SdkCalendarChoices = {
  calendars: SdkCalendarChoice[];
  /** One line for each Google account that could not list its calendars. */
  errors: string[];
  /**
   * The name of a new calendar, on the signed-in identity's own record. `null` on another
   * person's record, where `newCalendar` is refused.
   */
  new_calendar_name: string | null;
};

/** The outcome of a calendar change or of a Google sign-in. */
export type SdkCalendarResult = {
  status: 'success' | 'warning' | 'error';
  message: string;
};

/** The outcome of a Google sign-in that returned to the page. */
export type SdkCalendarSignInResult = SdkCalendarResult & {
  /** The sign-in added a Google account. A reconnect renews one, so offer its calendars only after this. */
  new_login: boolean;
};

export type SdkCalendarLinkFlags = {
  /** Diagonal adds the person's events to this calendar. Needs a "Make changes" share. */
  receivesEvents: boolean;
  /** Busy time in this calendar blocks the person's availability. */
  blocksAvailability: boolean;
};

/**
 * Link a calendar from `listChoices` by `calendarId`, a calendar shared with the account by
 * `email`, or, with `newCalendar: true`, a calendar named for the workspace that Diagonal
 * creates in the account. A new calendar works only on the signed-in identity's own record, needs
 * `receivesEvents: true`, and reuses the calendar that Diagonal created before.
 */
export type SdkAddCalendarLinkInput = SdkCalendarLinkFlags & {
  /** One of the viewer's own Google accounts. */
  loginId: string;
} & (
    | { calendarId: string; email?: never; newCalendar?: never }
    | { email: string; calendarId?: never; newCalendar?: never }
    | { newCalendar: true; calendarId?: never; email?: never }
  );

export type SdkUseCalendarConnectionsOptions = {
  /** A Schedulable staff resource. Omit both IDs for the signed-in identity's own record. */
  resourceId?: string | null;
  /** A record of that resource. Another person's record needs "Manage calendar connections" and Show access. */
  recordId?: string | null;
};

export type SdkCalendarConnectionsHandle = {
  /** False while loading, after a failure, and for each `reason`. Render nothing then. */
  available: boolean;
  reason: SdkCalendarUnavailableReason | null;
  /** False when this environment has no Google Calendar client. Connect and Add then fail. */
  configured: boolean;
  staff: SdkCalendarStaff | null;
  links: SdkCalendarLink[];
  /** The viewer's own Google accounts. A viewer adds calendars only through these. */
  logins: SdkCalendarLogin[];
  /** The viewer holds "Manage calendar connections". */
  canManageOthers: boolean;
  /** The outcome of the Google sign-in that just returned to this page. Show it once. */
  result: SdkCalendarSignInResult | null;
  isLoading: boolean;
  /** The read failure, or `null`. A 403 means the viewer may not manage this staff record. */
  error: ApiError | null;
  /**
   * Leave the page for Google sign-in. The browser returns to this page afterwards and
   * `result` carries the outcome. Rejects with the refusal, for example from an editor
   * preview, which can't open Google sign-in.
   */
  connect: () => Promise<void>;
  /** Renew one of the viewer's own Google accounts. Leaves the page like `connect`. */
  reconnect: (loginId: string) => Promise<void>;
  /**
   * End one of the viewer's own Google accounts. Every calendar that uses it stops working.
   * `events` defaults to `keep`.
   */
  disconnect: (
    loginId: string,
    options?: { events?: SdkCalendarEventsChoice }
  ) => Promise<SdkCalendarResult>;
  /** The calendars that the viewer's Google accounts can link, or one account's with `loginId`. */
  listChoices: (loginId?: string) => Promise<SdkCalendarChoices>;
  addLink: (input: SdkAddCalendarLinkInput) => Promise<SdkCalendarResult>;
  /** `events` applies when `receivesEvents` turns off, and defaults to `keep`. */
  updateLink: (
    linkId: string,
    flags: SdkCalendarLinkFlags,
    options?: { events?: SdkCalendarEventsChoice }
  ) => Promise<SdkCalendarResult>;
  /** Stop using a calendar. `events` defaults to `keep`. */
  removeLink: (
    linkId: string,
    options?: { events?: SdkCalendarEventsChoice }
  ) => Promise<SdkCalendarResult>;
};

export type SdkUseSendNotificationOptions = Omit<SdkUseSendEmailOptions, 'onSuccess'> & {
  onSuccess?: (response: SdkSendNotificationResponse) => void;
};

export type SdkSendNotificationHandle = Omit<SdkSendEmailHandle, 'send' | 'data'> & {
  send: (input: SdkSendNotificationInput) => Promise<SdkSendNotificationResponse | null>;
  data: SdkSendNotificationResponse | undefined;
};

/** Resolve a saved template. Omit recipient for a type without a recipient resource. */
export type SdkPreviewNotificationInput = {
  notification_type_id: string;
  /** Omit to use the type's current default template. */
  notification_template_id?: string | null;
  recipient?: SdkNotificationRecipient;
  variables: Record<string, unknown>;
};

/** Why one channel cannot be sent to the previewed recipient. */
export type SdkNotificationChannelUnavailableReason =
  | 'template_off'
  | 'no_permission'
  | 'no_address'
  | 'no_phone'
  | 'opted_out';

export type SdkNotificationChannelAvailability = {
  available: boolean;
  /** Null when the channel is available. */
  reason: SdkNotificationChannelUnavailableReason | null;
  /**
   * The mapped email address or E.164 phone number the channel sends to. Null when the
   * recipient has none, or when the type takes email addresses at send time.
   */
  destination: string | null;
};

/** One channel a notification template can send. */
export type SdkNotificationChannel = 'email' | 'sms';

/**
 * Which channels the viewer can send to the previewed recipient. The server computes it
 * from the template's channels, the viewer's `send_emails` and `send_sms` permissions,
 * the recipient's mapped email address and phone number, and the recipient's SMS consent.
 */
export type SdkNotificationChannels = {
  /** `reason` is `template_off`, `no_permission`, or `no_address`. */
  email: SdkNotificationChannelAvailability;
  /** `reason` is `template_off`, `no_permission`, `no_phone`, or `opted_out`. */
  sms: SdkNotificationChannelAvailability;
};

/** What preview returns: every channel the template enables, before a channel is chosen. */
export type SdkPreviewNotificationResponse = {
  /** The template that was resolved, including a default chosen by the server. */
  notification_template_id: string;
  /** Null when the template has no email. */
  subject: string | null;
  email_layout_id: string | null;
  /** Null when the template has no email. */
  layout_data: Record<string, unknown> | null;
  /** The rendered SMS text, ready to edit. Null when the template has no SMS. */
  sms_body: string | null;
  channels: SdkNotificationChannels;
  resolved_variables: Record<string, unknown>;
};

/** The edited email copy of a resolved draft. */
export type SdkResolvedNotificationEmail = {
  subject: string;
  email_layout_id: string | null;
  layout_data: Record<string, unknown>;
};

/**
 * The complete edited draft that NotificationComposer returns. `delivery_channel` names
 * the channels it sends, and the draft carries exactly the copy those channels need:
 * the email copy, the final `sms_body`, or both. Spread it into
 * useSendResolvedNotification().send() with the type and the recipient. Preserve
 * unchanged fields and the context snapshot.
 */
export type SdkResolvedNotification = {
  notification_template_id: string;
  resolved_variables: Record<string, unknown>;
} & (
  | (SdkResolvedNotificationEmail & {
      delivery_channel: 'email';
      sms_body?: never;
    })
  | (SdkResolvedNotificationEmail & {
      delivery_channel: 'email_and_sms';
      sms_body: string;
    })
  | {
      delivery_channel: 'sms';
      sms_body: string;
      subject?: never;
      email_layout_id?: never;
      layout_data?: never;
    }
);

/**
 * Send the edited draft. An email send carries the email copy; an SMS send carries the
 * final `sms_body`, sent as written; `email_and_sms` carries both. The email copy is
 * prohibited on an SMS-only send, and `sms_body` is prohibited on an email-only send.
 */
export type SdkSendResolvedNotificationInput = Omit<
  SdkPreviewNotificationInput,
  'variables' | 'notification_template_id' | 'recipient'
> &
  Pick<SdkResolvedNotification, 'notification_template_id'> & {
    resolved_variables?: Record<string, unknown>;
    /** The same key that `SdkSendNotificationInput` accepts. */
    idempotency_key?: string;
  } & (
    | (SdkResolvedNotificationEmail &
        Pick<SdkSendEmailInput, 'records' | 'attachments' | 'reply_to' | 'calendar_events'> & {
          delivery_channel?: 'email';
          sms_body?: never;
        } & (
          | { recipient: SdkNotificationRecipient; to?: never }
          | {
              recipient?: never;
              to: Array<SdkEmailRecipient & { data?: never }>;
            }
        ))
    | (SdkResolvedNotificationEmail &
        Pick<SdkSendEmailInput, 'records' | 'attachments' | 'reply_to' | 'calendar_events'> & {
          delivery_channel: 'email_and_sms';
          sms_body: string;
          recipient: SdkNotificationRecipient;
          to?: never;
        })
    | (Pick<SdkSendEmailInput, 'records'> & {
        delivery_channel: 'sms';
        sms_body: string;
        recipient: SdkNotificationRecipient;
        subject?: never;
        email_layout_id?: never;
        layout_data?: never;
        to?: never;
        attachments?: never;
        reply_to?: never;
        calendar_events?: never;
      })
  );

export type SdkSendResolvedNotificationHandle = Omit<SdkSendEmailHandle, 'send' | 'data'> & {
  send: (input: SdkSendResolvedNotificationInput) => Promise<SdkSendNotificationResponse | null>;
  /** The response of the most recent successful call. */
  data: SdkSendNotificationResponse | undefined;
};

/** Scheduling metadata for a record the caller can read. */
export type SdkUpcomingNotification = {
  id: string;
  revision: number;
  idempotency_key: string | null;
  computed_at: string | null;
  scheduled_at_override: string | null;
  changed_by_resource_id: string | null;
  changed_by_record_id: string | null;
  notification_type_name: string;
  scheduled_at: string | null;
  status: 'draft' | 'scheduled' | 'failed';
  is_one_off: boolean;
  is_paused: boolean;
  /** Held occurrences do not send until they are resumed. */
  is_held: boolean;
  can_cancel: boolean;
  can_edit: boolean;
  can_reschedule: boolean;
  can_save: boolean;
  can_schedule: boolean;
  can_send_now: boolean;
  can_hold: boolean;
  can_resume: boolean;
};

/** Raw template source. Liquid and context references resolve when the occurrence sends. */
export type SdkScheduledNotificationContent = {
  email_layout_id: string | null;
  email_subject: string;
  email_layout_data: Record<string, unknown>;
};

export type SdkScheduledNotificationDetail = Omit<SdkUpcomingNotification, 'status'> & {
  status: 'draft' | 'scheduled' | 'failed' | 'queued' | 'cancelled' | 'expired';
  notification_type_id: string;
  notification_template_id: string;
  recipient: SdkNotificationRecipient;
  /** Saved override, or the current shared template when no override exists. */
  content: SdkScheduledNotificationContent;
  has_content_override: boolean;
};

export type SdkScheduledNotificationEdits = {
  expected_revision: number;
  recipient?: SdkNotificationRecipient;
  /** Omit to retain content. Null restores the shared template. Use raw content from ScheduledNotificationComposer, not resolved NotificationComposer output. */
  content_override?: SdkScheduledNotificationContent | null;
};

export type SdkUpdateScheduledNotificationInput = SdkScheduledNotificationEdits & {
  /** Required with scheduled_at to release a saved draft. */
  transition?: 'schedule';
  /** Explicit send-time override. Future ISO datetime with Z or an explicit offset. */
  scheduled_at?: string;
};

export type SdkUpdateScheduledNotificationHandle = {
  /** Rejects with ApiError on permission, validation, or dispatch conflict. Never retries automatically. */
  update: (
    id: string,
    input: SdkUpdateScheduledNotificationInput
  ) => Promise<SdkScheduledNotificationResult>;
  isPending: boolean;
  error: ApiError | null;
  reset: () => void;
};

export type SdkSendScheduledNotificationNowHandle = {
  /** Applies edits and queues this occurrence once. Queued does not mean delivered. Never retries automatically. */
  sendNow: (
    id: string,
    input: SdkScheduledNotificationEdits
  ) => Promise<{ id: string; status: 'queued' }>;
  isPending: boolean;
  error: ApiError | null;
  reset: () => void;
};

export type SdkNotificationHistoryStatus = 'upcoming' | 'sent' | 'failed' | 'cancelled' | 'expired';

/** `sent` rows are saved notifications. `scheduled` rows are occurrences that never sent. */
export type SdkNotificationHistoryKind = 'scheduled' | 'sent';

export type SdkNotificationSourceKind = 'api' | 'one_off' | 'schedule' | 'workflow';

export type SdkEmailAttemptStatus =
  | 'bounced'
  | 'deferred'
  | 'delivered'
  | 'dropped'
  | 'failed'
  | 'sending'
  | 'sent'
  | 'spam_reported'
  | 'suppressed';

export type SdkEmailRecipientRole = 'to' | 'cc' | 'bcc';

export type SdkEmailMessagesRecord = {
  /** Resource the record belongs to. */
  resourceId: string;
  /** Record whose linked emails are included. */
  recordId: string;
};

/**
 * Search filters for tracked email history. Every filter narrows the scope the page
 * chose (a record, several records, or the workspace); none of them widens it.
 */
export type SdkEmailMessagesFilters = {
  /** Only emails linked to a record of this resource. */
  resourceId?: string | null;
  /** Case-insensitive subject substring. */
  subject?: string | null;
  /** Exact recipient addresses; an email matches when any of them received it. */
  emails?: string[];
  /** Recipient roles the `emails` filter checks. Defaults to To, CC, and BCC. */
  recipientRoles?: SdkEmailRecipientRole[];
  /** Exact sender address. */
  fromAddress?: string | null;
  /** Delivery status. */
  status?: SdkEmailAttemptStatus | null;
  /** Inclusive first recorded day, `YYYY-MM-DD` in `timezone`. */
  dateFrom?: string | null;
  /** Exclusive end day, `YYYY-MM-DD` in `timezone`. */
  dateBefore?: string | null;
  /** IANA time zone the date range is read in. Defaults to the viewer's browser time zone. */
  timezone?: string;
  /**
   * Also match unlinked emails sent to the selected records' current email address.
   * Requires the `manage_notifications` permission; the option is dropped silently for
   * other viewers so their linked history still loads.
   */
  includeCurrentAddresses?: boolean;
  /** Email field to read the current address from. Defaults to the Notifiable mapping. */
  emailFieldId?: string | null;
  /** Extra addresses to match alongside the resolved current addresses. */
  additionalAddresses?: string[];
  /** Resolved current addresses to leave out. */
  excludedAddresses?: string[];
};

export type SdkEmailMessagesOptions = {
  /** Pair with recordId for one record's history. */
  resourceId?: string;
  recordId?: string;
  /** Up to 1,000 records. Replaces the single record pair. Each requires Show access. */
  records?: SdkEmailMessagesRecord[];
  /** Explicit workspace scope. Requires manage_notifications. Do not combine with records. */
  workspace?: boolean;
  filters?: SdkEmailMessagesFilters;
  /** Defaults to newest first. Equal sort values are ordered by message ID. */
  sort?: 'created_at' | '-created_at' | 'subject' | '-subject' | 'status' | '-status';
  /** One-based page number. Defaults to 1. */
  page?: number;
  /** Rows per page, 1 to 100. Defaults to 50. */
  perPage?: number;
  /** False pauses the query. Defaults to true. Empty records also leave it idle. */
  enabled?: boolean;
  /** Single-record searches default to 30 seconds. Multi-record and workspace searches do not poll by default. Minimum 1,000 ms. */
  pollInterval?: number | false;
};

export type SdkEmailMessage = SdkEmailAttempt['message'] & {
  /** Workflow links are present only when the viewer can open them. */
  context: {
    workflow_title: string | null;
    workflow_url: string | null;
    execution_url: string | null;
  } | null;
};

export type SdkEmailMessages = {
  data: SdkEmailMessage[];
  pagination: ResourcePagination;
};

export type SdkNotificationHistoryItem = {
  id: string;
  kind: SdkNotificationHistoryKind;
  status: SdkNotificationHistoryStatus;
  /** Saved name for sent rows; the current name, or a deleted placeholder, otherwise. */
  notification_type_name: string;
  notification_template_name: string;
  /** Send time for scheduled rows; preparation time for sent rows. ISO 8601, or null for unscheduled drafts. */
  occurred_at: string | null;
  source_kind: SdkNotificationSourceKind;
  /** Schedule or workflow name. Null without the `manage_notifications` permission. */
  source_name: string | null;
  is_paused: boolean;
  is_held: boolean;
  /** Email attempts the notification produced. Always 0 for scheduled rows. */
  email_count: number;
  /** The attempt furthest along the delivery lifecycle. Null without an attempt. */
  email_status: SdkEmailAttemptStatus | null;
  /** Attempts that have `email_status`. Show this count with the status, not `email_count`. */
  email_status_count: number;
  sms_status_counts?: Record<string, number>;
};

export type SdkNotificationHistory = {
  data: SdkNotificationHistoryItem[];
  pagination: ResourcePagination;
};

export type SdkNotificationHistoryOptions = {
  /** Lifecycle status to list. Defaults to `upcoming`. */
  status?: SdkNotificationHistoryStatus;
  /** Notifications linked to any of these records. The viewer needs Show access to each. */
  records?: { resourceId: string; recordId: string }[];
  /**
   * List the whole workspace. Requires `manage_notifications`. Ignored when `records`
   * is set. Without `records` or `workspace`, the hook waits.
   */
  workspace?: boolean;
  /** Only notifications linked to a record of this resource. */
  resourceId?: string | null;
  /** Case-insensitive match on type, template, ID, or email subject. */
  search?: string | null;
  /** Exact type name. Sent rows match the saved name. */
  typeName?: string | null;
  /** Exact template name. Sent rows match the saved name. */
  templateName?: string | null;
  /** `none` for one-offs, `workflow`, `api`, or a schedule ID. */
  source?: string | null;
  /** Sent only. An attempt status, or `no_attempt` for notifications without an email. */
  emailStatus?: SdkEmailAttemptStatus | 'no_attempt' | null;
  /** Inclusive first day, `YYYY-MM-DD` in `timezone`. */
  dateFrom?: string | null;
  /** Exclusive end day, `YYYY-MM-DD` in `timezone`. */
  dateBefore?: string | null;
  /** IANA time zone for the date range. Defaults to the workspace time zone. */
  timezone?: string;
  /** `occurred_at` (oldest first) or `-occurred_at` (newest first). */
  sort?: 'occurred_at' | '-occurred_at';
  page?: number;
  /** 1 to 100. Defaults to 25. */
  perPage?: number;
  enabled?: boolean;
};

export type SdkEmailAttemptEvent = {
  id: string;
  event_type:
    | 'bounce'
    | 'click'
    | 'deferred'
    | 'delivered'
    | 'dropped'
    | 'group_resubscribe'
    | 'group_unsubscribe'
    | 'open'
    | 'processed'
    | 'spamreport'
    | 'unsubscribe';
  recipient_email: string;
  reason: string | null;
  timestamp: string;
};

export type SdkEmailAttempt = {
  message: {
    id: string;
    from_address: string;
    from_name: string | null;
    to_summary: string;
    subject: string;
    status: SdkEmailAttemptStatus;
    has_html_body: boolean;
    created_at: string;
    /** The files the email carried, without its inline images. */
    attachments: SdkEmailAttemptAttachment[];
  };
  /** Provider delivery events, oldest first. */
  events: SdkEmailAttemptEvent[];
  /**
   * Every To, CC, and BCC address. Null without the `manage_notifications` permission;
   * `message.to_summary` still names the first To address.
   */
  recipients: SdkEmailAttemptRecipients | null;
};

export type SdkEmailAddress = { address: string; name: string | null };

export type SdkEmailAttemptRecipients = {
  to: SdkEmailAddress[];
  cc: SdkEmailAddress[];
  bcc: SdkEmailAddress[];
};

export type SdkEmailAttemptAttachment = {
  name: string;
  mime: string | null;
  /** Size in bytes. */
  size: number | null;
  /** The stored File id. Null when the email did not send a stored file. */
  id: string | null;
  /** A link that opens the file for a viewer who can read the email. Null without a stored file. */
  url: string | null;
};

export type SdkNotificationHistoryRecord = {
  id: string;
  resource_id: string;
  label: string;
  resource_name: string;
  is_archived: boolean;
};

export type SdkNotificationHistoryDetail = Omit<
  SdkNotificationHistoryItem,
  'email_count' | 'email_status' | 'email_status_count' | 'sms_status_counts'
> & {
  /** Preparation error. Null without the `manage_notifications` permission. */
  error: string | null;
  /** Saved variable values. Empty without the `manage_notifications` permission. */
  variables: Record<string, unknown>;
  /** Workflow context of a sent notification. Links appear only for viewers who can open them. */
  context: {
    workflow_title: string | null;
    workflow_url: string | null;
    execution_url: string | null;
  } | null;
  /** Linked records the viewer can read. Other links are left out. */
  records: SdkNotificationHistoryRecord[];
  /** For a queued scheduled row, the saved notification to open with kind `sent`. */
  sent_notification_id: string | null;
  /** Readable email attempts in send order. Empty for scheduled rows and prepared notifications without an email. */
  attempts: SdkEmailAttempt[];
  /** Readable SMS messages in send order. A record-scoped viewer sees the first destination only. */
  sms_messages?: SdkSmsMessage[];
};

export type SdkSmsMessageStatus =
  | 'queued'
  | 'sending'
  | 'sent'
  | 'delivered'
  | 'failed'
  | 'suppressed';

export type SdkSmsMessage = {
  id: string;
  from_number: string;
  to_number: string;
  /** The text as sent. */
  body: string;
  status: SdkSmsMessageStatus;
  /** Why a failed or suppressed message did not reach the carrier. */
  status_reason: string | null;
  /** `gsm7` or `ucs2`. */
  encoding: string;
  segments: number;
  sent_at: string | null;
  finalized_at: string | null;
};

export type SdkUpcomingNotifications = {
  data: SdkUpcomingNotification[];
  pagination: ResourcePagination;
};

export type SdkUpcomingNotificationsOptions = {
  resourceId: string | null | undefined;
  recordId: string | null | undefined;
  page?: number;
  perPage?: number;
  enabled?: boolean;
};

export type SdkScheduleNotificationInput =
  | ({
      notification_type_id: string;
      notification_template_id: string;
      recipient: SdkNotificationRecipient;
      variables: Record<string, unknown>;
      content_override?: SdkScheduledNotificationContent | null;
      idempotency_key?: string;
    } & (
      | { status: 'draft'; idempotency_key: string; scheduled_at?: never }
      | { status?: 'scheduled'; scheduled_at: string }
    ))
  | {
      notification_schedule_id: string;
      source_record_id: string;
      stage_id: string;
      status: 'draft';
      content_override?: SdkScheduledNotificationContent | null;
    };

export type SdkScheduledNotificationResult = {
  id: string;
  revision: number;
  status: 'draft' | 'scheduled' | 'failed' | 'cancelled' | 'queued' | 'expired';
};
export type SdkCancelledNotificationResult = {
  id: string;
  revision: number;
  status: 'cancelled';
};

export type SdkScheduleNotificationHandle = {
  /** Rejects with ApiError on failure. */
  schedule: (input: SdkScheduleNotificationInput) => Promise<SdkScheduledNotificationResult>;
  isPending: boolean;
  error: ApiError | null;
  reset: () => void;
};

export type SdkCancelScheduledNotificationHandle = {
  /** Rejects with ApiError on failure, including 409 once delivery has started. */
  cancel: (id: string, expectedRevision: number) => Promise<SdkCancelledNotificationResult>;
  isPending: boolean;
  error: ApiError | null;
  reset: () => void;
};

export type SdkHeldNotificationResult = {
  id: string;
  revision: number;
  status: 'scheduled' | 'failed';
  is_held: boolean;
  scheduled_at: string;
};

export type SdkHoldScheduledNotificationHandle = {
  /** Rejects with ApiError on failure, including 409 once delivery has started. */
  hold: (id: string, expectedRevision: number) => Promise<SdkHeldNotificationResult>;
  isPending: boolean;
  error: ApiError | null;
  reset: () => void;
};

export type SdkResumeScheduledNotificationHandle = {
  /**
   * Pass a future ISO `scheduledAt` with Z or an offset to send at a new time. It is required
   * when the saved time passed during the hold. Rejects with ApiError on failure.
   */
  resume: (
    id: string,
    expectedRevision: number,
    scheduledAt?: string
  ) => Promise<SdkHeldNotificationResult>;
  isPending: boolean;
  error: ApiError | null;
  reset: () => void;
};

/** Paginated notification catalog options. Requires send_emails or send_sms. */
export type SdkNotificationListOptions = {
  page?: number;
  /** Between 1 and 100. Defaults to 50. */
  perPage?: number;
  search?: string;
  /** Only templates that support every requested channel, and only types with such a template. */
  deliveryChannel?: SdkNotificationDeliveryChannel;
  enabled?: boolean;
};

export type SdkNotificationTypeListOptions = SdkNotificationListOptions & {
  /** notification selects record recipients; email selects address recipients. */
  recipientMode?: 'notification' | 'email';
};

/** One value a sender supplies for a notification type. */
export type SdkNotificationVariableSummary = {
  key: string;
  /** Falls back to the key in title case. */
  label: string;
  /** The variable type key, such as `text`, `date`, `select`, or `record`. */
  type: string;
  description: string | null;
  required: boolean;
};

export type SdkNotificationTypeSummary = {
  id: string;
  name: string;
  /** Every variable key, including the derived `record` of a type with a recipient resource. */
  variableKeys: string[];
  /** The values a sender supplies, in order. Never includes the derived `record`. */
  variables: SdkNotificationVariableSummary[];
  notifiable_resource_id: string | null;
};

export type SdkNotificationTemplateSummary = {
  id: string;
  name: string;
  notification_type_id: string;
  notification_type_name: string;
  email_layout_name: string | null;
  is_default: boolean;
  is_email_enabled: boolean;
  is_sms_enabled: boolean;
};

export type SdkNotificationTypeList = {
  data: SdkNotificationTypeSummary[];
  pagination: ResourcePagination;
};

export type SdkNotificationTemplateList = {
  data: SdkNotificationTemplateSummary[];
  pagination: ResourcePagination;
};

export type SdkNavigationMenu = {
  id: string;
  name: string;
  items: Diagonal.Navigation.Data.NavigationMenuItemData[];
};

export type SdkNavigationMenuCatalog = Record<string, SdkNavigationMenu>;

/** A RecordEvent lifecycle event a workflow input can be attached to. */
export type SdkWorkflowEvent = 'created' | 'deleted' | 'restored' | 'saved' | 'updated';

/**
 * A resource's published RecordEvent workflow inputs, grouped by trigger event
 * and keyed by input key — the shape of `resource.workflow_inputs`.
 */
export type SdkResourceWorkflowInputs = Partial<
  Record<SdkWorkflowEvent, Record<string, SdkWorkflowInput>>
>;

/**
 * The flat workflow-input map a resource-record form exposes as `workflowInputs`
 * in its render state: the resource's `workflow_inputs` buckets for the form's
 * lifecycle events (`created`/`saved` on create, `updated`/`saved` on update)
 * merged into one map keyed by input key.
 */
export type SdkResourceFormWorkflowInputs<
  TResource extends Pick<SdkResource, 'workflow_inputs'>,
  TEvents extends SdkWorkflowEvent
> = [Extract<keyof TResource['workflow_inputs'], TEvents>] extends [never]
  ? Record<never, never>
  : UnionToIntersection<
      NonNullable<
        TResource['workflow_inputs'][Extract<keyof TResource['workflow_inputs'], TEvents>]
      >
    >;

export type SdkWorkflowInputSettings = {
  ui?: SdkResourceFieldSettingsUi;
  [key: string]: unknown;
};

/** A published workflow input declaration owned by one RecordEvent workflow. */
export type SdkWorkflowInput = {
  workflow_id: string;
  workflow_resource_id?: string | null;
  workflow_event?: string | null;
  id: string | null;
  workspace_id?: string | null;
  configurable_id: string | null;
  configurable_type: string | null;
  type: string;
  scope: string | null;
  key: string;
  value: unknown;
  label: string | null;
  description: string | null;
  guard: unknown[] | Record<string, unknown> | null;
  settings: SdkWorkflowInputSettings | null;
  resolver: Record<string, unknown> | null;
  sort_order?: number | null;
  created_at?: string;
  updated_at?: string;
  deleted_at?: string | null;
};

/**
 * The current logged-in identity, or `null` for anonymous requests.
 *
 * It is the identity's underlying resource record, verbatim — the same plain shape as
 * records read via `Sdk.resource(...)` — plus `$`-prefixed keys the platform computes:
 * `$display_name` (the resource's configured display value for this record) and the
 * loaded `$permission_ids` / `$permission_slugs`. Workspace-defined fields (e.g. `name`,
 * `email`, phone) are returned as-is and exist only if the auth resource defines them.
 * The password hash is stripped at the data layer and the remember token is omitted.
 * Use `$display_name` when labeling a person; use fields when using their data.
 */
export interface PageIdentityCatalog {}

type PageIdentityRecord = keyof PageIdentityCatalog extends never
  ? Record<string, any>
  : PageIdentityCatalog;

export type SdkIdentity =
  | ({
      id: string;
      workspace_id: string;
      resource_id: string;
      created_at: string;
      updated_at: string;
      $display_name: string;
      $permission_ids: string[];
      $permission_slugs: string[];
    } & PageIdentityRecord)
  | null;

export type SdkPermissions = {
  /** Return whether the current identity has a permission id or slug. */
  has(permissionIdOrSlug: string): boolean;
  /** Return whether the current identity has at least one of the supplied permission ids or slugs. */
  hasAny(permissionIdsOrSlugs: readonly string[]): boolean;
  /** Return whether the current identity has the system-administrator permission. */
  isSystemAdmin(): boolean;
};

/**
 * A workspace field definition exposed to pages via `Sdk.workspace.fields.<key>`.
 * Carries no value: read values through `Sdk.hooks.useWorkspaceValues`.
 */
export type SdkWorkspaceField = {
  id: string | null;
  key: string;
  type: string;
  label: string | null;
  /** Rendered as help text under inputs. */
  description: string | null;
  guard: string[] | Record<string, string[]> | null;
  settings: Record<string, unknown>;
  /** Derived on the server from `required` guard rules. */
  is_required: boolean;
  /** Read-only system fields and package-locked values. `WorkspaceFieldsForm` never renders these. */
  is_read_only: boolean;
  /** Hidden from the admin field list because it has its own management surface. */
  is_hidden: boolean;
};

/** A field handle accepted by `FormFieldInput` and the form render-prop `FieldInput`. */
export type SdkFormField = SdkResourceField | SdkWorkspaceField;

/** Workspace field values keyed by field key. */
export type SdkWorkspaceValues = Record<string, unknown>;

/**
 * The current workspace record exposed to pages via `Sdk.workspace`.
 *
 * Mirrors the serialized `Diagonal.Workspaces.Data.WorkspaceData` shape plus the
 * field definitions under `fields`. `values` is the load-time snapshot of the
 * field-value map keyed by stable workspace field key; live reads go through
 * `Sdk.hooks.useWorkspaceValues`. Declared locally rather than aliased to
 * `Diagonal.*` because the SDK `.dts` flattener degrades `Diagonal.*` references to
 * `any`.
 */
export type SdkWorkspace = {
  id: string;
  name: string;
  slug: string;
  primary_domain_id: string | null;
  /** The workspace's default navigation menu, resolvable via `Sdk.navigationMenu()`. */
  default_navigation_menu_id: string | null;
  /** Workspace field definitions keyed by field key. Values live in `Sdk.hooks.useWorkspaceValues`. */
  fields: Record<string, SdkWorkspaceField>;
  /** @deprecated Load-time snapshot. Read live values with `Sdk.hooks.useWorkspaceValues`. */
  values: Record<string, unknown> | null;
} | null;

/**
 * The serialized workspace record as the server ships it, before the runtime
 * attaches the field definition catalog from the separate `sdkWorkspaceFields` prop.
 */
export type SdkWorkspaceRecord = Omit<NonNullable<SdkWorkspace>, 'fields'>;

/**
 * HTTP helper wrapping the application's shared Axios client.
 * Returns the parsed response body on 2xx; throws an {@link SdkApiError} on non-2xx.
 *
 * @remarks
 * `Sdk.api` vs server `dapi`:
 * - `Sdk.api` runs in the browser inside a rendered page. Use it for client-driven
 *   reads/writes from a page.
 * - `dapi` runs on the server in the customer-code execution sandbox. Use it for one-shot
 *   tasks driven by the AI via `execute_code`, not for code that ships inside a page.
 *
 * @example
 * ```tsx
 * const summary = await Sdk.api.get('/api/notifications/summary');
 * await Sdk.api.post('/api/notifications/mark-read', {
 *   ids: summary.unread_ids
 * });
 * ```
 */
export type SdkApi = {
  /** Parsed body of a 2xx GET; throws {@link SdkApiError} on non-2xx. */
  get<T = unknown>(url: string, config?: AxiosRequestConfig): Promise<T>;
  /** Parsed body of a 2xx POST; throws {@link SdkApiError} on non-2xx. */
  post<T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T>;
  /** Parsed body of a 2xx PUT; throws {@link SdkApiError} on non-2xx. */
  put<T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T>;
  /** Parsed body of a 2xx PATCH; throws {@link SdkApiError} on non-2xx. */
  patch<T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T>;
  /** Parsed body of a 2xx DELETE; throws {@link SdkApiError} on non-2xx. */
  delete<T = unknown>(url: string, config?: AxiosRequestConfig): Promise<T>;
};

/** A failed SDK request exposed to page code. */
export type SdkApiError = {
  /** HTTP status code, or `0` when no response was received. */
  readonly status: number;
  /** Parsed response body, or `null` when no response body was received. */
  readonly body: unknown;
  /** Human-readable request failure message. */
  message: string;
};

/** Cache key for {@link SdkHooks.useApi}. The first segment groups related endpoint data. */
export type SdkApiQueryKey = readonly [string, ...unknown[]];

export type SdkApiFetcher<TData> = (api: SdkApi) => Promise<TData>;

/** Authoritative subscription billing terms returned by the backend. */
export type SdkSubscriptionPreview = Diagonal.Core.Data.SubscriptionPreviewData;

export type SdkSubscriptionPreviewOptions = {
  /** Subscription resource ID. No payer or business record is needed. */
  resourceId: string;
  /** Eligible price IDs and integer quantities of at least one. */
  items: { price_record_id: string; quantity: number }[];
  /**
   * Up to ten discounts. Requires Store access to the discount resource. A specific-period
   * discount applies to the invoice whose billing period starts on `period_start`.
   */
  discounts?: {
    calculation_type: 'percentage' | 'fixed_amount';
    percent?: number;
    fixed_amount?: { amount: number; currency: string };
    duration: 'first_invoice' | 'every_invoice' | 'specific_period';
    period_start?: string;
    description?: string;
  }[];
  /** Proposed service start, in the subscription's configured timezone. */
  startedAt?: string;
  /** Permitted calendar anchor month. Monthly billing validates only the day. */
  billingAnchorMonth?: number;
  /** Permitted calendar anchor day. */
  billingAnchorDay?: number;
  /** Calendar mode uses billingAnchorMonth and billingAnchorDay. Omitted uses the Subscription capability default. */
  billingCycleMode?: 'anniversary' | 'calendar_aligned';
  /** Activation policy override. Omitted uses the Subscription capability default. */
  activationPolicy?: 'immediate' | 'after_first_payment';
  /** With first_payment_at, dates assume payment on the preview day and move to the payment date. */
  initialTermAnchor?: 'subscription_created_at' | 'first_payment_at';
  /** Days allowed for the first payment. Omitted uses the Subscription capability default. */
  initialPaymentWindowDays?: number;
  /** Trial duration in days. Zero disables a default trial. Use either this or trialEndsAt. */
  trialDays?: number;
  /** ISO trial-end date in the billing timezone. Use either this or trialDays. */
  trialEndsAt?: string;
  /** Saved payment method requirement for a trial. Omitted uses the Subscription capability default. */
  trialPaymentMethodPolicy?: 'optional' | 'required';
  /** Days past due before lapse. Omitted uses the Subscription capability default. */
  gracePeriodDays?: number;
  /** Billing behavior after lapse. Omitted uses the Subscription capability default. */
  lapseBillingBehavior?: 'continue_billing' | 'suspend_renewals' | 'cancel';
  /** Hold requests until the selection is ready. Defaults to true. */
  enabled?: boolean;
};

export type SdkSubscriptionPreviewHandle = {
  /** Hidden during loading, after failure, and while disabled. */
  preview: SdkSubscriptionPreview | undefined;
  /** Current quote, or null while no terms can be reviewed. */
  quote: string | null;
  isLoading: boolean;
  error: ApiError | null;
  /** Refresh terms after a failed creation or before renewed confirmation. */
  refetch: () => Promise<void>;
};

/** Authoritative terms for a change to the items of an existing subscription. */
export type SdkSubscriptionChangePreview = Diagonal.Core.Data.SubscriptionChangePreviewData;

export type SdkSubscriptionChangePreviewOptions = {
  /** Subscription resource ID. */
  resourceId: string;
  /** Subscription record ID. The visitor needs Show access to this record. */
  recordId: string;
  /** The complete desired selection: price IDs and integer quantities of at least one. */
  items: {
    price_record_id: string;
    quantity: number;
    pricing?: {
      mode: 'catalog' | 'custom';
      unit_price?: { amount: number; currency: string };
    };
  }[];
  /** Apply now, keep current items until renewal, or start at a later billing period. Defaults to now. */
  effective?: 'now' | 'next_renewal' | 'period';
  /**
   * Start date (`YYYY-MM-DD`, in the subscription's preview timezone) of the later billing period
   * for `effective: 'period'`. It must be a future billing boundary that has no invoice. It is
   * ignored for other timings. The hook holds the request until it is set.
   */
  periodStart?: string;
  /** Ordinary current-period billing. Defaults to prorate. Initial and renewal invoice revisions ignore this option and use their invoice terms. */
  prorationBehavior?: 'prorate' | 'none';
  /**
   * Revise an unpaid renewal invoice that was already issued. Use `effective: 'now'` for the
   * current-period invoice and `effective: 'next_renewal'` for the upcoming renewal invoice.
   * `this_renewal` changes only that invoice; `this_and_following` also carries recurring
   * items forward. Omit for an ordinary item change.
   */
  revisionScope?: 'this_renewal' | 'this_and_following';
  /** Hold requests until the visitor is signed in and the selection is ready. Defaults to true. */
  enabled?: boolean;
};

export type SdkSubscriptionChangePreviewHandle = {
  /** Hidden during loading, after failure, and while disabled. */
  preview: SdkSubscriptionChangePreview | undefined;
  /** Current quote, or null while no terms can be reviewed. */
  quote: string | null;
  isLoading: boolean;
  error: ApiError | null;
  /** Refresh terms after a failed change or before renewed confirmation. */
  refetch: () => Promise<void>;
};

/** One schedulable record, such as a staff member or a room. */
export type SdkSchedulableRecordRef = { resourceId: string; id: string };

/**
 * Schedulable records that a booking needs together. With `mode: 'all'` the booking needs
 * every record. With `mode: 'any'` it needs one of them, chosen by `strategy`.
 */
export type SdkSlotSearchGroup = {
  /** Defaults to `'all'`. */
  mode?: 'all' | 'any';
  /** Only for `mode: 'any'`. Defaults to `'first_available'`. */
  strategy?: 'first_available' | 'least_booked' | 'round_robin';
  records: SdkSchedulableRecordRef[];
};

export type SdkAvailableSlotsOptions = {
  /** Booking resource ID. Its booking settings decide what a slot occupies. */
  resourceId: string;
  /** At least one group, each with at least one record. */
  groups: SdkSlotSearchGroup[];
  /** Start of the search window: a `Date` or an ISO 8601 date-time. */
  from: Date | string;
  /** End of the search window: a `Date` or an ISO 8601 date-time. */
  to: Date | string;
  /** Length of each slot in minutes. */
  durationMinutes: number;
  /** Minutes between slot starts. Defaults to 15. */
  stepMinutes?: number;
  /** The first slot starts at least this many minutes from now. */
  noticeMinutes?: number;
  /** The last slot starts at most this many days from now. */
  maxAdvanceDays?: number;
  /** Units each slot needs, for records with a capacity. Defaults to 1. */
  quantity?: number;
  /** Most slots to return. Defaults to 50; at most 500. */
  maxSlots?: number;
  /** IANA time zone for each slot's `date`. When omitted, read the zone the search used from `timezone`. */
  timezone?: string;
  /** Makes the `round_robin` choice repeatable for the same seed. */
  seed?: string;
  /** Hold the search until its inputs are ready. Defaults to `true`. */
  enabled?: boolean;
};

/** One free time. `assignments` names the records that each group would book. */
export type SdkAvailableSlot = {
  /** Stable for the same start, end, and assignments. */
  id: string;
  start: string;
  end: string;
  /** The local date of `start` in the search time zone, `YYYY-MM-DD`. */
  date: string;
  assignments: {
    group: number;
    records: { resource_id: string; id: string }[];
  }[];
};

export type SdkAvailableSlotsHandle = {
  /** Slots in time order. Empty while loading, after a failure, and while disabled. */
  slots: SdkAvailableSlot[];
  /** The time zone of each slot's `date`, or null until a search succeeds. */
  timezone: string | null;
  /** True when the search stopped before the end of the window, for example at `maxSlots`. */
  stoppedEarly: boolean;
  isLoading: boolean;
  /** A 422 names the invalid option; a 429 means a guest searched too often. */
  error: ApiError | null;
  refetch: () => Promise<void>;
};

export type SdkAvailabilityOptions = {
  /** Schedulable resource ID. */
  resourceId: string;
  /** Record IDs of that resource. At most 50. */
  ids: string[];
  /** Start of the window: a `Date` or an ISO 8601 date-time. */
  from: Date | string;
  /** End of the window: a `Date` or an ISO 8601 date-time. At most 62 days after `from`. */
  to: Date | string;
  /** Hold the read until its inputs are ready. Defaults to `true`. */
  enabled?: boolean;
  /**
   * Words on the closed time of `backgroundTimes`. `busy` defaults to "Busy" and `closed` to
   * "Closed". `false` keeps the closed shading without words. Changing them sends no request.
   */
  labels?: { busy?: string; closed?: string } | false;
};

/** One part of a record's availability and the source that made it. */
export type SdkAvailabilityPiece = {
  /** `external` is busy time on the record's blocking connected calendars. It never names the calendar. */
  source: 'hours' | 'exception' | 'limit' | 'closure' | 'external';
  effect: 'open' | 'closed' | 'replace';
  start: string;
  end: string;
  /** The record that the piece came from, such as an exception record, or null for hours and busy time. */
  record_id: string | null;
};

export type SdkRecordAvailability = {
  id: string;
  /** The time zone that the record's hours are read in. */
  timezone: string;
  /** Free time in the window after hours, exceptions, limits, closures, and calendar busy time. */
  available: { start: string; end: string }[];
  pieces: SdkAvailabilityPiece[];
  /**
   * Times for `Sdk.Components.Calendar`: free time with the open tone, then closed time inside
   * the record's hours, labelled "Busy" for busy time on connected calendars and "Closed" for
   * exceptions, closures, and limits. Time outside the record's hours is absent.
   */
  backgroundTimes: CalendarBackgroundTime[];
};

export type SdkAvailabilityHandle = {
  /** One entry for each requested record. Empty while loading, after a failure, and while disabled. */
  records: SdkRecordAvailability[];
  isLoading: boolean;
  error: ApiError | null;
  refetch: () => Promise<void>;
};

export type SdkScheduleConflictsOptions = {
  /** Resource ID of the records that hold time: a Booking resource or an Eventable resource with a Calendar Owner. */
  resourceId: string;
  /** Only conflicts of these records. At most 50. */
  ids?: string[];
  /** Only conflicts on these Schedulable records. At most 50 IDs. */
  bookable?: { resourceId: string; ids: string[] };
  /** Hold the read until its inputs are ready. Defaults to `true`. */
  enabled?: boolean;
};

/** One held time that closed time conflicts with. */
export type SdkScheduleConflict = {
  id: number;
  /** `booking` for a booking's claim, `owned_event` for an event on its calendar owner's time. */
  kind: 'booking' | 'owned_event';
  /** The record that holds the time. */
  record_id: string;
  /** The field of that record that names the Schedulable record. */
  relation: string;
  bookable_resource_id: string;
  bookable_id: string;
  /** The record's own start, without booking buffers. */
  start: string;
  /** The record's own end, without booking buffers. */
  end: string;
  /** The start of the time detection checks: a booking's claim with the buffers of its resource. Equals `start` for an owned event. */
  claim_start: string;
  /** The end of the time detection checks. Equals `end` for an owned event. */
  claim_end: string;
  /** What closes the time. `external` is busy time on a blocking connected calendar. It never names the calendar. */
  sources: ('exception' | 'closure' | 'external' | 'limit' | 'hours')[];
  detected_at: string;
};

export type SdkScheduleConflictsHandle = {
  /** Conflicts whose time has not ended, by start. Empty while loading, after a failure, while disabled, and for a guest. */
  conflicts: SdkScheduleConflict[];
  /** More conflicts remain than the read returned (at most 500). Narrow the read with `ids` or `bookable` to see them. */
  truncated: boolean;
  isLoading: boolean;
  error: ApiError | null;
  refetch: () => Promise<void>;
};

/** The refund amount limits of one payment. All amounts use the payment currency. */
export type SdkPaymentRefundPreview = Diagonal.Core.Data.PaymentRefundPreviewData;

export type SdkPaymentRefundPreviewOptions = {
  /** Payment resource ID. */
  resourceId: string;
  /** Payment record ID. The visitor needs Show access to this record. */
  recordId: string;
  /** Hold requests until the visitor is signed in and the payment is known. Defaults to true. */
  enabled?: boolean;
};

export type SdkPaymentRefundPreviewHandle = {
  /** Hidden during loading, after failure, and while disabled. */
  preview: SdkPaymentRefundPreview | undefined;
  isLoading: boolean;
  error: ApiError | null;
  /** Refresh the limits after a refund or a rejected refund. */
  refetch: () => Promise<void>;
};

/**
 * One accounting entity of the workspace. Inactive entities are included, so a stored
 * entity ID keeps its name. A record of an invoice, subscription, or payer balance entry
 * resource carries its entity as `record.__meta?.accounting_entity`.
 */
export type SdkAccountingEntity = Diagonal.Workspaces.Data.WalletAccountingEntityFieldOptionData;

export type SdkAccountingEntities = {
  /** Ordered by name. Empty while loading, after failure, in a branch, and for a signed-out visitor. */
  entities: SdkAccountingEntity[];
  isLoading: boolean;
  error: ApiError | null;
};

/**
 * `record.__meta?.capabilities?.eventable?.series` on an occurrence of a resource that repeats
 * on the series engine. Change the series with `$operation.eventable.scope`
 * (`this_and_future` or `all`). Without a scope a change applies to this occurrence only.
 */
export type SdkSeriesOccurrenceMeta = {
  series_id: string;
  /** The occurrence key, today its local date (`YYYY-MM-DD`). Treat it as opaque. */
  key: string;
  /** `restored`: a person restored it after the series removed it. `kept`: the series could not change it, or a lock rule, a referrer, a hook, or the editor's policy kept it from removal. */
  state: 'regular' | 'restored' | 'kept';
  /** Why the series kept it. Present only while `state` is `kept`. */
  reason?:
    | 'series_kept_referenced'
    | 'series_kept_policy'
    | 'series_kept_locked'
    | 'series_kept_hook'
    | 'series_kept_override';
  /** Fields this occurrence changed on its own, which series changes leave alone. */
  overrides: string[];
};

/**
 * `meta.series` of a save that changed a series: the counts of the first run and each
 * occurrence the change could not apply, in the per-record shape of bulk actions.
 */
export type SdkSeriesChangeResult = {
  series_id: string;
  /** The counts cover only what ran before the save returned. */
  counts: {
    created: number;
    moved: number;
    updated: number;
    removed: number;
    kept: number;
    failed: number;
    /** Booking dates that wait for rescheduling, the queued remainder included. */
    waiting: number;
  };
  items: Array<{
    id: string;
    status: 'error';
    http_status: number;
    errors: string[];
    code:
      | 'series_kept_referenced'
      | 'series_kept_policy'
      | 'series_kept_locked'
      | 'series_kept_hook'
      | 'series_kept_override'
      | 'series_kept_capacity'
      | 'series_failed';
  }>;
  /**
   * On a Booking resource: every date of the planned range that waits for rescheduling. Each
   * booking is written with the waiting status, to reschedule by hand. `reason` is `full` for a
   * date with no capacity left and `closed` for a date outside open hours on a resource that
   * requires them. `record_id` is null for a date the queued remainder has not written yet.
   */
  waiting: Array<{
    date: string;
    record_id: string | null;
    reason: 'full' | 'closed';
  }>;
  /**
   * What part of the change still runs on the queue after the save returned, or null when it ran
   * whole. `from` is the first date of the queued remainder within the horizon, or null when only
   * the past part waits. `past` is true while the past occurrences of an "All occurrences" change
   * still wait. The counts cover only what ran before the save returned, so a save whose run was
   * queued whole has zero counts.
   */
  queued: { from: string | null; past: boolean } | null;
};

/**
 * `meta.booking` of a create on a Booking resource whose idempotency value matched a booking
 * the same request already made: the response holds that booking, and nothing was booked again.
 */
export type SdkBookingCreateResult = {
  replayed: true;
};

/** The available payer balance of one accounting entity. */
export type SdkPayerEntityBalance = Diagonal.Core.Data.PayerEntityBalanceData;

export type SdkPayerBalancesOptions = {
  /** Payer resource ID. */
  resourceId: string;
  /** Payer record ID. The visitor needs Show access to this record and its balance field. */
  recordId: string;
  /** Hold requests until the visitor is signed in and the payer is known. Defaults to true. */
  enabled?: boolean;
};

export type SdkPayerBalances = {
  /** Each active entity, and each inactive entity with an available balance above zero. Empty while loading, after failure, and while disabled. */
  balances: SdkPayerEntityBalance[];
  /** The payer's balance field. Null without a balance, while loading, after failure, and while disabled. */
  total: { amount: number; currency: string } | null;
  isLoading: boolean;
  error: ApiError | null;
  /** Refresh the amounts after a ledger entry or a payment. */
  refetch: () => Promise<void>;
};

/** One installed package's release history, as `Sdk.hooks.usePackageReleaseNotes` returns it. */
export type SdkPackageReleaseHistory = Diagonal.Packages.Data.PackageReleaseHistoryData;

export type SdkPackageReleaseNotesOptions = {
  /** Only this installed package. Omit it for every installed package. */
  packageId?: string;
  /** How many releases at or below the installed one to list per package. Newer releases are always listed. Defaults to 20; at most 100. */
  limit?: number;
  /** Hold the request until its inputs are ready. Defaults to true. */
  enabled?: boolean;
};

export type SdkPackageReleaseNotes = {
  /** Each installed package, by name. Empty while loading, after failure, and while disabled. */
  packages: SdkPackageReleaseHistory[];
  isLoading: boolean;
  error: ApiError | null;
  refetch: () => Promise<void>;
};

export type SdkPackageReleaseNotesProps = {
  /** Only this installed package. Omit it to list every installed package. */
  packageId?: string;
  /** Show only the installed release and the newer ones, for a dashboard. */
  compact?: boolean;
  /** How many releases at or below the installed one to list per package. */
  limit?: number;
  /** Shown when no package is installed. */
  emptyMessage?: string;
  className?: string;
};

export type SdkUseApiOptions<TData = unknown> = {
  /** Hold the request until its inputs are ready. Defaults to `true`. */
  enabled?: boolean;
  /** Keep the last successful value visible while a changed key fetches. */
  keepPreviousData?: boolean;
  /**
   * Refetch this endpoint on an interval. The function form receives the current `data`
   * (`undefined` before the first fetch) and returns `false` to stop.
   *
   * @example
   * ```tsx
   * Sdk.hooks.useApi(
   *   ['records', order.id, 'fulfillment'],
   *   (api) => api.get(Sdk.utils.workflow.url('...', { order_id: order.id })),
   *   { pollInterval: (data) => (data?.shipped ? false : 5000) }
   * );
   * ```
   */
  pollInterval?: SdkPollInterval<TData | undefined>;
};

export type SdkApiHandle<TData> = {
  data: TData | undefined;
  isLoading: boolean;
  isError: boolean;
  /** The normalized request failure, or `null` before/after a successful request. */
  error: ApiError | null;
  /**
   * Whole seconds until the next automatic `pollInterval` request, rounded up. `0`
   * means a refresh is in flight. `null` means no refresh is scheduled.
   */
  refreshIn: number | null;
  /** Mark this exact key stale and refetch it when active. */
  invalidate: () => Promise<void>;
};

export type SdkRouterVisitOptions = TaskRouterVisitOptions;

export type SdkRouter = TaskRouter;

/**
 * An authenticatable resource the workspace can log identities in against. `id` is the
 * resource id passed to {@link SdkAuth.login}. The resource's email identifier field is
 * resolved server-side by the Dynamic Auth flow and is deliberately not part of the
 * catalog — nothing here is a login parameter besides the resource id.
 */
export type SdkAuthResource = {
  id: string;
  name: string;

  /** Whether the resource has a password field, and so can offer a reset link. */
  passwordResetEnabled: boolean;

  /**
   * Whether the resource opted in to emailed sign-in links. When false,
   * {@link SdkAuth.sendMagicLink} is rejected server-side for this resource.
   */
  magicLinkEnabled: boolean;

  /**
   * Whether the resource requires an SMS code after the password or emailed link. A
   * sign-in then continues with {@link SdkAuth.twoFactor}.
   */
  twoFactorEnabled: boolean;
};

/**
 * How a sign-in that needs two-factor continues. `'hosted'`, the default, leaves the page
 * for Diagonal's challenge page. `'inline'` stays on this page and sets
 * {@link SdkAuth.twoFactor}: the page must then render `Sdk.Components.TwoFactorChallenge`
 * and `Sdk.Components.TwoFactorSetup`, or `Sdk.Components.LoginForm`, which renders both.
 */
export type SdkTwoFactorMode = 'hosted' | 'inline';

/**
 * A sign-in of this browser whose first factor passed and whose second factor is
 * outstanding. It lasts 10 minutes or 5 wrong codes.
 */
export type SdkTwoFactorPending = {
  /** The authenticatable resource that the sign-in belongs to. */
  resourceId: string;

  /**
   * `'challenge'`: the visitor enters a code sent to the account's MFA number, or a backup
   * code. `'setup'`: the account has no MFA number yet, so the visitor sets one up first.
   */
  stage: 'challenge' | 'setup';
};

/**
 * Credentials for {@link SdkAuth.login}. `resourceId` is optional — it defaults to the
 * workspace's sole authenticatable resource ({@link SdkAuth.resourceId}) and is required
 * only when the workspace has more than one. `redirectPage` is the **id of the page** to
 * land on after sign-in — pass a stable page id, never a path; the server resolves the
 * page's route itself. A stored intended URL — the protected URL the visitor was bounced
 * to the login page from — takes precedence, the workspace homepage is the final
 * fallback, and an id that does not resolve to a same-workspace page with a param-free
 * route is ignored server-side (it never fails the login). For destinations that depend
 * on who logged in, redirect to a static page that reads `Sdk.identity` instead.
 * Set `returnTo: 'current'` to land back on the page the login was submitted from,
 * path parameters and query string included; it takes precedence over `redirectPage`.
 */
export type SdkAuthLoginInput = {
  identifier: string;
  password: string;
  resourceId?: string;
  redirectPage?: string;
  returnTo?: 'current';
  /** See {@link SdkTwoFactorMode}. Defaults to `'hosted'`. */
  twoFactor?: SdkTwoFactorMode;
};

/**
 * Request an emailed sign-in link for {@link SdkAuth.sendMagicLink}. `resourceId` defaults
 * to the workspace's sole authenticatable resource, exactly as on {@link SdkAuthLoginInput},
 * and `redirectPage` is the **id of the page** to land on once the link is followed. Set
 * `returnTo: 'current'` to carry the exact served page URL through the emailed link.
 */
export type SdkAuthMagicLinkInput = {
  identifier: string;
  resourceId?: string;
  redirectPage?: string;
  returnTo?: 'current';
  /**
   * See {@link SdkTwoFactorMode}. With `'inline'`, a link that needs two-factor returns
   * to this page, path parameters and query string included, to show the challenge.
   */
  twoFactor?: SdkTwoFactorMode;
};

/**
 * Request a password reset link for {@link SdkAuth.sendPasswordResetLink}. `resourceId`
 * defaults to the workspace's sole authenticatable resource, as on
 * {@link SdkAuthLoginInput}. There is no `redirectPage` — the emailed link always lands
 * on the reset page, which returns to the login page once the password is changed.
 */
export type SdkAuthPasswordResetInput = {
  identifier: string;
  resourceId?: string;
  /** With `'current'`, the visitor returns to this page after the reset signs them in. */
  returnTo?: 'current';
  /**
   * See {@link SdkTwoFactorMode}. With `'inline'`, a reset that needs two-factor returns
   * to this page to show the challenge.
   */
  twoFactor?: SdkTwoFactorMode;
};

/**
 * The `Sdk.auth` namespace: sign the current visitor in or out via the existing Dynamic
 * Auth flow. `login` wraps `POST /auth/resource/{resourceId}/verify/password`,
 * `sendMagicLink` wraps `POST /auth/resource/{resourceId}/magic-link`, and `logout` wraps
 * `POST /logout`, all through {@link SdkRouter} so CSRF, session regeneration, and rate
 * limiting are handled server-side. On a successful login the server responds with an
 * Inertia-compatible redirect: same-origin workspace destinations use a standard 302, so
 * the router follows them without a full document reload and `onSuccess` callbacks run;
 * cross-origin destinations still fall back to a full-page `X-Inertia-Location` redirect.
 * Steer password and Magic Link destinations with `redirectPage`. Both input types also
 * accept `returnTo: 'current'`; the served page binds that option to its validated
 * same-origin referring URL.
 *
 * @remarks
 * When the resource enables two-factor, pass `twoFactor: 'inline'` to finish the
 * second factor on this page, and render `Sdk.Components.TwoFactorChallenge` and
 * `Sdk.Components.TwoFactorSetup` for {@link SdkAuth.twoFactor}. `Sdk.Components.LoginForm`
 * does both. A signed-in member changes the MFA number with `Sdk.Components.TwoFactorSetup`
 * and manages backup codes with `Sdk.Components.TwoFactorBackupCodes`. An administrator
 * sets or removes another member's MFA number through the Data API with `Sdk.api`:
 * `PUT /api/data/resources/{resource}/records/{record}/two-factor/phone` with `{ phone }`,
 * and `DELETE /api/data/resources/{resource}/records/{record}/two-factor`. Both need
 * update access to the record and refuse the caller's own record.
 */
export type SdkAuth = {
  /**
   * The default authenticatable resource id used when {@link SdkAuth.login} is called
   * without a `resourceId`. `null` when the workspace has zero or multiple authenticatable
   * resources — pass an explicit `resourceId` in that case.
   */
  resourceId: string | null;

  /** Every authenticatable resource in the workspace — see {@link SdkAuthResource}. */
  resources: SdkAuthResource[];

  /**
   * The pending two-factor sign-in of this browser, or `null`. It is set after an
   * `'inline'` sign-in whose resource enables two-factor; render the challenge or the
   * setup for it. Always `null` in previews.
   */
  twoFactor: SdkTwoFactorPending | null;

  /**
   * Sign in with an identifier (email) and password. Resolves the target resource from
   * `credentials.resourceId`, falling back to {@link SdkAuth.resourceId}; throws if neither
   * is set. Pass `options` (`onError`, `onSuccess`, `preserveState`, `preserveScroll`, …)
   * straight through to the router — read validation errors (bad credentials, rate limiting)
   * from `onError`. A successful login to a same-origin workspace page follows the 302
   * redirect as an Inertia visit, so `onSuccess` runs and `preserveState` can keep
   * surrounding form data intact; cross-origin destinations still trigger a full-page
   * `X-Inertia-Location` redirect, so `onSuccess` does not run. Control the destination
   * with `credentials.redirectPage` or use `credentials.returnTo: 'current'` for the
   * exact served page URL.
   */
  login(credentials: SdkAuthLoginInput, options?: SdkRouterVisitOptions): void;

  /**
   * Email a one-time sign-in link to `identifier`, for resources that enabled magic link
   * login (see {@link SdkAuthResource.magicLinkEnabled} — the endpoint 404s otherwise).
   * The response is deliberately identical whether or not an account exists, so treat
   * `onSuccess` as "the request was accepted", never as "an account was found". Unlike
   * `login` this stays on the current page, so `onSuccess` does run. `returnTo: 'current'`
   * also carries that exact served page URL through the emailed link for confirmation in
   * the same tab, another tab, or another browser.
   */
  sendMagicLink(input: SdkAuthMagicLinkInput, options?: SdkRouterVisitOptions): void;

  /**
   * Email a password reset link to `identifier`. Like `sendMagicLink`, the response is
   * the same whether or not an account exists, and the visitor stays on the current
   * page so `onSuccess` runs. `Sdk.Components.LoginForm` already offers this inline.
   */
  sendPasswordResetLink(input: SdkAuthPasswordResetInput, options?: SdkRouterVisitOptions): void;

  /** Sign the current identity out. Logout also clears this origin's local and session storage. */
  logout(options?: SdkRouterVisitOptions): void;
};

/**
 * Server-injected auth config backing {@link SdkAuth}. Threaded from the `sdkAuth`
 * page prop into the SDK builder.
 */
export type SdkAuthConfig = {
  resourceId: string | null;
  resources: SdkAuthResource[];
  /** The stable id of the currently served Page. Draft and authoring previews omit it. */
  currentPageId?: string | null;
  /** The pending two-factor sign-in of this browser. Previews omit it. */
  twoFactor?: SdkTwoFactorPending | null;
};

/** Which two-factor flow a locked send component sends for. */
export type SdkTwoFactorFlow = 'challenge' | 'setup' | 'backupCodes';

/** Options for the two-factor hooks. */
export type SdkUseTwoFactorOptions = {
  /**
   * The authenticatable resource. Defaults to the resource of the pending sign-in, then of
   * the signed-in identity, then the workspace's sole authenticatable resource.
   */
  resourceId?: string;
};

/** The challenge of a pending sign-in, as {@link SdkHooks.useTwoFactorChallenge} reads it. */
export type SdkTwoFactorChallengeState = {
  /**
   * `'consent'`: no code was sent yet; render `Sdk.Components.TwoFactorSmsConsent` with
   * `flow="challenge"`. `'code'`: a code was sent to `maskedPhone`.
   */
  step: 'consent' | 'code';

  /** The last four digits of the MFA number, such as `•••• 0123`. */
  maskedPhone: string;

  /** Seconds until `Sdk.Components.TwoFactorSendCodeButton` can send a new code. */
  resendAvailableIn: number;

  /** A confirmation from the last send, such as "We sent you a new code." */
  status: string | null;

  /** Why the last code could not be sent. A backup code still works. */
  deliveryError: string | null;
};

/** Shared request state of the two-factor hooks. */
export type SdkTwoFactorRequestState = {
  /** The first load is in flight. */
  isLoading: boolean;

  /** The flow did not load. */
  error: SdkApiError | null;

  /** A step is being submitted. */
  isPending: boolean;

  /** The last submitted step failed. Read field messages with `Sdk.utils.error`. */
  actionError: SdkApiError | null;

  /**
   * The pending sign-in ended: it expired, or too many codes were wrong. Call `cancel()`
   * to start again from the email step.
   */
  restartRequired: boolean;
};

/** Returned by {@link SdkHooks.useTwoFactorChallenge}. */
export type SdkTwoFactorChallengeHandle = SdkTwoFactorRequestState & {
  /** Whether a pending sign-in waits for this challenge. Nothing loads otherwise. */
  isActive: boolean;

  data: SdkTwoFactorChallengeState | undefined;

  /**
   * Set when the code passed and the account has no unused backup codes: render
   * `Sdk.Components.TwoFactorBackupCodes` before the visitor continues.
   */
  needsBackupCodes: boolean;

  /** Check a 6-digit SMS code. On success the page navigates to the sign-in destination. */
  verifyCode(code: string): Promise<boolean>;

  /** Check a backup code, such as `ABCDE-12345`. Each code works once. */
  verifyBackupCode(code: string): Promise<boolean>;

  /** End the pending sign-in and return to the email step. */
  cancel(): Promise<void>;
};

/** The step of {@link SdkHooks.useTwoFactorSetup}. */
export type SdkTwoFactorSetupStep = 'authorize' | 'phone' | 'consent' | 'verify' | 'complete';

/** The MFA setup, as {@link SdkHooks.useTwoFactorSetup} reads it. */
export type SdkTwoFactorSetupState = {
  /**
   * `'authorize'`: a replacement first proves the current number or a backup code.
   * `'phone'`: enter the new MFA number. `'consent'`: render
   * `Sdk.Components.TwoFactorSmsConsent` with `flow="setup"`. `'verify'`: enter the code
   * sent to the new number. `'complete'`: the number is verified; call `finish()`.
   */
  step: SdkTwoFactorSetupStep;

  /** Whether this changes an existing MFA number of the signed-in member. */
  isReplacement: boolean;

  /** The last four digits of the number this step texts, or `null` before one is entered. */
  maskedPhone: string | null;

  /** A confirmation from the last send. */
  status: string | null;
};

/** Returned by {@link SdkHooks.useTwoFactorSetup}. */
export type SdkTwoFactorSetupHandle = SdkTwoFactorRequestState & {
  /**
   * Whether a setup applies: a pending sign-in in the setup stage, or a signed-in member
   * of a resource that enables two-factor. Nothing loads otherwise.
   */
  isActive: boolean;

  data: SdkTwoFactorSetupState | undefined;

  /**
   * The new backup codes, once, after the new number is verified. Show them with
   * `Sdk.Components.TwoFactorBackupCodeList`. Never store them.
   */
  codes: string[] | null;

  /** Prove the current factor before a replacement, with an SMS code or a backup code. */
  verifyExistingFactor(factor: { code: string } | { recoveryCode: string }): Promise<boolean>;

  /** Set the proposed MFA number, with its country code. */
  submitPhone(phone: string): Promise<boolean>;

  /** Check the 6-digit code sent to the proposed number. */
  verifyNewNumber(code: string): Promise<boolean>;

  /**
   * Finish after `'complete'`. A pending sign-in navigates to its destination; a
   * replacement resolves `true` and the page stays.
   */
  finish(): Promise<boolean>;

  /** Stop the setup. A pending sign-in also ends. */
  cancel(): Promise<void>;
};

/** The backup codes of the signed-in member, as {@link SdkHooks.useTwoFactorBackupCodes} reads them. */
export type SdkTwoFactorBackupCodesState = {
  /** How many unused backup codes remain. */
  remainingCount: number;

  /**
   * Whether the first set can be generated without a code, right after a sign-in that
   * passed the second factor.
   */
  setupAvailable: boolean;

  /** The last four digits of the MFA number. */
  maskedPhone: string;

  /** Whether proving the factor by SMS needs consent first. */
  smsConsentRequired: boolean;

  /** A confirmation from the last send. */
  status: string | null;
};

/** Returned by {@link SdkHooks.useTwoFactorBackupCodes}. */
export type SdkTwoFactorBackupCodesHandle = SdkTwoFactorRequestState & {
  /** Whether the signed-in member's resource enables two-factor. Nothing loads otherwise. */
  isActive: boolean;

  data: SdkTwoFactorBackupCodesState | undefined;

  /**
   * The new codes, once, after `generate()`. Show them with
   * `Sdk.Components.TwoFactorBackupCodeList`. Never store them.
   */
  codes: string[] | null;

  /**
   * Generate a new set. Every earlier code stops working. Pass a fresh SMS code or an
   * unused backup code; pass nothing only when `setupAvailable` is true.
   */
  generate(factor?: { code: string } | { recoveryCode: string }): Promise<boolean>;

  /** Leave after the first set: go on to the sign-in destination. */
  continue(): void;
};

export type SdkUseForm = <TForm extends FormDataType<TForm>>(
  defaults: TForm
) => InertiaFormProps<TForm>;

export type WorkflowRouteSettings = CoreWorkflowRouteSettings;

export type PageRouteSettings = CorePageRouteSettings;

export type SdkWorkspaceFields = TaskWorkspaceFields;

export type SdkContentValue = {
  type?: string;
  text?: string;
  attrs?: Record<string, any> | null;
  marks?: any[] | null;
  content?: SdkContentValue[] | null;
};

export type SdkMoney = TaskMoney;
export type SdkMoneyInput = TaskMoneyInput;

export type SdkPointValue = { x: number; y: number };

export type SdkRecurrenceDefinition = {
  rule: string;
  exceptions: string[];
  /**
   * On a series occurrence, read only: the date a pending series change takes effect. The
   * `rule` of an occurrence is read from its series, and sending it back unchanged changes
   * nothing.
   */
  changes_on?: string | null;
};

export type SdkRecurrenceValue = SdkRecurrenceDefinition | string;

export type SdkEventScheduleValue = {
  start: string;
  end: string;
  all_day: boolean;
  recurrence: SdkRecurrenceDefinition | null;
};

export type SdkRegularHoursDay =
  | 'monday'
  | 'tuesday'
  | 'wednesday'
  | 'thursday'
  | 'friday'
  | 'saturday'
  | 'sunday';

/**
 * Usual weekly hours, read in the record's own time zone. Each period applies from its `from`
 * date (`YYYY-MM-DD`) until the next period starts. At most one period has `from: null`; it
 * applies before every dated period. A date before the earliest dated period has no hours when
 * no period has `from: null`. A rule's `start` and `end` are `HH:mm`. An end before the start
 * runs past midnight, and an equal start and end is the whole day. Rules can overlap.
 */
export type SdkRegularHoursValue = {
  periods: {
    from: string | null;
    rules: { days: SdkRegularHoursDay[]; start: string; end: string }[];
  }[];
};

export type SdkLocalStoragePrimitive = boolean | null | number | string;

export interface SdkLocalStorageObject {
  readonly [key: string]: SdkLocalStorageValue;
}

export interface SdkLocalStorageArray extends ReadonlyArray<SdkLocalStorageValue> {}

/** A recursively JSON-safe value: null, boolean, finite number, string, array, or plain object. */
export type SdkLocalStorageValue =
  | SdkLocalStoragePrimitive
  | SdkLocalStorageObject
  | SdkLocalStorageArray;

export type SdkLocalStorageSetter<TValue> = (
  value: TValue | null | ((current: TValue | null) => TValue | null)
) => void;

export type SdkLocalStorageResult<TValue> = readonly [
  value: TValue | null,
  setter: SdkLocalStorageSetter<TValue>
];

export type SdkMoneyFormatOptions = TaskMoneyFormatOptions;

export type SdkDateAddUnit = TaskDateAddUnit;
export type SdkDateBoundaryUnit = TaskDateBoundaryUnit;

export type SdkTimeAddUnit = TaskTimeAddUnit;
export type SdkTimeBoundaryUnit = TaskTimeBoundaryUnit;

export type SdkWorkflowTarget = TaskWorkflowTarget<WorkflowReference>;

export type SdkBrandLogoOptions = TaskBrandLogoOptions;

export type SdkClassValue = TaskClassValue;

export type SdkNavigateOptions = TaskNavigateOptions;

/**
 * @deprecated Input for the deprecated `Sdk.utils.tiptap.parse`.
 * @aiHidden
 */
export type SdkTiptapParseInput = TaskTiptapParseInput;

export type SdkTextGenerateOptions = TaskTextGenerateOptions;

export type SdkLocationValue = TaskLocationValue;

export type SdkLocationFormatOptions = TaskLocationFormatOptions;

export type SdkPhoneFormatOptions = TaskPhoneFormatOptions;

export type SdkNumberFormatOptions = TaskNumberFormatOptions;

export type SdkConfettiOptions = TaskConfettiOptions;

export type SdkClipboardCopyOptions = TaskClipboardCopyOptions;

export type SdkNotifyToastOptions = TaskNotifyToastOptions;

export type SdkNotifyVariant = TaskNotifyVariant;

export type SdkBadgeVariant = 'default' | 'secondary' | 'success' | 'warning' | 'destructive';

export type SdkDisplayFormatOptions = {
  /** Forwarded to the existing Page SDK phone formatter. Defaults to `national`. */
  phoneFormat?: 'national' | 'international' | 'e164' | 'rfc3966';
  /** Treat a numeric field value as a duration measured in this unit. */
  durationUnit?: 'milliseconds' | 'seconds' | 'minutes' | 'hours';
};

export type SdkFieldSelector<TKey extends string = string> = TKey | { readonly key: TKey };

export type SdkRecordInput<TRecord extends ResourceRecord = ResourceRecord> =
  | TRecord
  | { fields?: TRecord | null }
  | null
  | undefined;

export type SdkFieldValue<TRecord extends ResourceRecord, TSelector extends SdkFieldSelector> =
  TSelector extends SdkFieldSelector<infer TKey>
    ? TKey extends keyof TRecord
      ? TRecord[TKey]
      : unknown
    : unknown;

export type SdkDisplayUtils = {
  /**
   * Format a resource field value for plain-text display. The field handle supplies
   * its type, configured date/time pattern, select options, and boolean labels.
   * Date, time, datetime, money, phone, location, select, number, boolean, content,
   * markdown, and scalar text values are supported; pass `durationUnit` to format a
   * numeric duration. A content value formats to its plain text, with one line for each
   * paragraph. A markdown value formats to its plain text with the syntax removed: one
   * line for each block, link text kept, and link URLs and images dropped. Existing SDK
   * date, time, money, number, phone, and location formatters remain the source of truth.
   *
   * @remarks
   * Configured date/time display patterns are forwarded to those formatters. Their
   * current locale and timezone behavior is preserved; use the dedicated formatter
   * utilities directly when you need their lower-level options. Nullish or malformed
   * values return `null` and report the standard SDK diagnostic.
   *
   * @example
   * const startsAt = Sdk.utils.display.format(events.fields.starts_at, event.starts_at);
   * const summary = Sdk.utils.display.format(events.fields.description, event.description);
   * const duration = Sdk.utils.display.format(events.fields.duration_minutes, event.duration_minutes, {
   *   durationUnit: 'minutes'
   * });
   */
  format: (
    field: SdkResourceField | null | undefined,
    value: unknown,
    options?: SdkDisplayFormatOptions
  ) => string | null;

  /**
   * Build initials from the first `count` whitespace-delimited name parts.
   * `count` defaults to `2`. Nullish/blank names or an invalid count return `''`
   * and report the standard SDK diagnostic.
   *
   * @example
   * const fallback = Sdk.utils.display.initials('Ada Lovelace'); // 'AL'
   */
  initials: (name: string | null | undefined, count?: number) => string;

  /**
   * Map a select option's configured color to an `Sdk.Components.Badge` variant.
   * `green`/`emerald` → `success`, `yellow`/`orange` → `warning`, `red` →
   * `destructive`, `blue`/`pink`/`purple` → `default`, and missing, custom,
   * `gray`, or `slate` colors → `secondary`.
   *
   * @example
   * const status = invoices.fields.status;
   * <Sdk.Components.Badge variant={Sdk.utils.display.badgeVariant(status, invoice.status)}>
   *   {Sdk.utils.display.format(status, invoice.status)}
   * </Sdk.Components.Badge>
   */
  badgeVariant: (field: SdkResourceField | null | undefined, value: unknown) => SdkBadgeVariant;

  /**
   * Read a field from a flat SDK record or a legacy/external `{ fields: record }`
   * shape. Prefer the nested value when both shapes contain the key. Missing,
   * nullish, or malformed inputs return `null`; pass a field handle to retain the
   * workspace-generated record value type.
   *
   * @example
   * const name = Sdk.utils.display.fieldValue(student, students.fields.name);
   */
  fieldValue: <TRecord extends ResourceRecord, TSelector extends SdkFieldSelector>(
    record: SdkRecordInput<TRecord>,
    field: TSelector
  ) => SdkFieldValue<TRecord, TSelector> | null;
};

export type SdkApiErrorBody = TaskApiErrorBody;

// Workspace field values are read through `Sdk.workspace.values`, so the page
// SDK intentionally omits the shared `utils.workspace` accessor.
export type SdkUtils = Omit<TaskUtils, 'workspace' | 'workflow' | 'tiptap'> & {
  /**
   * Render TipTap JSON to plain text, or to sanitized HTML with `html: true`.
   *
   * @deprecated Render a content field with `Sdk.Components.FormFieldDisplay` or
   * `Sdk.Components.RecordFields`. Read its plain text with `Sdk.utils.display.format`.
   * @aiHidden Kept on the runtime SDK for existing pages, but omitted from the generated
   * SDK reference so agents stop using it.
   *
   * @example
   * const text = Sdk.utils.tiptap.parse({ content: record.notes });
   */
  tiptap: TaskUtils['tiptap'];
  /**
   * Format a count with the matching English singular or plural word.
   *
   * @example
   * Sdk.utils.pluralize(1, 'item', 'items'); // '1 item'
   * Sdk.utils.pluralize(2, 'item', 'items'); // '2 items'
   */
  pluralize: (count: number, one: string, other: string) => string;
  /**
   * Build URLs for published workflows by stable slug or ID.
   *
   * @example
   * const action = Sdk.utils.workflow.url('archive-student', { id: student.id });
   * if (action) await Sdk.api.post(action);
   */
  workflow: {
    /**
     * Build a workflow URL. Pass the target by stable slug or ID, or as
     * `{ id, params }`.
     *
     * @returns The published URL with route params interpolated, or `null` when
     * the target cannot be resolved.
     * @remarks Prefer the workflow slug from the workspace workflow catalog. The
     * workflow ID remains valid. Null/undefined targets, unpublished workflows,
     * and missing route params return `null` and warn in development/editor mode.
     */
    url: (target: SdkWorkflowTarget, params?: Record<string, unknown>) => string | null;
  };
  /**
   * Field-aware plain-text display helpers for formatting values, deriving avatar
   * initials and select badge variants, and reading flat or legacy nested records.
   *
   * @remarks
   * `format` delegates date, time, money, number, phone, and location rendering to the
   * existing SDK formatters so their current locale and timezone behavior remains consistent.
   * Nullish or malformed values return `null` with the standard SDK diagnostic;
   * `fieldValue` returns `null`.
   *
   * @example
   * const status = Sdk.utils.display.format(tasks.fields.status, task.status);
   */
  display: SdkDisplayUtils;
};

export interface PageInputCatalog {}

export interface PageRouteParamCatalog {}

export interface PageRouteBindingCatalog {}

type PageContextKey<TCatalog, TKey extends string> = keyof TCatalog extends never
  ? TKey
  : TKey extends Extract<keyof TCatalog, string>
    ? TKey
    : string extends TKey
      ? TKey
      : never;

type PageContextRequiredKey<TCatalog, TKey extends string> = keyof TCatalog extends never
  ? TKey
  : string extends TKey
    ? TKey
    : TKey extends Extract<keyof TCatalog, string>
      ? {} extends Pick<TCatalog, Extract<TKey, keyof TCatalog>>
        ? never
        : TKey
      : never;

type PageContextValue<TCatalog, TKey extends string> = keyof TCatalog extends never
  ? any
  : TKey extends keyof TCatalog
    ? TCatalog[TKey]
    : any;

type PageContextPresentValue<TCatalog, TKey extends string> = Exclude<
  PageContextValue<TCatalog, TKey>,
  null | undefined
>;

/**
 * Page input + route parameter readers, exposed as `Sdk.helpers` — see
 * {@link SdkContract.helpers}.
 */
export type SdkHelpers = {
  /**
   * Read a value from the page's input data by key. Dot paths reach nested
   * values (`'record.id'`). Returns `defaultValue` (default `null`) when the
   * key is absent. Optional page inputs require an explicit, type-compatible
   * default value.
   */
  getInput<TKey extends string>(
    key: PageContextRequiredKey<PageInputCatalog, TKey>
  ): PageContextValue<PageInputCatalog, TKey>;
  getInput<TKey extends string>(
    key: PageContextKey<PageInputCatalog, TKey>,
    defaultValue: PageContextPresentValue<PageInputCatalog, TKey>
  ): PageContextPresentValue<PageInputCatalog, TKey>;

  /**
   * Alias of {@link SdkHelpers.getInput} for reads inside the rendered
   * component. The page re-renders with fresh input data, so component reads
   * stay current.
   */
  useInput<TKey extends string>(
    key: PageContextRequiredKey<PageInputCatalog, TKey>
  ): PageContextValue<PageInputCatalog, TKey>;
  useInput<TKey extends string>(
    key: PageContextKey<PageInputCatalog, TKey>,
    defaultValue: PageContextPresentValue<PageInputCatalog, TKey>
  ): PageContextPresentValue<PageInputCatalog, TKey>;

  /**
   * Every raw route parameter from the current request URL, keyed by param
   * name. Always an object — `{}` when the page's route has no params.
   */
  getRouteParams: () => keyof PageRouteParamCatalog extends never
    ? Record<string, any>
    : PageRouteParamCatalog;

  /**
   * A single raw route parameter value — e.g. the `{member}` URL segment as a
   * string. Available even when the param has no route binding configured.
   */
  getRouteParam<TKey extends string>(
    key: PageContextKey<PageRouteParamCatalog, TKey>,
    defaultValue?: unknown
  ): PageContextValue<PageRouteParamCatalog, TKey>;

  /**
   * The hydrated record bound to a route param via a route binding, or
   * `undefined` when that param has no binding configured. This is the
   * resolved resource that {@link SdkHelpers.getRouteParam} only exposes the
   * raw value of.
   */
  getRouteBinding<TKey extends string>(
    key: PageContextKey<PageRouteBindingCatalog, TKey>,
    defaultValue?: unknown
  ): PageContextValue<PageRouteBindingCatalog, TKey>;
};

export type SdkContract = {
  /** Namespace of built-in Diagonal SDK components (`Sdk.Components.Button`, `Card`, …). */
  Components: ComponentRegistry;

  /**
   * Drag-and-drop primitives and high-level sortable collections. Use `SortableList` for one
   * ordered collection and `SortableGroups` for movement within and between groups. For
   * sortable resource records, pass the query's records, resource handle, and stored Number
   * field. The high-level components own the
   * sensors, keyboard controls, drag handles, item transforms, changed-index calculation,
   * optimistic update, persistence state, and rollback. For resource records, pass the
   * resource handle and stored Number field; the component sorts nulls last, breaks ties
   * by record id, and bulk-updates only records whose positions changed. No resource
   * capability or dedicated reorder endpoint is required.
   *
   * `reorder(items, activeId, overId)` is the same pure move calculation without UI. It
   * returns both the complete `items` array and only the `changes` whose indices moved.
   * Positions are zero-based. Every other member exposes the underlying dnd-kit primitive
   * for custom drag-and-drop experiences outside sortable resource records.
   *
   * @example
   * ```tsx
   * const students = Sdk.resource('students');
   *
   * return function StudentRanking() {
   *   const { data } = Sdk.hooks.useRecords(students, { sort: 'rank', perPage: 100 });
   *
   *   return (
   *     <Sdk.Dnd.SortableList
   *       items={data}
   *       resource={students}
   *       sortField={students.fields.rank}
   *       renderItem={(student) => <span>{student.name}</span>}
   *     />
   *   );
   * }
   * ```
   */
  Dnd: SdkDnd;

  /**
   * Namespace of reactive data hooks for analytics, API requests, URL query parameters,
   * reading and writing records, building tables and calendars, and reading app chrome
   * state (sidebar, theme). See {@link SdkHooks} for the full interface.
   *
   * **Convention:** hooks belong here, not at the top level of `SdkContract`.
   * `useForm` and the deprecated top-level calendar aliases remain for backwards compatibility.
   *
   * @example
   * ```tsx
   * const students = Sdk.resource('...');
   * const { data } = Sdk.hooks.useRecord(students, recordId);
   * ```
   *
   * @example
   * ```tsx
   * function PaidInvoicesTile() {
   *   const invoices = Sdk.resource('...');
   *   const { data, isLoading, isError } = Sdk.hooks.useAnalytics(invoices, 'kpi', {
   *     metrics: [{ name: 'paid_count', operation: 'count' }]
   *   });
   *
   *   if (isLoading) return <Sdk.Components.Skeleton />;
   *   if (isError) return <Sdk.Components.Alert>Could not load invoices.</Sdk.Components.Alert>;
   *   return <Sdk.Components.Card>{Sdk.utils.number.format(data?.paid_count ?? 0, { decimals: 0 })} paid</Sdk.Components.Card>;
   * }
   * ```
   *
   * @example
   * ```tsx
   * const [params, setParams] = Sdk.hooks.useQueryParams({
   *   search: { default: '' },
   *   page: { type: 'number', default: 1 },
   *   active: { type: 'boolean', default: true },
   *   tags: { type: 'array', default: [] }
   * });
   * ```
   */
  hooks: SdkHooks;

  /**
   * Namespace of page input + route parameter readers — `getInput`, `useInput`,
   * `getRouteParams`, `getRouteParam`, and `getRouteBinding`. See {@link SdkHelpers}.
   *
   * @remarks
   * `getInput(key, defaultValue?)` reads a value from the page's input data
   * (workflow-provided values, route params, and route-binding records), supporting dot
   * paths such as `'record.id'`; `useInput` is its alias for reads inside the rendered
   * component. For a page routed at `/members/{member}`, `getRouteParam('member')`
   * returns the raw URL segment, `getRouteParams()` every raw param keyed by name, and
   * `getRouteBinding('member')` the hydrated record when a route binding is configured
   * (`undefined` otherwise). Raw params also remain readable via
   * `getInput('params.member')`, but prefer the dedicated route helpers. Never derive
   * route segments from `window.location` — the editor preview serves drafts at
   * `/preview/pages/{id}` with params in the query string, so pathname parsing renders the
   * page's not-found/empty state in preview even though the params were delivered. `params`
   * is a reserved key in page output; a page output variable named `params` takes precedence.
   * In the page editor and save-time gate, literal input/param/binding keys and bound-record
   * fields are inferred from the selected page. Runtime-computed string keys retain the
   * permissive dynamic fallback.
   *
   * @example
   * ```tsx
   * return function Page() {
   *   const recordId = Sdk.helpers.useInput('record_id');
   *   const member = Sdk.helpers.getRouteBinding('member');
   *
   *   return <p>{member ? `Editing ${member.name}` : `Record ${recordId}`}</p>;
   * };
   * ```
   */
  helpers: SdkHelpers;

  /** Namespace of workspace-authored shared components (`Sdk.Custom.StatusBadge`, …). */
  Custom: CustomComponentRegistry;

  /**
   * Look up a workspace resource by its portable slug or UUID and return a
   * {@link SdkResource} bundling metadata (`fields`, `display_field_key`, …) with the
   * record operations `list`, `search`, bounded `searchAll`, `count`, `get`, `create`,
   * `update`, `updateMany`, `delete`, and `restore` (plus `invalidate` to refresh cached record data),
   * the record API endpoint URLs under `urls` (`index`, `search`, `count`, `store`,
   * `bulk`, `show`, `update`, `delete`, `restore`) — see {@link ResourceUrls} — and aggregate
   * analytics under `analytics` (`kpi`, `timeSeries`, `leaderboard`, `distribution`) —
   * see {@link SdkResourceAnalytics}.
   * Use `can(operation)` to check the resource's server-resolved policy scope before
   * rendering controls for those operations.
   *
   * @remarks
   * `fields` is a keyed object (`resource.fields.<key>`), not an array — `.map()`/`.filter()`
   * on it fails to compile, so name each field key you use.
   * Prefer the resource slug shown in the workspace SDK catalog (for example, `students`).
   * Slugs make page source portable across workspaces and packages with matching resource
   * names. UUIDs remain supported as a fallback. Display names are not accepted. Throws if
   * the slug or UUID is unknown; the error lists the available slugs and ids.
   * The SDK renames `perPage` → `per_page`, `sort` → `$sort` (accepting `'name'`,
   * `'-created_at'`, `['a', '-b']`, or `{ created_at: -1 }`), and `filter` → `$filter`
   * before calling the records API. The rename does NOT restructure `filter`: pass a
   * structured filter tree (see {@link ResourceSearchBody}) — flat maps like
   * `{ status: 'paid' }` are rejected with a 422. Filtering and sorting are `search`-only;
   * `list` throws if given them, because the list endpoint would ignore them. Use
   * `count` for exact totals without rows and `analytics` for server-side numeric
   * aggregates. `searchAll` is only for a bounded client-side aggregate that needs row
   * values; its required `maxRecords` cap warns when the result is incomplete. Resource
   * ordering is explicit: query the desired sort field and pass that same field plus the
   * resource handle to `Sdk.Dnd.SortableList`. Use the bundled handle methods directly;
   * do not monkey-patch or extend handles with your own helpers.
   * `can(operation)` checks resource-policy access, not permission membership; use
   * {@link SdkContract.permissions} for permission ids and slugs. Both client-side
   * helpers are UI conveniences and never replace server-side enforcement.
   * On public pages, `resource.get(recordId)` rejects with forbidden when the guest
   * lacks record policy access and not-found only when the record is absent. Classify
   * the caught error with `Sdk.utils.error.isForbidden` / `isNotFound`; do not infer
   * not-found from missing data.
   * On a Booking resource, `create` and `update` reject a booking that does not fit with a
   * 422 whose message is under `$capacity`: read it with
   * `Sdk.utils.error.extractApiErrorBody(error)?.errors?.$capacity?.[0]` and show it. A
   * repeating booking whose first date fits resolves, and its `meta.series.waiting` lists each
   * later date that waits for rescheduling, so the page can show "4 dates need rescheduling"
   * with an action for each `record_id`. `meta.booking.replayed` marks a retried create that returned the first booking.
   *
   * @returns A {@link SdkResource} — resource metadata plus the record CRUD methods.
   *
   * @example
   * ```tsx
   * return function Page() {
   *   const students = Sdk.resource('students');
   *   const [rows, setRows] = React.useState([]);
   *
   *   React.useEffect(() => {
   *     students.list({ perPage: 50 }).then((r) => setRows(r.data ?? []));
   *   }, []);
   *
   *   return (
   *     <Sdk.Components.List
   *       items={rows.map((r) => ({ id: r.id, title: r[students.display_field_key] }))}
   *     />
   *   );
   * };
   * ```
   *
   * @example
   * ```ts
   * const result = await Sdk.resource('invoices').create({
   *   amount: 12500,
   *   currency: 'usd',
   *   client_id: '...'
   * });
   * ```
   *
   * @example
   * ```ts
   * // Filtered search — `filter` must be a structured tree
   * // `{ operator: 'and' | 'or', conditions: [...] }` of leaf conditions
   * // (`{ field, operator, value }`) and/or nested groups. Operators are the
   * // symbol or snake_case enum values (`=`, `!=`, `>=`, `contains`, `in`,
   * // `between`, `is_null`, …); flat maps like `{ status: 'paid' }` and word
   * // operators like `eq` are rejected with a 422.
   * const invoices = Sdk.resource('invoices');
   * const paid = await invoices.search({
   *   filter: {
   *     operator: 'and',
   *     conditions: [
   *       { field: 'status', operator: '=', value: 'paid' },
   *       { field: 'total', operator: '>=', value: 100 }
   *     ]
   *   },
   *   sort: '-created_at',
   *   perPage: 25
   * });
   * ```
   *
   * @example
   * ```ts
   * const invoices = Sdk.resource('019286e1-7c0a-7a3e-8f21-3b9d5e6a1c4d');
   * const filter = {
   *   operator: 'and',
   *   conditions: [{ field: 'status', operator: '=', value: 'paid' }]
   * };
   * const paidCount = await invoices.count({ filter });
   * const paidRows = await invoices.searchAll({ filter, maxRecords: 500 });
   * ```
   *
   * @example
   * ```ts
   * // Restore a soft-deleted record.
   * const invoices = Sdk.resource('invoices');
   * await invoices.restore('019286e1-9f2c-7b10-bc44-1a2b3c4d5e6f');
   * ```
   *
   * @example
   * ```ts
   * // Aggregate analytics — headline KPIs plus a monthly time series, without
   * // building a workflow per chart. See {@link SdkResourceAnalytics}.
   * const invoices = Sdk.resource('invoices');
   * const kpis = await invoices.analytics.kpi({
   *   metrics: [{ name: 'revenue', operation: 'sum', field: 'amount' }]
   * });
   * const monthly = await invoices.analytics.timeSeries({
   *   date_field: 'created_at',
   *   granularity: 'month',
   *   metrics: [{ name: 'revenue', operation: 'sum', field: 'amount' }]
   * });
   * ```
   */
  resource(id: string): SdkResource;

  /**
   * A page of this workspace by its stable id. The code editor shows and
   * completes the page's name in this position and stores the id, so a renamed
   * page keeps every reference. Use the handle where a page id or URL is
   * expected.
   *
   * @example
   * const invoices = Sdk.page(INVOICES_PAGE_ID);
   * return <a href={invoices.url() ?? '#'}>Invoices</a>;
   * @example
   * Sdk.page(INVOICE_DETAIL_PAGE_ID).navigate({ invoice: row.id });
   * @example
   * <Sdk.Components.PageLink pageId={Sdk.page(INVOICES_PAGE_ID).id}>Invoices</Sdk.Components.PageLink>
   */
  page(id: PageReference): SdkPageHandle;

  /**
   * Access a workspace resource by id or slug, or `null` when the current viewer cannot
   * access it.
   *
   * @remarks
   * The nullable twin of {@link SdkContract.resource}. Reach for it only when access is
   * genuinely viewer-dependent (a public page that also serves logged-in members) and the
   * code path is imperative. For data hooks, pass the id or slug straight to
   * `Sdk.hooks.useRecords` / `useRecord` / `useInfiniteRecords` instead; they stay idle
   * for viewers without access and need no null check.
   *
   * @example
   * ```ts
   * const contacts = Sdk.resourceOrNull('business-contacts');
   * if (contacts) {
   *   await contacts.create({ status: 'pending' });
   * }
   * ```
   */
  resourceOrNull(id: string): SdkResource | null;

  /**
   * Look up a workspace resource's stable id by its slug.
   *
   * @remarks
   * This lookup is policy-independent and returns identification only. It can identify a
   * resource that the current viewer cannot access. It does not expose resource metadata,
   * fields, capabilities, policies, records, or record operations. Continue to use
   * {@link SdkContract.resource} for policy-checked data access. Static slug references are
   * validated when page code is saved.
   *
   * @example
   * ```tsx
   * const isContact = Sdk.identity?.resource_id === Sdk.resourceId('contacts');
   * return isContact ? <ContactHome /> : <DefaultHome />;
   * ```
   */
  resourceId(resourceSlug: string): string;

  /**
   * List every resource id available to the current user through {@link SdkContract.resource}.
   *
   * @returns The array of resource ids available to the current user.
   *
   * @example
   * ```ts
   * const ids = Sdk.resourceIds();
   * ```
   */
  resourceIds(): string[];

  /**
   * The current workspace record, its field definitions under `fields`, and the
   * load-time value snapshot under `values`.
   *
   * @remarks
   * `workspace.fields` is the definition catalog keyed by stable workspace field key;
   * pass an entry to `Sdk.Components.FormFieldInput` or `Sdk.Components.WorkspaceFieldsForm`.
   * `workspace.values` is a snapshot taken when the page loaded and is deprecated for
   * reads: use `Sdk.hooks.useWorkspaceValues([...keys])`, which updates after a save on
   * this page. `null` when no workspace is resolved, so guard for null/missing
   * (`Sdk.workspace?.fields.support_email`).
   *
   * @example
   * ```tsx
   * const name = Sdk.workspace?.name;
   * const { support_email } = Sdk.hooks.useWorkspaceValues(['support_email']);
   * const supportEmailField = Sdk.workspace?.fields.support_email;
   * ```
   */
  workspace: SdkWorkspace;

  /**
   * Look up a navigation menu by its id and return its metadata
   * plus resolved items.
   *
   * @remarks
   * Throws if the id is unknown — the error lists available ids (also via
   * {@link SdkContract.navigationMenuIds}). Each `items` entry exposes `title`, a resolved
   * `url` (`null` for `dropdown` containers), `type.value`
   * (`'page' | 'url' | 'external_link' | 'dropdown' | 'system_config'`), optional `icon`,
   * `sort_order`, and nested `children` (for `dropdown` items). Prefer
   * `Sdk.Components.Link` / `ButtonLink` for individual nav items and
   * `Sdk.Components.DropdownMenu` for dropdown containers.
   *
   * @returns A {@link SdkNavigationMenu} — `{ id, name, items }`.
   *
   * @example
   * ```tsx
   * const menu = Sdk.navigationMenu(MAIN_NAVIGATION_MENU_ID);
   * return (
   *   <Sdk.Components.List
   *     items={menu.items.map((item) => ({
   *       id: item.id,
   *       title: item.title,
   *       href: item.url ?? '#'
   *     }))}
   *   />
   * );
   * ```
   */
  navigationMenu(id: string): SdkNavigationMenu;

  /**
   * List every available navigation menu id for {@link SdkContract.navigationMenu}.
   *
   * @returns The array of navigation menu ids.
   *
   * @example
   * ```ts
   * const ids = Sdk.navigationMenuIds();
   * ```
   */
  navigationMenuIds(): string[];

  /**
   * Hook wrapping Inertia's `useForm` for plain Inertia
   * form submissions to your own endpoints.
   *
   * @remarks
   * Same return shape as Inertia's `useForm` — `.data`, `.errors`, `.processing`,
   * `.setData(...)`, `.submit(...)`, `.post(...)`, etc. For submitting a Diagonal workflow,
   * use `Sdk.Components.WorkflowForm` or `Sdk.hooks.useWorkflowForm` instead.
   *
   * @returns Inertia `InertiaFormProps<TForm>`.
   *
   * @example
   * ```tsx
   * return function Page() {
   *   const form = Sdk.useForm({ name: '', email: '' });
   *
   *   return (
   *     <form
   *       onSubmit={(e) => {
   *         e.preventDefault();
   *         form.post('/students');
   *       }}
   *     >
   *       <Sdk.Components.FormTextInput
   *         label="Name"
   *         value={form.data.name}
   *         onChange={(v) => form.setData('name', v)}
   *       />
   *       <Sdk.Components.FormTextInput
   *         label="Email"
   *         value={form.data.email}
   *         onChange={(v) => form.setData('email', v)}
   *       />
   *       <Sdk.Components.Button disabled={form.processing} type="submit">
   *         Save
   *       </Sdk.Components.Button>
   *     </form>
   *   );
   * };
   * ```
   */
  useForm: SdkUseForm;

  /**
   * Hook that manages calendar view mode, focal date, derived window, title string, and
   * navigation helpers. Pair with `Sdk.hooks.useCalendarEvents` and `Sdk.Components.Calendar`.
   *
   * @remarks
   * Returns `view`, `setView`, `date`, `setDate`, `windowStart`, `windowEnd`, `title`,
   * `goToToday`, `goToPrevious`, and `goToNext`. Pass `windowStart`/`windowEnd` directly to
   * `Sdk.hooks.useCalendarEvents({ from, to })`. Pass `view` and `date` directly to
   * `<Sdk.Components.Calendar view={view} date={date} />`.
   *
   * Use the pre-built header subcomponents (`CalendarTitle`, `CalendarViewSwitcher`,
   * `CalendarPrevButton`, `CalendarTodayButton`, `CalendarNextButton`) to compose a
   * standard calendar header, or build your own from the raw state values.
   *
   * @example
   * ```tsx
   * function Page() {
   *   const cal = Sdk.hooks.useCalendarView({ initialView: 'month' });
   *   const { events, isLoading } = Sdk.hooks.useCalendarEvents({
   *     resourceId: 'YOUR_RESOURCE_ID',
   *     from: cal.windowStart,
   *     to: cal.windowEnd
   *   });
   *
   *   return (
   *     <div>
   *       <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
   *         <Sdk.Components.CalendarTitle title={cal.title} isLoading={isLoading} />
   *         <div className="flex items-center gap-2">
   *           <Sdk.Components.CalendarViewSwitcher view={cal.view} onViewChange={cal.setView} />
   *           <Sdk.Components.CalendarPrevButton view={cal.view} onClick={cal.goToPrevious} />
   *           <Sdk.Components.CalendarTodayButton onClick={cal.goToToday} />
   *           <Sdk.Components.CalendarNextButton view={cal.view} onClick={cal.goToNext} />
   *         </div>
   *       </div>
   *       <Sdk.Components.Calendar events={events} view={cal.view} date={cal.date} />
   *     </div>
   *   );
   * }
   * ```
   *
   * @deprecated Use `Sdk.hooks.useCalendarView`.
   * @aiHidden
   */
  useCalendarView: SdkHooks['useCalendarView'];

  /**
   * Hook that fetches calendar events for a resource and normalises them to the canonical
   * `CalendarEvent = { id, start: Date, end: Date, allDay: boolean, record }` shape.
   *
   * @remarks
   * The `record` field preserves every raw field value (including loaded relations) so your
   * `renderEvent` callback can read e.g. `event.record.title` or `event.record.status`.
   * Pass `from`/`to` directly from `Sdk.hooks.useCalendarView({ … }).windowStart` / `windowEnd`.
   *
   * @example
   * ```tsx
   * const { events, isLoading } = Sdk.hooks.useCalendarEvents({
   *   resourceId: 'YOUR_RESOURCE_ID',
   *   from: cal.windowStart,
   *   to: cal.windowEnd,
   *   relations: ['assigned_to']
   * });
   * ```
   *
   * @deprecated Use `Sdk.hooks.useCalendarEvents`.
   * @aiHidden
   */
  useCalendarEvents: SdkHooks['useCalendarEvents'];

  /**
   * Hook that manages an optimistic placeholder event for a calendar create flow.
   *
   * @remarks
   * Call `setPlaceholder(payload)` inside your `onSlotClick` handler to immediately
   * reflect the selected slot on the calendar while a create modal is open. Pass
   * `newEvent.mergeEvents(events)` to the `events` prop and `newEvent.placeholderTitle`
   * to the `placeholderTitle` prop on `<Sdk.Components.Calendar>`. Call
   * `clearPlaceholder()` when the modal is cancelled or after the record is saved
   * (and optionally after refetching events).
   *
   * The hook owns only the intermediate placeholder state; persisting the calendar
   * event is handled by the parent page.
   *
   * @example
   * ```tsx
   * function LessonsCalendar() {
   *   const cal = Sdk.hooks.useCalendarView({ initialView: 'week' });
   *   const { events, refetch } = Sdk.hooks.useCalendarEvents({
   *     resourceId: 'LESSONS_RESOURCE_ID',
   *     from: cal.windowStart,
   *     to: cal.windowEnd
   *   });
   *   const newEvent = Sdk.hooks.useNewCalendarEvent({ placeholderTitle: 'New Lesson' });
   *   const [isModalOpen, setIsModalOpen] = React.useState(false);
   *   const [slotPayload, setSlotPayload] = React.useState(null);
   *
   *   function handleSlotClick(payload) {
   *     newEvent.setPlaceholder(payload);
   *     setSlotPayload(payload);
   *     setIsModalOpen(true);
   *   }
   *
   *   function handleModalCancel() {
   *     newEvent.clearPlaceholder();
   *     setIsModalOpen(false);
   *   }
   *
   *   async function handleModalSave(data) {
   *     await Sdk.resource('LESSONS_RESOURCE_ID').create(data);
   *     newEvent.clearPlaceholder();
   *     await refetch();
   *     setIsModalOpen(false);
   *   }
   *
   *   return (
   *     <Sdk.Components.Calendar
   *       events={newEvent.mergeEvents(events)}
   *       placeholderTitle={newEvent.placeholderTitle}
   *       view={cal.view}
   *       date={cal.date}
   *       onSlotClick={handleSlotClick}
   *     />
   *   );
   * }
   * ```
   *
   * @deprecated Use `Sdk.hooks.useNewCalendarEvent`.
   * @aiHidden
   */
  useNewCalendarEvent: SdkHooks['useNewCalendarEvent'];

  /**
   * Inertia router operations using the application's shared request handling
   * (`visit`, `get`, `post`, `put`, `patch`, `delete`, `reload`).
   *
   * @remarks
   * Use for SPA-style navigation and write operations that should trigger Inertia visits.
   * Prefer this over the bare Inertia `router`. Use `reload` to re-fetch the current
   * page's props in place after a mutation.
   *
   * @example
   * ```tsx
   * function SaveButton() {
   *   return (
   *     <Sdk.Components.Button onClick={() => Sdk.router.post('/students', { name: 'Ada' })}>
   *       Save
   *     </Sdk.Components.Button>
   *   );
   * }
   * ```
   *
   * @example
   * ```tsx
   * function RefreshButton() {
   *   return (
   *     <Sdk.Components.Button onClick={() => Sdk.router.reload()}>
   *       Refresh
   *     </Sdk.Components.Button>
   *   );
   * }
   * ```
   */
  router: SdkRouter;

  /**
   * HTTP helper wrapping the application's shared Axios client — see {@link SdkApi}.
   *
   * @remarks
   * Use `Sdk.api` for consistent response parsing and error handling.
   *
   * @remarks
   * For the current logged-in identity, read {@link SdkContract.identity} directly — there is
   * no `/api/me` endpoint to fetch.
   *
   * @remarks
   * To call a Diagonal workflow, do not hand-write its `/api/workflows/...` path. Address the
   * workflow by its stable slug or ID: resolve the URL with `Sdk.utils.workflow.url(reference, params)`,
   * check its documented `null` fallback, and pass the URL here. Alternatively, use
   * `Sdk.Components.WorkflowForm` / `Sdk.hooks.useWorkflowForm` for form submissions. To
   * send the user through a workflow that redirects, assign a resolved non-null URL to
   * `window.location.href`.
   * Id-based references survive route changes and keep page→workflow calls trackable.
   *
   * @example
   * ```tsx
   * const action = Sdk.utils.workflow.url(APPROVE_INVOICE_WORKFLOW_ID);
   * if (action) await Sdk.api.post(action, { invoice_id: 'invoice-id' });
   *
   * await Sdk.api.post('/api/notifications/mark-read', {
   *   ids: [Sdk.identity?.id]
   * });
   * ```
   */
  api: SdkApi;

  /**
   * The current logged-in identity, or `null` for anonymous requests.
   *
   * @remarks
   * The identity's underlying resource record, verbatim — the same plain shape as records
   * read via `Sdk.resource(...)` — plus `$`-prefixed platform-computed keys: `$display_name`
   * (the resource's configured display value) and `$permission_ids` / `$permission_slugs`.
   * Workspace-defined fields such as `name`/`email` are returned as-is and exist only if the
   * auth resource defines them; use `$display_name` when labeling the current user. NOT a
   * workspace user model or bespoke DTO. Secret fields (password, two-factor phone, remember
   * token) are stripped server-side. Read it directly; there is no `/api/me` endpoint to
   * fetch. Always guard for `null` to handle anonymous (unauthenticated) page views.
   * When the workspace has one authenticatable resource, its field catalog is included in
   * editor and save-time checking; multi-resource or otherwise dynamic identity contexts stay
   * permissive.
   *
   * @example
   * ```tsx
   * return function Page() {
   *   const name = Sdk.identity?.$display_name ?? 'Guest';
   *   return <p>Welcome, {name}</p>;
   * };
   * ```
   */
  identity: SdkIdentity;

  /**
   * Sign the current visitor in or out via the existing Dynamic Auth flow — see
   * {@link SdkAuth}. Use this to build fully custom login pages on public code pages;
   * `Sdk.auth.login` posts to the workspace's authenticatable resource and follows the
   * server redirect on success.
   *
   * @example
   * ```tsx
   * function LoginPage() {
   *   const [identifier, setIdentifier] = React.useState('');
   *   const [password, setPassword] = React.useState('');
   *   const [error, setError] = React.useState(null);
   *
   *   return (
   *     <Sdk.Components.Button
   *       onClick={() =>
   *         Sdk.auth.login(
   *           { identifier, password },
   *           { onError: (errors) => setError(errors.identifier ?? errors.password ?? null) }
   *         )
   *       }
   *     >
   *       Sign in
   *     </Sdk.Components.Button>
   *   );
   * }
   * ```
   */
  auth: SdkAuth;

  /**
   * Permission checks for the current identity. Both permission ids and slugs are
   * accepted. Anonymous requests return `false` for every check.
   *
   * @remarks
   * These helpers are for conditionally rendering page UI. Server-side route and
   * policy enforcement remains authoritative. The raw permission arrays remain
   * available on {@link SdkContract.identity}.
   * `Sdk.Components.HasPermission` and `Sdk.Components.HasAnyPermission` gate a
   * section of JSX on the same checks.
   *
   * @example
   * ```tsx
   * if (Sdk.permissions.has('manage-users')) {
   *   return <AdminTools />;
   * }
   *
   * return null;
   * ```
   */
  permissions: SdkPermissions;

  /**
   * Namespace of SDK utilities (`Sdk.utils.money`, `date`, `page`, …); see
   * {@link SdkUtils}.
   *
   * @remarks
   * Null-safety convention: display formatters and value/URL resolvers never
   * throw for nullish or malformed field data. They return their documented
   * empty fallback (`''` or `null`) and report a diagnostic through the app
   * logger, with a console warning in development or the page editor.
   * Computation helpers propagate nullish inputs by returning `null`; genuine
   * programmer errors such as unsupported units or currency mismatches between
   * two real values may still throw with an actionable message. The collection
   * helper `money.sum` retains valid entries but reports any skipped nullish or
   * malformed entries rather than silently lowering a total.
   *
   * @example
   * ```tsx
   * const href = Sdk.utils.page.url(DETAIL_PAGE_ID, { record: row.id });
   * return href ? <a href={href}>View</a> : null;
   * ```
   */
  utils: SdkUtils;
};

/** A plain resource record — the same shape returned by all record API endpoints. */
export type ResourceRecord = Record<string, unknown>;

/**
 * The form handle returned inside `UseCreateResourceRecordFormResult` and by
 * `Sdk.hooks.useUpdateResourceRecordForm`. Provides direct access to form state and actions.
 */
export type SdkResourceForm = {
  /**
   * Current form data keyed by resource field. `$operation` is reserved for transient
   * mutation metadata: it is validated and delivered to record-event workflows, but
   * is never persisted on or returned as part of the record. Treat it as untrusted
   * trigger input in downstream workflows.
   */
  data: Record<string, unknown>;
  errors: Record<string, string>;
  fields: SdkResourceField[];
  isSubmitting: boolean;
  submit: () => void;
  reset: (...keys: string[]) => void;
  /**
   * Set a form value. For recurring eventable updates, use
   * `setData('$operation', { eventable: { scope } })` where `scope` is
   * `this`, `this_and_future`, or `all`.
   */
  setData: (key: string, value: unknown) => void;
};

/**
 * One step of a multi-step record form, passed to the `steps` prop of
 * `Sdk.Components.CreateResourceRecordForm` / `UpdateResourceRecordForm`.
 * Rendering precedence within a step: `content` > `review` > `fields`. A step's
 * `fields` always count toward the form's active field set, even when `content`
 * is provided.
 */
export type ResourceRecordFormStep<RenderState> = {
  /** Step label shown in the step indicator and the "Step X of N" caption. */
  label: string;

  /** Field handles rendered stacked, in order, while this step is active. */
  fields?: SdkResourceField[];

  /**
   * Custom step layout. Receives the form's render state
   * (`{ FieldInput, form, fields }`) and replaces the default field rendering.
   */
  content?: (state: RenderState) => React.ReactNode;

  /** Render a read-only summary of every form field and its current value. */
  review?: boolean;
};

/** Options for `Sdk.hooks.useWorkspaceFieldsForm`. */
export type SdkUseWorkspaceFieldsFormOptions = {
  /** Workspace field definitions to render, in order. Omit to render every field that is
   * not hidden and not read-only. Undefined, read-only, and hidden entries are dropped. */
  fields?: Array<SdkWorkspaceField | undefined>;
  /** Editable seed values that override the workspace's current values. */
  initialValues?: Record<string, unknown>;
  /** Locked values submitted even when omitted from `fields`; rendered disabled. Keys of
   * read-only or hidden definitions are dropped. */
  fixedValues?: Record<string, unknown>;
  /** Called after a successful save with the saved values keyed by field key. */
  onSuccess?: (values: SdkWorkspaceValues) => void;
};

/** The form handle returned by `Sdk.hooks.useWorkspaceFieldsForm`. */
export type SdkWorkspaceFieldsForm = {
  data: Record<string, unknown>;
  errors: Record<string, string>;
  fields: SdkWorkspaceField[];
  isSubmitting: boolean;
  submit: () => void;
  reset: (...keys: string[]) => void;
  setData: (key: string, value: unknown) => void;
};

/** The result of `Sdk.hooks.useWorkspaceFieldsForm`. */
export type UseWorkspaceFieldsFormResult = {
  form: SdkWorkspaceFieldsForm;
  /** The active field definitions, in render order. */
  fields: SdkWorkspaceField[];
  /** Whether a save can happen: the identity holds `update_workspace_fields` or `manage_workspace_fields` and at
   * least one field or fixed value is active. `submit()` is a no-op otherwise. */
  canSubmit: boolean;
  /** Context-wired input for one workspace field. Must be rendered inside `Form`. */
  FieldInput: (props: FormFieldProps) => React.ReactNode;
  /** Form wrapper with `onSubmit` pre-wired to `form.submit`. */
  Form: (props: FormWrapperProps) => React.ReactNode;
};

/** Props accepted by the `Field` component returned from `useCreateResourceRecordForm`. */
export type FormFieldProps = {
  /** A resource field handle (`resource.fields.someField`) or a workspace field
   * definition (`Sdk.workspace.fields.someField`). */
  field: SdkFormField;
  /** Optional wrapper className. */
  className?: string;
};

export type WorkflowInputProps = {
  /** A workflow input handle from the render state's `workflowInputs.<key>` map
   * (or `resource.workflow_inputs.<event>.<key>`). */
  input: SdkWorkflowInput;
  /** Optional wrapper className. */
  className?: string;
};

/** Props accepted by the `WorkflowInputs` component exposed by resource-record form render state. */
export type WorkflowInputsProps = {
  /** Optional wrapper className. */
  className?: string;
};

/** Props accepted by the `Fields` component exposed by resource-record form render state. */
export type ResourceRecordFormFieldsProps = {
  className?: string;
};

/** Props accepted by the `Actions` component exposed by resource-record form render state. */
export type ResourceRecordFormActionsProps = {
  submitLabel?: string;
  defaultSubmitLabel?: string;
  /** When provided, the secondary button becomes a Cancel action wired to this
   * callback (instead of the default Reset). */
  onCancel?: () => void;
  cancelLabel?: string;
  className?: string;
};

/** Props accepted by the `Form` component returned from `useCreateResourceRecordForm`. */
export type FormWrapperProps = {
  children: React.ReactNode;
  className?: string;
};

/**
 * The result returned by `Sdk.hooks.useCreateResourceRecordForm`.
 * Bundles the form handle with context-wired `Field` and `Form` components.
 */
export type UseCreateResourceRecordFormResult = {
  /** The form handle — `data`, `errors`, `fields`, `isSubmitting`, `submit`, `reset`, `setData`. */
  form: SdkResourceForm;
  /**
   * Context-wired field input. Renders the correct editor for a resource field handle;
   * returns `null` for handles that are not part of the active form.
   * Must be rendered inside `Form`.
   */
  FieldInput: (props: FormFieldProps) => React.ReactNode;
  /** Context-wired RecordEvent workflow input. Must be rendered inside `Form`. */
  WorkflowInput: (props: WorkflowInputProps) => React.ReactNode;
  /** Renders every applicable RecordEvent workflow input, stacked in order.
   * Must be rendered inside `Form`. */
  WorkflowInputs: (props: WorkflowInputsProps) => React.ReactNode;
  /** The applicable RecordEvent workflow inputs, keyed by input key. */
  workflowInputs: Record<string, SdkWorkflowInput>;
  /**
   * Form wrapper with `onSubmit` pre-wired to `form.submit`. Provides the context that
   * `FieldInput` reads from. Renders a `<form>` element; accepts `className` and `children`.
   */
  Form: (props: FormWrapperProps) => React.ReactNode;
};

/**
 * The result returned by `Sdk.hooks.useUpdateResourceRecordForm`.
 * Bundles the form handle with context-wired `FieldInput` and `Form` components,
 * plus any pending capability-aware interaction prompt that direct hook consumers
 * must render.
 * `form.isLoading` is `true` while the record is being fetched and seeded.
 */
export type UseUpdateResourceRecordFormResult = {
  /** The form handle — `data`, `errors`, `fields`, `isSubmitting`, `isLoading`, `submit`, `reset`, `setData`. */
  form: SdkResourceForm & { isLoading: boolean };
  /**
   * Context-wired field input. Renders the correct editor for a resource field handle;
   * returns `null` for handles that are not part of the active form.
   * Must be rendered inside `Form`.
   */
  FieldInput: (props: FormFieldProps) => React.ReactNode;
  /** Context-wired RecordEvent workflow input. Must be rendered inside `Form`. */
  WorkflowInput: (props: WorkflowInputProps) => React.ReactNode;
  /** Renders every applicable RecordEvent workflow input, stacked in order.
   * Must be rendered inside `Form`. */
  WorkflowInputs: (props: WorkflowInputsProps) => React.ReactNode;
  /** The applicable RecordEvent workflow inputs, keyed by input key. */
  workflowInputs: Record<string, SdkWorkflowInput>;
  /**
   * Form wrapper with `onSubmit` pre-wired to `form.submit`. Provides the context that
   * `FieldInput` reads from. Renders a `<form>` element; accepts `className` and `children`.
   */
  Form: (props: FormWrapperProps) => React.ReactNode;
  /**
   * Ready-to-render prompt for pending capability-aware interactions. Render this
   * next to `Form`; it is `null` unless `form.submit()` needs extra operation metadata
   * before saving. For recurring eventable updates, this prompts for
   * `$operation.eventable.scope` and then completes the submit.
   */
  interaction: React.ReactNode;
};

/**
 * The form handle returned inside `UseWorkflowFormResult` — the workflow analog of
 * `SdkResourceForm`. `isReady` is `false` until the workflow's published route and
 * method resolve and every route `{param}` has a value (async `params` flip it once
 * they arrive); gate submit buttons on it.
 */
export type SdkWorkflowForm = {
  data: Record<string, unknown>;
  /** Field-validation messages only. HTTP 409 conflicts and every other
   * non-validation failure are exposed through `failure`, never here. */
  errors: Record<string, string>;
  /** The latest non-validation submission failure, or `null`. Read `status` / `body`,
   * or classify it with `Sdk.utils.error.isConflict`, `isForbidden`, or `isNotFound`. */
  failure: SdkApiError | null;
  fields: SdkResourceField[];
  isSubmitting: boolean;
  isReady: boolean;
  submit: () => void;
  /** Reset the requested values and clear `failure`; a full reset also clears field errors. */
  reset: (...keys: string[]) => void;
  setData: (key: string, value: unknown) => void;
  /** Replace or update the error map — pass a complete map or a functional updater.
   * Useful for client-side validation; server 422s populate `errors` automatically. */
  setErrors: (
    errors: Record<string, string> | ((current: Record<string, string>) => Record<string, string>)
  ) => void;
  /** Pass to a `<form>` `onSubmit` when managing the form tag yourself — calls
   * `preventDefault` and submits. The returned `Form` wrapper already wires this. */
  handleSubmit: (event?: React.FormEvent<HTMLFormElement>) => void;
};

/** Response returned when an asynchronous workflow has been accepted for execution. */
export type SdkWorkflowPendingResponse = { execution_id: string };

/** JSON-compatible value returned by a synchronous workflow or custom HTTP response. */
export type SdkWorkflowOutput =
  | string
  | number
  | boolean
  | null
  | unknown[]
  | Record<string, unknown>;

/**
 * Parsed body returned by a published workflow route. Synchronous workflows
 * return their authored output directly, which may be a scalar, list, record, or
 * object. Asynchronous workflows return {@link SdkWorkflowPendingResponse}.
 */
export type SdkWorkflowResponse = SdkWorkflowOutput | SdkWorkflowPendingResponse;

/** State returned by `Sdk.hooks.useWorkflowAction`. */
export type UseWorkflowActionResult = {
  /** Run the workflow with these inputs. Resolves with the parsed response on success
   * and `null` on failure; failures never reject into an unhandled click promise. */
  run: (inputs: Record<string, unknown>) => Promise<SdkWorkflowResponse | null>;
  /** `true` while the latest run is in flight. */
  isPending: boolean;
  /** Display-ready message for the latest failure, or `null`. */
  errorMessage: string | null;
  /** Structured latest failure. Classify it with `Sdk.utils.error`, including
   * `Sdk.utils.error.isConflict(failure)`. */
  failure: SdkApiError | null;
  /** Parsed response from the latest successful run, or `null`. */
  result: SdkWorkflowResponse | null;
  /** Clear submission state, ignore any in-flight response, and start a fresh
   * managed-idempotency lifecycle. */
  reset: () => void;
};

/**
 * The result returned by `Sdk.hooks.useWorkflowForm`.
 * Bundles the form handle with context-wired `FieldInput` and `Form` components,
 * the projected schema fields, the workflow's HTTP method, and availability.
 */
export type UseWorkflowFormResult = {
  /** The form handle — `data`, `errors`, `fields`, `isSubmitting`, `isReady`, `submit`, `reset`, `setData`. */
  form: SdkWorkflowForm;
  /**
   * Context-wired field input. Renders the correct editor for a schema field handle
   * from `fields`; returns `null` for handles that are not part of the active form.
   * Must be rendered inside `Form`.
   */
  FieldInput: React.ComponentType<FormFieldProps>;
  /**
   * Form wrapper with `onSubmit` pre-wired to `form.submit`. Provides the context that
   * `FieldInput` reads from. Renders a `<form>` element; accepts `className` and `children`.
   */
  Form: React.ComponentType<FormWrapperProps>;
  /** The projected schema fields (the same array as `form.fields`). */
  fields: SdkResourceField[];
  /** The workflow's uppercased HTTP method, or an empty string when unavailable. */
  method: string;
  /** `false` when the workflow has no published route settings (unpublished, paused, or unknown id). */
  isAvailable: boolean;
};

/** One offset entry in a record-schedule timeline. */
export type SdkRecordScheduleTimelineOffset = {
  /** Signed ISO-8601 duration relative to the anchor date, e.g. `-P7D` for 7 days before. */
  offset: string;
  /** Absolute ISO datetime of the run, or `null` when the anchor date is unset or hidden. */
  scheduled_run_at: string | null;
  /** Pending, dispatched, automatically skipped, or manually skipped by a caller. */
  state: 'pending' | 'sent' | 'skipped' | 'manually_skipped';
  /** `true` on the soonest offset that is still pending — the upcoming run. */
  is_next: boolean;
};

/** One record-schedule workflow's timeline for a record. */
export type SdkRecordScheduleTimeline = {
  workflow_id: string;
  workflow_title: string;
  /** Label of the workflow's anchor date field, e.g. "Due Date". */
  anchor_date_label: string;
  /** The record's anchor date value, or `null` when unset or hidden from the viewer. */
  anchor_date_value: string | null;
  /** The workflow's offsets in chronological order. */
  offsets: SdkRecordScheduleTimelineOffset[];
};

/** Options accepted by `Sdk.hooks.useRecordScheduleTimelines`. */
export type SdkUseRecordScheduleTimelinesOptions = {
  /** Resource the record belongs to. */
  resourceId: string;
  /** Record whose schedules are fetched. Nullish keeps the hook idle. */
  recordId: string | null | undefined;
  /** Limit the result to a single record-schedule workflow. */
  recordScheduleWorkflowId?: string;
  /**
   * Refetch the timelines on an interval. The function form receives the current
   * timelines and returns `false` to stop. Omit to fetch once per mount.
   *
   * @example
   * ```tsx
   * Sdk.hooks.useRecordScheduleTimelines({
   *   resourceId,
   *   recordId,
   *   pollInterval: (timelines) => (timelines.length ? 60000 : false)
   * });
   * ```
   */
  pollInterval?: SdkPollInterval<SdkRecordScheduleTimeline[]>;
};

/** State returned by `Sdk.hooks.useRecordScheduleTimelines`. */
export type SdkRecordScheduleTimelinesHandle = {
  /** Skip an upcoming offset. Requires Show and Update access to the record. Refreshes all its timeline queries. */
  skipOffset: (input: {
    workflowId: string;
    offset: string;
    /** Reject a stale selection if the reminder has moved since it was displayed. */
    scheduledRunAt?: string;
  }) => Promise<{ data: SdkRecordScheduleTimeline[] }>;
  isSkipping: boolean;
  /** One timeline per record-schedule workflow the record participates in. */
  timelines: SdkRecordScheduleTimeline[];
  isLoading: boolean;
  isError: boolean;
  refetch: () => Promise<unknown>;
};

export type SdkHooks = {
  /**
   * React Query wrapper around a resource analytics aggregation. Choose `kpi`,
   * `timeSeries`, `leaderboard`, or `distribution`; each kind requires the same params
   * and returns the same result as its matching `resource.analytics` method. Returns
   * `{ data, isLoading, isError, invalidate }`. Record mutations refresh analytics
   * across resources because relation-backed filters and aggregates may depend on the
   * changed record.
   *
   * Each metric holds an {@link AnalyticsValue} — a `number`, or `{ amount, currency }`
   * when the aggregated field is money-typed. Render it through
   * `Sdk.utils.number.format` or `Sdk.utils.money.format`; a money object thrown
   * straight into JSX is not a renderable child.
   *
   * `params` is part of the query key. Keep it the same between renders: a date range
   * built from `Sdk.utils.time.current()` or `new Date()` during render changes on every
   * render and makes the hook request without end. See `useRecords`.
   *
   * @example
   * ```tsx
   * function PaidInvoicesTile() {
   *   const invoices = Sdk.resource('...');
   *   const { data, isLoading, isError } = Sdk.hooks.useAnalytics(invoices, 'kpi', {
   *     metrics: [{ name: 'paid_count', operation: 'count' }],
   *     $filter: {
   *       operator: 'and',
   *       conditions: [{ field: 'status', operator: '=', value: 'paid' }]
   *     }
   *   });
   *
   *   if (isLoading) return <Sdk.Components.Skeleton />;
   *   if (isError) return <Sdk.Components.Alert>Could not load invoices.</Sdk.Components.Alert>;
   *
   *   return <Sdk.Components.Card>{Sdk.utils.number.format(data?.paid_count ?? 0, { decimals: 0 })} paid</Sdk.Components.Card>;
   * }
   * ```
   */
  useAnalytics<const TMetrics extends readonly AnalyticsMetric[]>(
    resource: SdkAnalyticsResource<'kpi'>,
    kind: 'kpi',
    params: AnalyticsKpiParams<TMetrics>,
    options?: SdkUseAnalyticsOptions
  ): SdkAnalyticsHandle<AnalyticsKpiResult<TMetrics>>;
  useAnalytics(
    resource: SdkAnalyticsResource<'timeSeries'>,
    kind: 'timeSeries',
    params: AnalyticsTimeSeriesParams,
    options?: SdkUseAnalyticsOptions
  ): SdkAnalyticsHandle<AnalyticsRow[]>;
  useAnalytics(
    resource: SdkAnalyticsResource<'leaderboard'>,
    kind: 'leaderboard',
    params: AnalyticsLeaderboardParams,
    options?: SdkUseAnalyticsOptions
  ): SdkAnalyticsHandle<AnalyticsRow[]>;
  useAnalytics(
    resource: SdkAnalyticsResource<'distribution'>,
    kind: 'distribution',
    params: AnalyticsDistributionParams,
    options?: SdkUseAnalyticsOptions
  ): SdkAnalyticsHandle<AnalyticsRow[]>;

  /**
   * Reactive escape hatch for custom HTTP endpoints. The key controls caching and
   * deduplication; the fetcher receives the same scoped `get`/`post`/`put`/`patch`/`delete`
   * helpers as {@link SdkContract.api}. Returns
   * `{ data, isLoading, isError, error, refreshIn, invalidate }`, with every failure normalized to
   * {@link ApiError}.
   *
   * Keys are isolated from the app's other React Query caches. Begin a key with
   * `'records'` when the endpoint's result depends on resource records, for example
   * `['records', resource.id, 'summary']`; successful resource create/update/delete/restore
   * operations then invalidate it automatically. Other keys refresh only through their
   * returned `invalidate` function.
   *
   * Keep the key the same between renders. A clock read such as `Date.now()` in the key
   * makes a new query on every render, and the hook requests without end. See
   * `useRecords`.
   *
   * With `options.pollInterval`, `refreshIn` counts the whole seconds until the next
   * automatic request, rounded up. It is `0` while a refresh is in flight and `null` when
   * no refresh is scheduled.
   *
   * @example
   * ```tsx
   * function NotificationSummary() {
   *   const summary = Sdk.hooks.useApi(
   *     ['notifications', 'summary'],
   *     (api) => api.get('/api/notifications/summary')
   *   );
   *
   *   if (summary.isLoading) return <Sdk.Components.Skeleton />;
   *   if (summary.error) return <Sdk.Components.Alert>{summary.error.message}</Sdk.Components.Alert>;
   *   return <p>{summary.data?.unread_count ?? 0} unread</p>;
   * }
   * ```
   */
  useApi<TData>(
    key: SdkApiQueryKey,
    fetcher: SdkApiFetcher<TData>,
    options?: SdkUseApiOptions<TData>
  ): SdkApiHandle<TData>;

  /**
   * Preview subscription charges without creating records or collecting payment.
   * All amounts and dates come from the backend. Changing inputs invalidates the
   * displayed quote; responses for older inputs cannot replace the current result.
   * Pass the reviewed quote as `confirmed_quote` to Subscription: Create.
   * Pass the same settings to Subscription: Create as the preview, or the confirmed quote does not match.
   * Discounts apply after proration. First-invoice discounts apply to the first paid
   * invoice after a trial and do not reduce the renewal amount.
   *
   * @example
   * const billing = Sdk.hooks.useSubscriptionPreview({
   *   resourceId: Sdk.resource('subscriptions').id,
   *   items: [{ price_record_id: selectedPriceId, quantity: 1 }],
   *   discounts: [{ calculation_type: 'percentage', percent: 10, duration: 'first_invoice' }]
   * });
   * // On a creation failure, await billing.refetch() and confirm the refreshed terms.
   */
  useSubscriptionPreview(options: SdkSubscriptionPreviewOptions): SdkSubscriptionPreviewHandle;

  /**
   * Preview a change to the items of an existing subscription without applying it.
   * Pass the complete desired selection. All amounts and dates come from the backend.
   * The preview includes discount rows, discount_total, and each credit's gross_amount
   * before source discounts or credits. The credit amount is net of those reductions.
   * Renewal includes discounts on the next invoice. Discount term changes require
   * a new confirmed_quote.
   * An incomplete subscription uses `effective: 'now'` without `revisionScope`. Its
   * `initial_revision` preview revises the first invoice in place. `revision.starts_on_payment`
   * means its term dates move when payment activates the subscription. `revision.activates`
   * means applying the revision activates the subscription. A settled draft first invoice
   * activates it only after staff open the invoice.
   * For initial revisions, `credit_to_balance` is potential excess. Settlement moves only
   * eligible amounts; excess stays on the overpaid invoice when transfer mappings are missing.
   * With `revisionScope`, the preview revises an unpaid renewal invoice: `mode` is
   * `current_revision` or `upcoming_revision`, and `preview.revision` holds the invoice ID,
   * before and after lines and totals, conflicts, and the following renewal. Pass the same
   * selection, `effective`, `revision_scope`, confirmed quote, and a reason to
   * Subscription: Change Items.
   * With `effective: 'period'` and `periodStart`, `preview.schedule` holds the period dates,
   * invoices kept unchanged (`preserved_invoices`), replaced scheduled selections, and the
   * recurring subtotal. Pass the same items, `effective: 'period'`, `period_start`, and confirmed
   * quote to Subscription: Change Items.
   * Changing inputs invalidates the displayed quote; responses for older inputs cannot
   * replace the current result. `preview.changed` is false when the selection matches
   * the current subscription. The endpoint requires a signed-in visitor. `Sdk.resource`
   * throws for a visitor who cannot see the resource, so resolve the ID with
   * `Sdk.resourceOrNull` on a page that signed-out visitors can open.
   *
   * @example
   * const change = Sdk.hooks.useSubscriptionChangePreview({
   *   resourceId: Sdk.resourceOrNull('subscriptions')?.id ?? '',
   *   recordId: subscriptionId,
   *   items: [{ price_record_id: selectedPriceId, quantity: 1 }],
   *   enabled: Sdk.identity !== null
   * });
   * const canConfirm = change.quote !== null && change.preview?.changed === true;
   */
  useSubscriptionChangePreview(
    options: SdkSubscriptionChangePreviewOptions
  ): SdkSubscriptionChangePreviewHandle;

  /**
   * Search free slots for a booking across groups of schedulable records. Each group needs all
   * of its records (`mode: 'all'`) or one of them (`mode: 'any'`). A slot is advice: the booking
   * create decides. Changing an option starts a new search, and the slots of the old search are
   * hidden at once. Show `error` as an error state; an empty `slots` list is not a failure.
   * Render the result with `Sdk.Components.SlotPicker`.
   *
   * @example
   * const search = Sdk.hooks.useAvailableSlots({
   *   resourceId: Sdk.resource('appointments').id,
   *   groups: [{ mode: 'any', records: tutors.map((tutor) => ({ resourceId: tutorsResourceId, id: tutor.id })) }],
   *   from: weekStart,
   *   to: weekEnd,
   *   durationMinutes: 60
   * });
   */
  useAvailableSlots(options: SdkAvailableSlotsOptions): SdkAvailableSlotsHandle;

  /**
   * Read the free time of schedulable records in a window of at most 62 days, with the pieces
   * that made it: regular hours, exceptions, limits, closures, and busy time from staff members'
   * blocking connected calendars. Pass a record's `backgroundTimes` to `Sdk.Components.Calendar`
   * to shade open time and label busy and closed time in the week and day views.
   *
   * @example
   * const availability = Sdk.hooks.useAvailability({
   *   resourceId: Sdk.resource('tutors').id,
   *   ids: [tutorId],
   *   from: cal.windowStart,
   *   to: cal.windowEnd,
   *   labels: { busy: 'Away' }
   * });
   * <Sdk.Components.Calendar
   *   events={[]}
   *   view={cal.view}
   *   date={cal.date}
   *   backgroundTimes={availability.records[0]?.backgroundTimes ?? []}
   * />
   */
  useAvailability(options: SdkAvailabilityOptions): SdkAvailabilityHandle;

  /**
   * Read the held times of a resource's records that closed time conflicts with: bookings outside
   * open time, and events over a closed exception, a closure, or busy time on the calendar owner's
   * blocking connected calendars. Only a signed-in visitor reads conflicts, and only of records
   * whose time fields and relation the visitor can read. Show `error` as an error state.
   *
   * @example
   * const { conflicts } = Sdk.hooks.useScheduleConflicts({
   *   resourceId: Sdk.resource('lessons').id,
   *   bookable: { resourceId: Sdk.resource('tutors').id, ids: [tutorId] },
   *   enabled: Sdk.identity !== null
   * });
   */
  useScheduleConflicts(options: SdkScheduleConflictsOptions): SdkScheduleConflictsHandle;

  /**
   * Preview the refund amount limits of one payment without refunding it.
   * `refundable` is the largest amount that the refund amount checks of Payment: Refund
   * allow, and the amount that Payment: Refund refunds when the amount is omitted.
   * Payment: Refund can still reject a refund for another `payment_refund_rejected`
   * reason. `payment_remaining` is the payment amount less its succeeded refunds.
   * When the invoice supplied unused-service credit to a subscription change,
   * `change_credit` is that credit and `invoice_refundable` is what the invoice can
   * still refund, so `refundable` can be lower than `payment_remaining`. Both are null
   * when no change credit limits the refund, or when the visitor cannot see every
   * payment and succeeded refund on the invoice with its amount. `refundable` still
   * applies the cap. Call `refetch` after a refund.
   * The endpoint requires a signed-in visitor with Show access to the payment.
   *
   * @example
   * const limits = Sdk.hooks.usePaymentRefundPreview({
   *   resourceId: Sdk.resourceOrNull('payments')?.id ?? '',
   *   recordId: paymentId,
   *   enabled: Sdk.identity !== null
   * });
   * const refundable = limits.preview?.refundable;
   * const isFullRefund = refundable?.amount === limits.preview?.payment_remaining.amount;
   * const label = isFullRefund ? 'Refund full payment' : 'Refund available amount';
   * // After the refund request completes, await limits.refetch().
   */
  usePaymentRefundPreview(options: SdkPaymentRefundPreviewOptions): SdkPaymentRefundPreviewHandle;

  /**
   * Read the available payer balance of each accounting entity. The amounts are the
   * amounts that checkout uses when it pays an invoice of that entity from the payer
   * balance. The default entity holds ledger entries with no entity, and any part of the
   * total that no ledger entry explains. Each amount is clamped to zero and to the total,
   * so the amounts need not add up to `total`. The list holds each active entity, and each
   * inactive entity whose amount is above zero, ordered by name. A workspace with no
   * entity returns the total and an empty list.
   * The endpoint requires a signed-in visitor with Show access to the payer and its
   * balance field. Call `refetch` after a ledger entry or a payment.
   *
   * @example
   * const payerBalances = Sdk.hooks.usePayerBalances({
   *   resourceId: Sdk.resourceOrNull('businesses')?.id ?? '',
   *   recordId: businessId,
   *   enabled: Sdk.identity !== null
   * });
   * const rows = payerBalances.balances.map((balance) => ({
   *   name: balance.entity.name,
   *   available: Sdk.utils.money.format(balance.available)
   * }));
   */
  usePayerBalances(options: SdkPayerBalancesOptions): SdkPayerBalances;

  /**
   * The release history of the packages this workspace installed: the installed and the
   * newest version of each, and the notes of each published release, newest first. Every
   * release newer than the installed one is listed, so a skipped upgrade shows each
   * version's notes. The endpoint requires a visitor with the Manage Packages permission.
   *
   * @example
   * const releaseNotes = Sdk.hooks.usePackageReleaseNotes({ limit: 5 });
   * const updates = releaseNotes.packages.filter((item) => item.has_newer_release);
   */
  usePackageReleaseNotes(options?: SdkPackageReleaseNotesOptions): SdkPackageReleaseNotes;

  /**
   * List the accounting entities of the workspace with their `active` and `is_default`
   * flags, ordered by name. Inactive entities are included. A branch returns the copies
   * of its production workspace's entities. A signed-out visitor gets an empty list and
   * no request is made,
   * so a public page reads the entity of a record from `record.__meta?.accounting_entity`
   * instead. ShowRecord and IndexRecords add it to each invoice, subscription, and payer
   * balance entry record whose entity field the visitor can read. ShowRecord (`useRecord`)
   * uses the field visibility of the Show policy, and IndexRecords (`useRecords`) uses the
   * field visibility of the Index policy. A Show or Index policy with a field list must list
   * the entity field, or the name is absent. Add the entity field to each policy scope
   * that the page uses.
   *
   * @example
   * const { entities } = Sdk.hooks.useAccountingEntities();
   * const options = entities
   *   .filter((entity) => entity.active)
   *   .map((entity) => ({ value: entity.id, label: entity.name }));
   * // On a public invoice page:
   * const payee = invoice.__meta?.accounting_entity?.name ?? 'Our organization';
   */
  useAccountingEntities(): SdkAccountingEntities;

  /**
   * Refresh the page's route bindings and inputs on an interval.
   *
   * Each tick reloads only the page's own data. Reach for it when a backend change
   * arrives from outside the page — a webhook, a queued job, another user — and the page
   * must notice it without the visitor pressing anything. Returns `{ start, stop,
   * lastPolledAt }`; polling begins on mount unless `autoStart: false`, and slows down
   * while the tab is hidden. `lastPolledAt` is the time the latest tick finished, or
   * `null` before the first one.
   *
   * This is the route-binding layer. `Sdk.helpers.getRouteBinding(...)` and the other
   * `Sdk.helpers` data only refresh this way, and this is the only refresh mechanism an
   * anonymous visitor on a public page has, because the record API needs authentication.
   * Records fetched with `useRecord`/`useRecords`/`useApi` live in a separate cache that
   * a reload does not touch — poll those with their own `pollInterval` option.
   *
   * Every tick costs a full server-side page render, so keep the interval at 3000 ms or
   * more and call `stop()` as soon as the awaited change arrives. Intervals below 1000 ms
   * are raised to 1000 ms. A hidden tab slows to one tick in ten; it does not stop.
   *
   * The interval is read once on mount, so changing it later has no effect — use `stop()`
   * to end polling. When a tick returns changed data, the page source recompiles.
   * Only the returned page function preserves its local React state. Helper components
   * declared in page source and `Sdk.Custom` components can remount and reset their state.
   * Prefer `pollInterval` on `useRecord`/`useRecords`/`useApi` when a page holds state and
   * the record API is available. State initialized from old data does not automatically
   * track new data, so read route bindings during render.
   *
   * @example
   * ```tsx
   * function TicketList() {
   *   const { stop } = Sdk.hooks.usePoll(3000);
   *   const order = Sdk.helpers.getRouteBinding('event_order');
   *   const tickets = order?.tickets ?? [];
   *
   *   React.useEffect(() => {
   *     if (tickets.length > 0) stop();
   *   }, [tickets.length, stop]);
   *
   *   if (tickets.length === 0) return <Sdk.Components.Skeleton />;
   *   return <ul>{tickets.map((ticket) => <li key={ticket.id}>{ticket.code}</li>)}</ul>;
   * }
   * ```
   */
  usePoll(interval: number, options?: SdkUsePollOptions): SdkPollControls;

  /**
   * Bind page-owned filter, search, and pagination state to the current URL query string.
   * Returns `[params, setParams]`; updates merge the supplied keys into the current URL with
   * `history.replaceState`, so they do not reload the page or trigger an Inertia visit.
   * Pass `null` or `undefined` to remove a key. Browser back/forward and `Sdk.router`
   * navigations update `params` reactively. Without a schema, values are strings (or string
   * arrays for repeated keys). A schema can coerce numbers and booleans, read arrays from
   * repeated query keys, and supply defaults for missing or invalid values. Arrays are written
   * as repeated keys (`tag=a&tag=b`). Editor preview plumbing is hidden and reserved, so page
   * code behaves identically on published routes and in preview.
   *
   * @example
   * ```tsx
   * function StudentList() {
   *   const [params, setParams] = Sdk.hooks.useQueryParams({
   *     search: { default: '' },
   *     page: { type: 'number', default: 1 },
   *     active: { type: 'boolean', default: true },
   *     tags: { type: 'array', default: [] }
   *   });
   *
   *   return (
   *     <Sdk.Components.Button onClick={() => setParams({ page: params.page + 1 })}>
   *       Next page
   *     </Sdk.Components.Button>
   *   );
   * }
   * ```
   */
  useQueryParams(): [QueryParams, SetQueryParams<QueryParams>];
  useQueryParams<const TSchema extends QueryParamSchema>(
    schema: TSchema
  ): [QueryParamsFromSchema<TSchema>, SetQueryParams<QueryParamsFromSchema<TSchema>>];

  /**
   * Reactive wrapper around `resource.urls.show(recordId)` for fetching a single
   * record. Returns `{ data, isLoading, isError, error, refreshIn }`. Pass relation paths through
   * `options.relations`, for example `{ relations: ['family'] }`; known resource handles add only
   * selected relations to `data`, so accessing an omitted relation or unknown field is a
   * save-time type error. Runtime-dynamic resource ids retain the generic record fallback.
   *
   * On a public page, `data === undefined` does not prove that the record is absent:
   * guest policy denial is a 403 and a missing record is a 404. Check `error` with
   * `Sdk.utils.error.isForbidden(error)` / `isNotFound(error)` before choosing the UI state.
   *
   * The resource may be a handle from `Sdk.resource()` or a resource id / slug string. The
   * string form is typed from the workspace catalog and stays idle (`data` is `undefined`,
   * no error) for a viewer who cannot access the resource, so it suits pages that serve
   * both guests and members.
   *
   * With `options.pollInterval`, `refreshIn` counts the whole seconds until the next
   * automatic request, rounded up, so a page can show a countdown. It is `0` while a
   * refresh is in flight, and `null` whenever no refresh is scheduled: before the first
   * request settles, without a `pollInterval`, once a function-form `pollInterval` returns
   * `false`, or while the browser tab is hidden and the poll is paused.
   *
   * @example
   * ```tsx
   * function StudentCard({ recordId }) {
   *   const students = Sdk.resource('...');
   *   const { data: student, isLoading, error } = Sdk.hooks.useRecord(students, recordId, {
   *     relations: ['family']
   *   });
   *
   *   if (isLoading) return <Sdk.Components.Skeleton />;
   *   if (Sdk.utils.error.isForbidden(error)) return <AccessDenied />;
   *   if (Sdk.utils.error.isNotFound(error)) return <NotFound />;
   *   return <div>{student?.name} — {student?.family?.name}</div>;
   * }
   * ```
   *
   * @example
   * ```tsx
   * function OrderStatus({ recordId }) {
   *   const orders = Sdk.resource('...');
   *   const { data: order, refreshIn } = Sdk.hooks.useRecord(orders, recordId, {
   *     relations: ['tickets'],
   *     pollInterval: (record) => (record?.tickets?.length ? false : 3000)
   *   });
   *
   *   return (
   *     <div>
   *       {order?.status}
   *       {refreshIn === null ? null : <span>Refreshing in {refreshIn}s</span>}
   *     </div>
   *   );
   * }
   * ```
   */
  useRecord<
    TReference extends SdkResourceQueryReference<'show'>,
    const TRelations extends readonly SdkReferenceRelationPath<NoInfer<TReference>>[] = readonly []
  >(
    resource: TReference,
    recordId: string | null | undefined,
    options?: SdkUseRecordOptions<TRelations, NoInfer<SdkReferenceRecord<TReference, TRelations>>>
  ): SdkRecordHandle<SdkReferenceRecord<TReference, TRelations>>;

  /**
   * Reactive wrapper around the record search endpoint for fetching a page of records —
   * the collection twin of `useRecord`, and the right tool for any collection UI that
   * isn't a table (card grids, charts, select options, kanban boards, dashboard tiles).
   * Returns `{ data, pagination, isLoading, isError, refreshIn, invalidate }`, where `data` is the
   * current page of records (`[]` until the first fetch resolves). The previous page
   * stays rendered while a new page, filter, or sort loads, and records mutated through
   * `resource.create/update/delete/restore` refresh it automatically.
   *
   * Pass relation paths through `options.relations`; known resource handles add only
   * selected relations to `data`, so accessing an omitted relation or unknown field is
   * a save-time type error. Runtime-dynamic resource ids retain the generic record
   * fallback. `filter` must be a structured tree (see {@link ResourceSearchBody}) —
   * flat maps like `{ status: 'paid' }` are rejected with a 422. Filtering on a nested
   * relation path (`family.id`) does not load that relation: it still comes back as its
   * `{ id }` reference, so list it in `relations` to read any other related field.
   * With `options.pollInterval`, `refreshIn` counts the whole seconds until the next
   * automatic request, rounded up. It is `0` while a refresh is in flight and `null` when
   * no refresh is scheduled.
   *
   * Every option except `enabled` and `pollInterval` is part of the query key, so keep
   * the values the same between renders. Do not read the clock in them:
   * `Sdk.utils.time.current()`, `Date.now()`, and `new Date()` return a new value on every
   * render, so each response builds a new query and the page requests without end. Read
   * the time once with `React.useState(() => Sdk.utils.time.current())` or at the top of
   * the page factory. `Sdk.utils.time.startOf(...)`/`endOf(...)` of the current time stay
   * the same between renders and are safe. Saving a page rejects a clock read that
   * reaches a query hook's arguments.
   *
   * Reach for `useResourceDataTable` instead when the UI really is a table.
   *
   * The resource may be a handle from `Sdk.resource()` or a resource id / slug string.
   * A string reference is typed from the workspace catalog at authoring time and is
   * resolved against the viewer's accessible resources at render time, so a viewer
   * without access keeps the hook idle (`data` stays `[]`) instead of throwing. Prefer
   * the string form on pages that serve both guests and members.
   *
   * @example
   * ```tsx
   * function BusinessContacts({ contactId }: { contactId: string }) {
   *   const { data } = Sdk.hooks.useRecords('business-contacts', {
   *     enabled: Boolean(contactId),
   *     relations: ['business'],
   *     filter: {
   *       operator: 'and',
   *       conditions: [{ field: 'contact.id', operator: '=', value: contactId }]
   *     }
   *   });
   *
   *   return <ul>{data.map((link) => <li key={link.id}>{link.business?.name}</li>)}</ul>;
   * }
   * ```
   *
   * @example
   * ```tsx
   * function StudentCards() {
   *   const students = Sdk.resource('...');
   *   const { data, pagination, isLoading } = Sdk.hooks.useRecords(students, {
   *     relations: ['family'],
   *     filter: {
   *       operator: 'and',
   *       conditions: [{ field: 'status', operator: '=', value: 'active' }]
   *     },
   *     sort: '-created_at',
   *     perPage: 12
   *   });
   *
   *   if (isLoading) return <Sdk.Components.Skeleton />;
   *
   *   return (
   *     <div className="grid grid-cols-3 gap-4">
   *       {data.map((student) => (
   *         <Sdk.Components.Card key={student.id}>
   *           {student.name} — {student.family?.name}
   *         </Sdk.Components.Card>
   *       ))}
   *       <p>{pagination?.total} students</p>
   *     </div>
   *   );
   * }
   * ```
   */
  useRecords<
    TReference extends SdkResourceQueryReference<'search'>,
    const TRelations extends readonly SdkReferenceRelationPath<NoInfer<TReference>>[] = readonly []
  >(
    resource: TReference,
    options?: SdkUseRecordsOptions<TRelations, NoInfer<SdkReferenceRecord<TReference, TRelations>>>
  ): SdkRecordsHandle<SdkReferenceRecord<TReference, TRelations>>;

  /**
   * Saved views for a resource: the views the viewer may use or
   * manage (policy-gated server-side), the current selection,
   * and the live view state a selection hydrates — `filter`, `sorts`, `search`,
   * `perPage`, and `columns`, each `null` (`''` for search, `[]` for sorts) when not
   * configured. `sorts` is the ordered sort, primary first; `sort` is its primary entry.
   * The query values spread straight into `useRecords`. `columns` contains the visible
   * table column identifiers, including custom column IDs, in display order.
   * `useResourceDataTable` applies this layout automatically. Selecting applies the
   * saved state; the setters only dirty the local state until `reset`, `create` (save
   * as a new private view), or `update`.
   *
   * `useResourceDataTable` wires this up automatically — reach for it directly when
   * building a non-table view (lists, card grids, custom layouts). Pair it with
   * `Sdk.Components.SharedFilters` for a ready-made selector dropdown, or build your
   * own from `available` + `select`.
   *
   * @example
   * ```tsx
   * function StudentList() {
   *   const students = Sdk.resource('...');
   *   const sharedFilters = Sdk.hooks.useSharedFilters(students);
   *   const { data } = Sdk.hooks.useRecords(students, {
   *     filter: sharedFilters.filter,
   *     sort: sharedFilters.sorts,
   *     search: sharedFilters.search,
   *     perPage: sharedFilters.perPage
   *   });
   *
   *   return (
   *     <div className="grid gap-2">
   *       <Sdk.Components.SharedFilters sharedFilters={sharedFilters} />
   *       {data.map((student) => (
   *         <Sdk.Components.Card key={student.id}>{student.name}</Sdk.Components.Card>
   *       ))}
   *     </div>
   *   );
   * }
   * ```
   */
  useSharedFilters(
    resource: Pick<SdkResource, 'id' | 'policy_scope'>,
    options?: SdkUseSharedFiltersOptions
  ): SharedFiltersApi;

  /**
   * Infinite-scroll counterpart to `useRecords`. It accepts the same `relations`,
   * `filter`, `sort`, `perPage`, `scope`, and `enabled` options, then returns up to
   * the ten most recently loaded pages flattened in `data` with `fetchNextPage`,
   * `hasNextPage`, and `isFetchingNextPage`. Older pages are discarded so cache memory
   * and mutation-triggered refetch work remain bounded. Mutations through the resource
   * handle refresh the retained pages. Like `useRecords`, it accepts a resource id / slug
   * string typed from the workspace catalog, which stays idle for a viewer without access.
   * Keep the options the same between renders; see `useRecords` for why clock reads are
   * rejected.
   *
   * @example
   * ```tsx
   * const students = Sdk.resource('...');
   * const { data, fetchNextPage, hasNextPage, isFetchingNextPage } =
   *   Sdk.hooks.useInfiniteRecords(students, {
   *     filter: {
   *       operator: 'and',
   *       conditions: [{ field: 'status', operator: '=', value: 'active' }]
   *     },
   *     sort: '-created_at',
   *     perPage: 25
   *   });
   * ```
   */
  useInfiniteRecords<
    TReference extends SdkResourceQueryReference<'search'>,
    const TRelations extends readonly SdkReferenceRelationPath<NoInfer<TReference>>[] = readonly []
  >(
    resource: TReference,
    options?: SdkUseInfiniteRecordsOptions<TRelations>
  ): SdkInfiniteRecordsHandle<SdkReferenceRecord<TReference, TRelations>>;

  /**
   * Reactive `resource.create` for pages that write records without a form hook.
   * Returns `{ create, data, isPending, isError, error, reset }`. `create` resolves
   * with the created record, or `null` when the request failed — it never rejects, so
   * calling it straight from an `onClick` is safe; read `error` for the failure.
   * Cached record data refreshes on success.
   *
   * Prefer `Sdk.Components.CreateResourceRecordForm` when a schema-driven form fits.
   * On a Booking resource, a refusal lands on `error`; read its message with
   * `Sdk.utils.error.extractApiErrorBody(error)?.errors?.$capacity?.[0]`. `create` returns the record only, so call `resource.create`
   * when the page must read `meta.series.waiting` of a repeating booking.
   *
   * @example
   * ```tsx
   * function QuickAddTask() {
   *   const tasks = Sdk.resource('...');
   *   const { create, isPending, error } = Sdk.hooks.useCreateRecord(tasks);
   *   const [title, setTitle] = React.useState('');
   *
   *   return (
   *     <div className="flex gap-2">
   *       <Sdk.Components.FormTextInput value={title} onChange={setTitle} />
   *       <Sdk.Components.Button
   *         disabled={isPending}
   *         onClick={async () => {
   *           const task = await create({ title });
   *           if (task) setTitle('');
   *         }}
   *       >
   *         Add
   *       </Sdk.Components.Button>
   *       {error ? <Sdk.Components.Alert>{error.message}</Sdk.Components.Alert> : null}
   *     </div>
   *   );
   * }
   * ```
   */
  useCreateRecord<TResource extends SdkResourceType & Pick<SdkResource, 'create' | 'capabilities'>>(
    resource: TResource,
    options?: SdkUseCreateRecordOptions<SdkResourceRecord<TResource>>
  ): SdkCreateRecordHandle<SdkResourceRecord<TResource>>;

  /**
   * Reactive `resource.update` for pages that write records without a form hook —
   * inline status toggles, kanban drag-and-drop, quick edits. Returns
   * `{ update, data, isPending, isError, error, reset }`. `update` resolves with the
   * updated record, or `null` when the request failed — it never rejects; read `error`
   * for the failure. Cached record data refreshes on success.
   *
   * Prefer `Sdk.Components.UpdateResourceRecordForm` when a schema-driven form fits.
   *
   * @example
   * ```tsx
   * function CompleteButton({ task }) {
   *   const tasks = Sdk.resource('...');
   *   const { update, isPending } = Sdk.hooks.useUpdateRecord(tasks);
   *
   *   return (
   *     <Sdk.Components.Button
   *       disabled={isPending}
   *       onClick={() => update(task.id, { status: 'complete' })}
   *     >
   *       Complete
   *     </Sdk.Components.Button>
   *   );
   * }
   * ```
   */
  useUpdateRecord<TResource extends SdkResourceType & Pick<SdkResource, 'update'>>(
    resource: TResource,
    options?: SdkUseUpdateRecordOptions<SdkResourceRecord<TResource>>
  ): SdkUpdateRecordHandle<SdkResourceRecord<TResource>>;

  /**
   * Delete flow for a single record, with the impact preview and blocked-delete error
   * body the built-in delete dialog uses. Returns `{ preview, isPreviewLoading,
   * isPreviewError, confirm, isProcessing, deleteError, error, reset }`. `error` is
   * the normalized {@link SdkApiError}; classify it with `Sdk.utils.error`. Open `fetchPreview`
   * when your confirmation UI opens so the preview reflects the moment of decision,
   * then call `confirm()`. Cached record data refreshes on success.
   *
   * Prefer `Sdk.Components.DeleteResourceRecord` when the built-in dialog fits.
   *
   * @example
   * ```tsx
   * function DeleteStudent({ recordId }) {
   *   const students = Sdk.resource('...');
   *   const [open, setOpen] = React.useState(false);
   *   const { preview, confirm, isProcessing, deleteError } = Sdk.hooks.useDeleteResourceRecord({
   *     resource: students,
   *     recordId,
   *     fetchPreview: open,
   *     onSuccess: () => setOpen(false)
   *   });
   *
   *   if (!open) return <Sdk.Components.Button onClick={() => setOpen(true)}>Delete</Sdk.Components.Button>;
   *
   *   return (
   *     <div>
   *       <p>{preview?.summary}</p>
   *       {deleteError ? <Sdk.Components.Alert>{deleteError.message}</Sdk.Components.Alert> : null}
   *       <Sdk.Components.Button disabled={isProcessing} onClick={() => confirm()}>
   *         Confirm delete
   *       </Sdk.Components.Button>
   *     </div>
   *   );
   * }
   * ```
   */
  useDeleteResourceRecord(options: {
    resource: DeleteResourceRecordResource;
    recordId: string | undefined;
    /** Open this gate when the confirmation UI opens so the preview is fetched then. */
    fetchPreview?: boolean;
    onSuccess?: () => void;
  }): DeleteResourceRecordState;

  /**
   * Restore flow for a single soft-deleted record, mirroring
   * `useDeleteResourceRecord`. Returns `{ preview, isPreviewLoading, isPreviewError,
   * confirm, isProcessing, restoreError, reset }`. Pair it with
   * `Sdk.hooks.useRecords(resource, { scope: 'archived' })` to list what can be
   * restored. Cached record data refreshes on success.
   *
   * Prefer `Sdk.Components.RestoreResourceRecord` when the built-in dialog fits.
   *
   * @example
   * ```tsx
   * function RestoreStudent({ recordId }) {
   *   const students = Sdk.resource('...');
   *   const { confirm, isProcessing } = Sdk.hooks.useRestoreResourceRecord({
   *     resource: students,
   *     recordId
   *   });
   *
   *   return (
   *     <Sdk.Components.Button disabled={isProcessing} onClick={confirm}>
   *       Restore
   *     </Sdk.Components.Button>
   *   );
   * }
   * ```
   */
  useRestoreResourceRecord(options: {
    resource: RestoreResourceRecordResource;
    recordId: string | undefined;
    /** Open this gate when the confirmation UI opens so the preview is fetched then. */
    fetchPreview?: boolean;
    onSuccess?: () => void;
  }): RestoreResourceRecordState;

  /**
   * Queue an ad hoc email through `POST /api/email-messages`. Returns
   * `{ send, data, isPending, isError, error, reset }`. `send` resolves with the
   * response, or `null` when the request failed — it never rejects, so calling it
   * straight from an `onClick` is safe; read `error` for the failure. The viewer's
   * role must hold the `send_emails` permission, or the request fails with a 403.
   *
   * Prefer `Sdk.Components.EmailComposer` when a person writes the email in the page.
   *
   * @example
   * ```tsx
   * function ResendInvoice({ invoice }: { invoice: Sdk.RecordOf<'invoices'> }) {
   *   const invoices = Sdk.resource('invoices');
   *   const { send, isPending, error } = Sdk.hooks.useSendEmail();
   *
   *   return (
   *     <div className="space-y-2">
   *       <Sdk.Components.Button
   *         disabled={isPending}
   *         onClick={() =>
   *           send({
   *             to: [{ email: invoice.email }],
   *             subject: 'Invoice {{ number }}',
   *             email_layout_id: '...',
   *             layout_data: { number: invoice.number, content: '<p>Your invoice is attached.</p>' },
   *             records: [{ id: invoice.id, resource_id: invoices.id }]
   *           })
   *         }
   *       >
   *         Resend invoice
   *       </Sdk.Components.Button>
   *       {error ? <Sdk.Components.Alert variant="destructive">{error.message}</Sdk.Components.Alert> : null}
   *     </div>
   *   );
   * }
   * ```
   */
  useSendEmail(options?: SdkUseSendEmailOptions): SdkSendEmailHandle;

  /**
   * Send a notification template through `POST /api/email-messages/send-notification`.
   * Requires notification templates and the send permissions for every selected channel.
   * An omitted delivery_channel means email. SMS goes to the recipient record’s mapped
   * phone number and only when that record opted in; otherwise history shows it as
   * suppressed. Email and SMS requires one template with both channels enabled. Uses the
   * compatible current default when no template is selected. Refreshes email and
   * notification history after success. Returns null on failure and exposes the API
   * error on the handle. Add `calendar_events: [{ resource_id, record_id }]` to attach an
   * Eventable record as a calendar invite that a later send updates or cancels.
   * Pass `idempotency_key` (for example `renewal-notice:${record.id}:initial`) when a notice
   * must reach a recipient at most once. The first send with a key in the workspace claims it;
   * every later send with that key, from a page, workflow, bulk run, or scheduled notification,
   * sends nothing and resolves with `replayed: true`. The response names the first notification
   * (`notification_history_id`, its email and SMS IDs) only when the viewer can read it:
   * with `manage_notifications` or Show access to one of its records. Build the key from the record and the notice, never from a random value or the time.
   *
   * @example
   * ```tsx
   * function WelcomeButton() {
   *   const email = Sdk.hooks.useSendNotification();
   *   return <Sdk.Components.Button disabled={email.isPending} onClick={() => email.send({
   *     notification_type_id: '01990000-0000-7000-8000-000000000001',
   *     variables: { name: 'Morgan' },
   *     to: [{ email: 'morgan@example.com' }]
   *   })}>Send welcome</Sdk.Components.Button>;
   * }
   * ```
   */
  useSendNotification(options?: SdkUseSendNotificationOptions): SdkSendNotificationHandle;

  /**
   * Read and change a notifiable record’s consent for email and SMS through
   * `GET` and `PUT /api/notification-consents`. With no `resourceId` and `recordId` it
   * manages the signed-in identity’s own consent and needs no permission. Another record
   * follows the resource’s record policies. A record that was never asked has email on and
   * SMS off, and SMS notifications to it are suppressed until it opts in.
   *
   * @example
   * ```tsx
   * function SmsOptIn() {
   *   const consent = Sdk.hooks.useNotificationConsent();
   *   if (!consent.data) return null;
   *   return (
   *     <label className="flex items-center gap-2">
   *       <input
   *         type="checkbox"
   *         checked={consent.data.channels.sms}
   *         disabled={consent.isPending}
   *         onChange={(event) => consent.update({ sms: event.target.checked })}
   *       />
   *       Text me event reminders
   *     </label>
   *   );
   * }
   * ```
   */
  useNotificationConsent(options?: SdkUseNotificationConsentOptions): SdkNotificationConsentHandle;

  /**
   * Read and change the Google calendars of one Schedulable staff record through
   * `/api/calendar-connections`. With no `resourceId` and `recordId` it manages the
   * signed-in identity's own calendars and needs no permission. Another staff record needs
   * "Manage calendar connections" and Show access to the record. A viewer connects Google
   * accounts and adds calendars only through their own accounts. Nothing is available
   * while the workspace's `calendar_sync` flag is off, for a signed-out visitor (no request
   * is made), and for an identity that is not a Schedulable record: check `available` and
   * render nothing. Every write refreshes the data and rejects with an `ApiError` on a
   * refusal: read its text with `Sdk.utils.error.apiErrorMessage(error)`.
   * `Sdk.Components.CalendarConnections` renders all of it.
   *
   * @example
   * ```tsx
   * function CalendarStatus() {
   *   const calendars = Sdk.hooks.useCalendarConnections();
   *   if (!calendars.available) return null;
   *   if (calendars.links.length === 0) {
   *     return <Sdk.Components.Button onClick={() => calendars.connect()}>Connect Google Calendar</Sdk.Components.Button>;
   *   }
   *   return (
   *     <ul>
   *       {calendars.links.map((link) => (
   *         <li key={link.id}>{link.calendar_name}: {link.message ?? 'Active'}</li>
   *       ))}
   *     </ul>
   *   );
   * }
   * ```
   */
  useCalendarConnections(options?: SdkUseCalendarConnectionsOptions): SdkCalendarConnectionsHandle;

  /**
   * Drive the two-factor challenge of a pending sign-in on this page, for a custom UI.
   * `Sdk.Components.TwoFactorChallenge` already renders it. Codes are sent only by
   * `Sdk.Components.TwoFactorSmsConsent` and `Sdk.Components.TwoFactorSendCodeButton` with
   * `flow="challenge"`, which always show the SMS terms.
   *
   * @example
   * ```tsx
   * function Challenge() {
   *   const challenge = Sdk.hooks.useTwoFactorChallenge();
   *   const [code, setCode] = React.useState('');
   *   if (!challenge.isActive || !challenge.data) return null;
   *   if (challenge.data.step === 'consent') return <Sdk.Components.TwoFactorSmsConsent flow="challenge" />;
   *   return (
   *     <>
   *       <Sdk.Components.FormCodeInput label="Code" value={code} onChange={setCode} onComplete={challenge.verifyCode} />
   *       <Sdk.Components.TwoFactorSendCodeButton flow="challenge" />
   *     </>
   *   );
   * }
   * ```
   */
  useTwoFactorChallenge(options?: SdkUseTwoFactorOptions): SdkTwoFactorChallengeHandle;

  /**
   * Drive MFA setup for a custom UI: the first setup of a pending sign-in, or a signed-in
   * member's change of MFA number. `Sdk.Components.TwoFactorSetup` already renders it.
   * Codes are sent only by `Sdk.Components.TwoFactorSmsConsent` and
   * `Sdk.Components.TwoFactorSendCodeButton` with `flow="setup"`.
   *
   * @example
   * ```tsx
   * function ChangeMfaNumber() {
   *   const setup = Sdk.hooks.useTwoFactorSetup();
   *   const [phone, setPhone] = React.useState('');
   *   if (!setup.isActive || !setup.data) return null;
   *   if (setup.data.step === 'phone') {
   *     return (
   *       <>
   *         <Sdk.Components.FormPhoneInput label="New MFA number" value={phone} onChange={setPhone} />
   *         <Sdk.Components.Button onClick={() => setup.submitPhone(phone)}>Continue</Sdk.Components.Button>
   *       </>
   *     );
   *   }
   *   return <Sdk.Components.TwoFactorSetup />;
   * }
   * ```
   */
  useTwoFactorSetup(options?: SdkUseTwoFactorOptions): SdkTwoFactorSetupHandle;

  /**
   * Read and regenerate the signed-in member's backup codes for a custom UI.
   * `Sdk.Components.TwoFactorBackupCodes` already renders it. Codes for the proof are sent
   * only by `Sdk.Components.TwoFactorSmsConsent` and `Sdk.Components.TwoFactorSendCodeButton`
   * with `flow="backupCodes"`.
   *
   * @example
   * ```tsx
   * function BackupCodeCount() {
   *   const backupCodes = Sdk.hooks.useTwoFactorBackupCodes();
   *   if (!backupCodes.data) return null;
   *   return <p>{backupCodes.data.remainingCount} unused backup codes</p>;
   * }
   * ```
   */
  useTwoFactorBackupCodes(options?: SdkUseTwoFactorOptions): SdkTwoFactorBackupCodesHandle;

  /**
   * Start background notifications for selected records or a filtered audience. The server
   * saves the audience, template, and published layout before sending. Duplicate recipients
   * on different source records receive separate emails. Save the returned ID in page state
   * or a route parameter to reopen progress with useBulkNotificationRun after navigation.
   * Each new send call creates a separate run. Do not automatically retry a failed request.
   * To make a repeated run safe, pass `idempotency_key` containing `{source_record_id}`, for
   * example `renewal-notice:{source_record_id}:initial`. Each source record gets its own key,
   * which matches a single send with the same expanded key; a source record whose key any
   * earlier notification used sends nothing and counts in `duplicate_count`.
   * @example
   * ```tsx
   * const sender = Sdk.hooks.useSendBulkNotifications();
   * const progress = Sdk.hooks.useBulkNotificationRun(sender.data?.id);
   * // From a user action:
   * await sender.send({
   *   resource_id: attendeeResourceId,
   *   notification_type_id: notificationTypeId,
   *   recipient_path: '',
   *   audience: { mode: 'records', record_ids: selectedAttendeeIds },
   *   variables: {}
   * });
   * ```
   */
  useSendBulkNotifications(): SdkSendBulkNotificationsHandle;

  /**
   * Read your saved bulk send and poll every two seconds while it is queued or processing.
   * A nullish ID keeps the query idle. Polling stops for completed, failed, or interrupted runs.
   * A completed run can contain item failures. Read queued_count, failed_count,
   * uncertain_count, and duplicate_count separately. Only the initiating identity can read this run.
   * @example
   * ```tsx
   * const progress = Sdk.hooks.useBulkNotificationRun(savedRunId);
   * return <p>{progress.data?.queued_count ?? 0} emails queued</p>;
   * ```
   */
  useBulkNotificationRun(runId: string | null | undefined): SdkApiHandle<SdkBulkNotificationRun>;

  /**
   * Queue the complete edited NotificationComposer draft without resolving its copy again.
   * The draft carries the template that preview resolved, including the type's default.
   * delivery_channel selects email (default), sms, or email_and_sms; an SMS send carries
   * the edited sms_body as written and needs the send_sms permission. The response
   * includes the notification history ID and the queued or suppressed SMS messages.
   * For a type without a recipient resource, omit recipient and supply to email objects.
   * Preview that type without recipient or to. Accepts the same `idempotency_key` as
   * useSendNotification; a repeated key sends nothing and returns `replayed: true`.
   * @example
   * const notification = Sdk.hooks.useSendResolvedNotification();
   * await notification.send({ notification_type_id: typeId, recipient: member, ...draft });
   */
  useSendResolvedNotification(options?: SdkUseSendEmailOptions): SdkSendResolvedNotificationHandle;
  /**
   * List notification types using the send_emails or send_sms permission. Results are
   * paginated. deliveryChannel keeps the types with a template for every requested channel.
   * Each type lists the `variables` a sender supplies with their label, type, and
   * required flag. Render their inputs with `Sdk.Components.NotificationVariablesForm`.
   * Call invalidate() to refresh after an administrator changes the catalog.
   * @example
   * const types = Sdk.hooks.useNotificationTypes({ recipientMode: 'notification' });
   * const choices = types.data?.data ?? [];
   */
  useNotificationTypes(
    options?: SdkNotificationTypeListOptions
  ): SdkApiHandle<SdkNotificationTypeList>;
  /**
   * List a type's templates, with is_default, is_email_enabled, and is_sms_enabled, using
   * the send_emails or send_sms permission. deliveryChannel keeps the templates that
   * support every requested channel. No request runs until notificationTypeId is set.
   * Results are paginated.
   * @example
   * const templates = Sdk.hooks.useNotificationTemplates(selectedTypeId);
   * const choices = templates.data?.data ?? [];
   */
  useNotificationTemplates(
    notificationTypeId: string | null | undefined,
    options?: SdkNotificationListOptions
  ): SdkApiHandle<SdkNotificationTemplateList>;

  /**
   * List pending and failed notifications associated with one source or recipient record.
   * Requires Show on that record; does not require send_emails. Returns scheduling metadata
   * only. can_cancel reflects current permissions; cancellation rechecks them on the server.
   * Waits until both IDs are present. Results are ordered by send time and ID.
   * @example
   * const upcoming = Sdk.hooks.useUpcomingNotifications({ resourceId, recordId, perPage: 10 });
   */
  useUpcomingNotifications(
    options: SdkUpcomingNotificationsOptions
  ): SdkApiHandle<SdkUpcomingNotifications>;

  /**
   * Schedule one notification for a notifiable record. Requires send_emails and Show on
   * the recipient and context records. Supply literal variables and an explicit template.
   * scheduled_at must be a future ISO timestamp with Z or an offset; convert local input
   * with new Date(value).toISOString(). Template values resolve again at send preparation.
   * Success refreshes upcoming-notification queries. The promise rejects with ApiError.
   * Use status: "draft" without scheduled_at to prepare an unscheduled draft. One-off drafts
   * require idempotency_key. Generated drafts use notification_schedule_id, source_record_id,
   * and stage_id. Saving a draft never releases it; use Send or transition: "schedule".
   * An `idempotency_key` makes a repeated schedule call return the first scheduled
   * notification unchanged. At send time the key also stops delivery when a send, bulk item,
   * or workflow already used it, so a manual send and a scheduled send of one notice reach the
   * recipient once.
   * @example
   * const notification = Sdk.hooks.useScheduleNotification();
   * await notification.schedule({ notification_type_id: typeId, notification_template_id: templateId, recipient, variables: {}, scheduled_at: new Date(value).toISOString() });
   */
  useScheduleNotification(): SdkScheduleNotificationHandle;

  /**
   * Cancel one pending occurrence. Requires send_emails, recipient Show, and Update on
   * the recipient for one-offs or Show and Update on the source for generated occurrences.
   * Does not change the shared schedule or opt out of future occurrences. Already queued
   * or expired items return 409. Success refreshes upcoming lists. Rejects with ApiError.
   * @example
   * const cancellation = Sdk.hooks.useCancelScheduledNotification();
   * await cancellation.cancel(notification.id, notification.revision);
   */
  useCancelScheduledNotification(): SdkCancelScheduledNotificationHandle;

  /**
   * Hold one pending occurrence. It keeps its ID, content, and time but does not send until
   * it is resumed. Send now and cancel still work while it is held. Requires the same access
   * as cancel. Repeated holds succeed. Success refreshes upcoming lists. Rejects with ApiError.
   * @example
   * const hold = Sdk.hooks.useHoldScheduledNotification();
   * await hold.hold(notification.id, notification.revision);
   */
  useHoldScheduledNotification(): SdkHoldScheduledNotificationHandle;

  /**
   * Resume a held occurrence so it sends once at its time. When that time passed during the
   * hold, pass a new future time; the old time never sends late. A generated occurrence keeps
   * the explicit time across schedule date changes. Rejects with ApiError.
   * @example
   * const resume = Sdk.hooks.useResumeScheduledNotification();
   * await resume.resume(notification.id, notification.revision, new Date(value).toISOString());
   */
  useResumeScheduledNotification(): SdkResumeScheduledNotificationHandle;

  /**
   * Load editable raw content for one occurrence. Waits when id is nullish.
   * Requires send_emails and Show/Update on the one-off recipient, or Show/Update
   * on the generated source and Show on its recipient. Context records are not returned.
   * @example
   * const detail = Sdk.hooks.useScheduledNotification(selectedId);
   */
  useScheduledNotification(
    scheduledNotificationId: string | null | undefined
  ): SdkApiHandle<SdkScheduledNotificationDetail>;

  /**
   * Edit a draft, scheduled, or failed occurrence. Pass its expected_revision.
   * A null content_override restores the template. Failed preparation recovers only
   * after successful resolution. Refreshes detail and all upcoming record lists.
   * @example
   * const editing = Sdk.hooks.useUpdateScheduledNotification();
   * await editing.update(id, { expected_revision: notification.revision, content_override: null });
   */
  useUpdateScheduledNotification(): SdkUpdateScheduledNotificationHandle;

  /**
   * Apply edits and send the same draft, scheduled, or failed occurrence now, including
   * paused occurrences. The original time cannot send it again. Rejects terminal
   * items with 409. Refreshes detail, upcoming lists, and email history.
   * @example
   * const sending = Sdk.hooks.useSendScheduledNotificationNow();
   * await sending.sendNow(id, { expected_revision: notification.revision });
   */
  useSendScheduledNotificationNow(): SdkSendScheduledNotificationNowHandle;

  /**
   * Read tracked email rows, including ad hoc and template emails, for custom tables.
   * Uses the same scope, filters, permissions, and send invalidation as EmailMessages.
   * Each email appears once even when linked to several scoped records. Empty records
   * leave the query idle. Render isError before treating missing data as an empty result.
   * Changing scope, filters, or page clears the previous rows while the query loads.
   * Pagination covers emails only: merge and paginate other activity sources explicitly.
   * @example
   * const emails = Sdk.hooks.useEmailMessages({
   *   records: [{ resourceId: businessResource.id, recordId: business.id }],
   *   page: 1,
   *   perPage: 20
   * });
   */
  useEmailMessages(options: SdkEmailMessagesOptions): SdkApiHandle<SdkEmailMessages>;

  /**
   * List notifications by lifecycle status: upcoming, sent, failed, cancelled, or expired.
   * Pass `records` for record-scoped history (Show access to each record) or `workspace`
   * for every notification (`manage_notifications`). Filters narrow the scope and follow
   * the admin notification list. Sent rows keep saved names after template edits.
   * @example
   * const history = Sdk.hooks.useNotificationHistory({ records: [{ resourceId, recordId }], status: 'sent' });
   */
  useNotificationHistory(
    options: SdkNotificationHistoryOptions
  ): SdkApiHandle<SdkNotificationHistory>;

  /**
   * Load one notification and its email attempts with delivery events. Pass the list row's
   * `kind` and `id`. Waits when either is nullish. Reads require `manage_notifications` or
   * Show access to a linked record. Load an attempt body with `Sdk.Components.NotificationHistory`
   * or `Sdk.Components.EmailMessages`.
   * @example
   * const detail = Sdk.hooks.useNotificationHistoryDetail(selected?.kind, selected?.id);
   */
  useNotificationHistoryDetail(
    kind: SdkNotificationHistoryKind | null | undefined,
    notificationId: string | null | undefined
  ): SdkApiHandle<SdkNotificationHistoryDetail>;

  /**
   * Form hook for creating a resource record via the JSON API.
   * Returns `{ form, FieldInput, Form }` — a handle with form state, a context-wired field
   * input component, and a submit-pre-wired form wrapper.
   *
   * @aiHidden Kept on the runtime SDK for existing pages, but omitted from the generated
   * SDK reference so the code-builder agent is steered to `Sdk.Components.CreateResourceRecordForm`.
   *
   * @example
   * ```tsx
   * function CreateStudent() {
   *   const students = Sdk.resource('...');
   *   const { form, FieldInput, Form } = Sdk.hooks.useCreateResourceRecordForm({
   *     resource: students,
   *     onSuccess: (record) => console.log('Created', record)
   *   });
   *
   *   return (
   *     <Form className="grid gap-4">
   *       <FieldInput field={students.fields.first_name} />
   *       <FieldInput field={students.fields.email} />
   *       <button type="submit" disabled={form.isSubmitting}>Create</button>
   *     </Form>
   *   );
   * }
   * ```
   */
  useCreateResourceRecordForm(options: {
    resource: CreateResourceRecordFormResource;
    fields?: SdkResourceField[];
    /** Editable seed values — pre-fill fields the user can still change. */
    initialValues?: Record<string, unknown>;
    /** Locked writable Resource field values — rendered disabled and submitted even when
     * omitted from `fields`; unknown, system, virtual, or inaccessible keys are ignored. */
    fixedValues?: Record<string, unknown>;
    /** Include every creatable field, ignoring per-field `show_on_creating` form
     * visibility. Still excludes system/virtual fields and respects field access. */
    includeAllFields?: boolean;
    /** How relation fields resolve inline attach/create. Defaults to `'settings'`;
     * pass `'always'` to force them on. */
    relationVisibility?: RelationVisibility;
    /** Send a booking idempotency key, kept across retries of the same values. Defaults to
     * `false`. Submitting throws when the resource has no booking idempotency field. */
    idempotent?: boolean;
    onSuccess?: (record: ResourceRecord) => void;
    /** Called when a submit fails, after the form shows its errors. */
    onError?: (error: unknown) => void;
  }): UseCreateResourceRecordFormResult;

  /**
   * Form hook for updating an existing resource record via the JSON API.
   * Loads the record and seeds the form before rendering fields.
   * Returns `{ form, FieldInput, Form, interaction }` — same shape as the create hook,
   * with an additional `form.isLoading` flag while the record is being fetched and a
   * ready-to-render `interaction` node for capability-aware pre-submit prompts.
   * Direct hook consumers must render `interaction` next to `Form`; otherwise recurring
   * eventable updates can pause at the scope prompt without showing it.
   *
   * @aiHidden Kept on the runtime SDK for existing pages, but omitted from the generated
   * SDK reference so the code-builder agent is steered to `Sdk.Components.UpdateResourceRecordForm`.
   *
   * @example
   * ```tsx
   * function EditStudent({ recordId }) {
   *   const students = Sdk.resource('...');
   *   const { form, FieldInput, Form, interaction } = Sdk.hooks.useUpdateResourceRecordForm({
   *     resource: students,
   *     recordId,
   *     onSuccess: (record) => console.log('Updated', record)
   *   });
   *
   *   if (form.isLoading) return <Sdk.Components.Skeleton />;
   *
   *   return (
   *     <>
   *       <Form className="grid gap-4">
   *         <FieldInput field={students.fields.first_name} />
   *         <FieldInput field={students.fields.email} />
   *         <button type="submit" disabled={form.isSubmitting}>Save</button>
   *       </Form>
   *       {interaction}
   *     </>
   *   );
   * }
   * ```
   */
  useUpdateResourceRecordForm(options: {
    resource: UpdateResourceRecordFormResource;
    recordId: string | null | undefined;
    fields?: SdkResourceField[];
    /** Editable seed values applied before the record loads (or when no `recordId`). */
    initialValues?: Record<string, unknown>;
    /** Locked writable Resource field values — rendered disabled and submitted even when
     * omitted from `fields`; unknown, system, virtual, or inaccessible keys are ignored. */
    fixedValues?: Record<string, unknown>;
    /** Include every updatable field, ignoring per-field `show_on_updating` form
     * visibility. Still excludes system/virtual fields and respects field access. */
    includeAllFields?: boolean;
    /** How relation fields resolve inline attach/create. Defaults to `'settings'`;
     * pass `'always'` to force them on. */
    relationVisibility?: RelationVisibility;
    onSuccess?: (record: ResourceRecord) => void;
  }): UseUpdateResourceRecordFormResult;

  /**
   * Schema-aware workflow form hook — the headless counterpart of
   * `Sdk.Components.WorkflowForm`. Resolves a
   * published Request workflow's input schema (query fields for GET, body fields for
   * POST/PUT/PATCH, none for DELETE) and returns `{ form, FieldInput, Form, fields,
   * method, isAvailable }`. Reach for it when the form UI must escape the component's
   * managed shell — e.g. a submit button rendered outside the `<form>`, a bespoke
   * multi-step wizard, or hand-wired inputs bound through `form.data`/`form.setData`.
   * Prefer `Sdk.Components.WorkflowForm` when the default managed form fits.
   * `form.errors` contains only 422 field validation. Other failures are available
   * as `form.failure`; for example, branch on
   * `Sdk.utils.error.isConflict(form.failure)` without inspecting status codes.
   * Success callbacks receive the parsed workflow body directly. Synchronous
   * output can be a scalar, list, record, or object; asynchronous workflows return
   * `{ execution_id }`. Narrow the value before reading authored output fields.
   * Match `initialValues` keys to the workflow's expected `request.body` keys;
   * `initialValues` is captured once on mount, so when seeding from fetched data gate
   * rendering until it has loaded. Rich text inputs use `Sdk.Components.FormContentInput`
   * initialized as TipTap JSON. Markdown inputs use `Sdk.Components.FormMarkdownInput`;
   * disable submit while `form.isSubmitting` or `!form.isReady`.
   *
   * @example
   * ```tsx
   * function InvitePanel() {
   *   const { form, FieldInput, Form, fields, isAvailable } = Sdk.hooks.useWorkflowForm({
   *     workflowId: 'send-invite'
   *   });
   *
   *   if (!isAvailable) return null;
   *
   *   return (
   *     <div className="grid gap-4">
   *       <Form className="grid gap-4">
   *         {fields.map((field) => <FieldInput key={field.key} field={field} />)}
   *       </Form>
   *       <Sdk.Components.Button onClick={form.submit} disabled={form.isSubmitting}>
   *         Send invite
   *       </Sdk.Components.Button>
   *     </div>
   *   );
   * }
   * ```
   */
  useWorkflowForm(options: {
    /** The workflow's slug or ID. Both values are stable across title renames. */
    workflowId: WorkflowReference;
    /** Route parameter values for parameterized published routes
     * (e.g. `/invites/{id}` needs `params: { id: recordId }`). */
    params?: Record<string, unknown>;
    /** Input-field keys controlling which schema fields are exposed and their order.
     * Omit for every field from the workflow's input schema; unknown keys are ignored. */
    fields?: string[];
    /** Editable seed values — captured once on mount; they form the `reset()` baseline. */
    initialValues?: Record<string, unknown>;
    /** Locked values — rendered as disabled fields and always submitted. */
    fixedValues?: Record<string, unknown>;
    /** Defaults to `true` for mutation workflows and `false` for GET workflows, so a
     * search form keeps its filters. */
    resetOnSuccess?: boolean;
    onSuccess?: (response: SdkWorkflowResponse) => void;
    onError?: (context: { status: number; data: unknown; error: unknown }) => void;
  }): UseWorkflowFormResult;

  /**
   * Form state for editing workspace field values, for layouts that must escape the
   * managed `Sdk.Components.WorkspaceFieldsForm` shell. Submits to
   * `PUT /api/data/workspace-fields/values`; requires `update_workspace_fields` or `manage_workspace_fields`
   * permission.
   *
   * @example
   * ```tsx
   * const { fields } = Sdk.workspace!;
   * const { form, canSubmit, FieldInput, Form } = Sdk.hooks.useWorkspaceFieldsForm({ fields: [fields.support_email] });
   * return (
   *   <Form>
   *     <FieldInput field={fields.support_email} />
   *     <Sdk.Components.Button type="submit" disabled={form.isSubmitting || !canSubmit}>Save</Sdk.Components.Button>
   *   </Form>
   * );
   * ```
   */
  useWorkspaceFieldsForm(options: SdkUseWorkspaceFieldsFormOptions): UseWorkspaceFieldsFormResult;

  /**
   * Imperatively runs a published Request workflow while managing reactive
   * `{ isPending, errorMessage, failure, result }` state. Use this for button-triggered
   * actions that do not need schema-driven form state. `run(inputs)` resolves with
   * the parsed workflow body on success and `null` on failure; it never rejects.
   * Only the latest concurrent run may update the returned state.
   *
   * Set `idempotent: true` to add a generated UUID under the conventional
   * `idempotency_key` input. Identical retries keep the key, while changed inputs or
   * route parameters rotate it, including the conflict/failure -> edit -> retry flow.
   * `reset()` also starts a fresh key lifecycle. The hook does not poll asynchronous
   * executions.
   *
   * @example
   * ```tsx
   * function ReserveButton({ slotId }) {
   *   const action = Sdk.hooks.useWorkflowAction({
   *     workflowId: 'reserve-slot',
   *     idempotent: true
   *   });
   *
   *   return (
   *     <div>
   *       <Sdk.Components.Button
   *         disabled={action.isPending}
   *         onClick={() => action.run({ slot_id: slotId })}
   *       >
   *         Reserve
   *       </Sdk.Components.Button>
   *       {Sdk.utils.error.isConflict(action.failure) ? (
   *         <Sdk.Components.Alert>That slot was just taken. Choose another.</Sdk.Components.Alert>
   *       ) : action.errorMessage ? (
   *         <Sdk.Components.Alert>{action.errorMessage}</Sdk.Components.Alert>
   *       ) : null}
   *     </div>
   *   );
   * }
   * ```
   */
  useWorkflowAction(options: {
    /** The workflow's slug or ID. Both values are stable across title renames. */
    workflowId: WorkflowReference;
    /** Reactive route parameter values for parameterized published routes
     * (e.g. `/invites/{id}` needs `params: { id: recordId }`). */
    params?: Record<string, unknown>;
    /** Generate and manage the conventional `idempotency_key` input. */
    idempotent?: boolean;
  }): UseWorkflowActionResult;

  /**
   * Reactive timelines of a record's schedule workflow runs, the data behind
   * `Sdk.Components.RecordScheduleDisplay`, for pages that render a custom
   * schedule UI. For each published record-schedule workflow the record participates
   * in, returns the anchor date and every offset in chronological order with its
   * state (`pending`, `sent`, `skipped`, or `manually_skipped`) and an `is_next`
   * flag on the upcoming run. Show `manually_skipped` offsets distinctly from
   * automatic skips. Call `skipOffset({ workflowId, offset, scheduledRunAt })` to
   * skip one upcoming pending occurrence. Recomputing the same date preserves its
   * skip; a changed reminder date does not inherit the old skip.
   * Skipping requires Show and Update access to the record. Viewers only see the
   * schedule of records they are allowed to see. Prefer the component when the
   * default timeline rendering fits.
   *
   * @example
   * ```tsx
   * function NextReminder({ invoiceId }) {
   *   const { timelines, isLoading } = Sdk.hooks.useRecordScheduleTimelines({
   *     resourceId: '0192a1b2-c3d4-7e5f-8a6b-9c0d1e2f3a4b',
   *     recordId: invoiceId
   *   });
   *
   *   if (isLoading) return <Sdk.Components.Skeleton />;
   *
   *   const next = timelines
   *     .flatMap((timeline) => timeline.offsets)
   *     .find((offset) => offset.is_next);
   *
   *   return <p>{next ? `Next reminder: ${next.scheduled_run_at}` : 'Nothing scheduled'}</p>;
   * }
   * ```
   */
  useRecordScheduleTimelines(
    options: SdkUseRecordScheduleTimelinesOptions
  ): SdkRecordScheduleTimelinesHandle;

  /**
   * Builds a client-side table from seeded, in-memory data (no resource). Sorting,
   * search, and pagination run in the browser. Pair the returned `table` with
   * `Sdk.Components.DataTable`.
   *
   * @example
   * ```tsx
   * const { table } = Sdk.hooks.useDataTable({
   *   data: rows,
   *   columns: [{ accessor: 'name', header: 'Name' }, { accessor: 'total', header: 'Total' }]
   * });
   * return <Sdk.Components.DataTable table={table} searchable />;
   * ```
   */
  useDataTable<TData extends Record<string, unknown>>(
    options: UseDataTableOptions<TData>
  ): UseDataTableResult<TData>;

  /**
   * Batteries-included resource-backed table. Creates columns for every Index-scope field
   * the viewer can access, uses `initialFields` to select which start visible, wires
   * server-side search / sort / pagination / filters, fetches records, and returns a
   * TanStack `table` for `Sdk.Components.DataTable`. Use `fieldOverrides` for the uncommon
   * field that needs a custom cell. Each cell override receives the row plus row-bound
   * `FieldValue` / `FieldLabel` helpers. Relation field overrides receive the preloaded relation
   * value on the typed row. An override for a field that policy removes does not run.
   *
   * The `columns` option provides complete column control. It accepts field-handle shorthand,
   * an explicit field column (`{ field, header?, enableSorting?, width?, className? }`), or a
   * custom column. Cells wrap at 240px unless the column sets `width`, so one long name or email
   * cannot push the other columns out of view. A `max-w-*` class in `className` replaces the limit.
   * Nested relation field handles (e.g. `students.fields.family.fields.name`) load their
   * relations automatically, but cannot sort.
   * Custom columns that read relation properties from `row` should also list those paths in
   * `relations`; this keeps the callback's record type aligned with its requested data.
   * Resource columns never take react-table `{ accessor, header }` objects — that shape is
   * `useDataTable`-only (seeded in-memory data) and fails the save-time type check here.
   *
   * Authored `filters` narrow every query invisibly; filter relation fields by nested
   * scalar paths such as `family.id`, not the relation object path (`family`). The
   * returned `filters` object powers the end-user advanced-filters dialog — pass it to
   * `Sdk.Components.DataTable`'s `filters` prop to show the "Filters" toolbar button.
   *
   * @example
   * ```tsx
   * const students = Sdk.resource('students');
   * const { table, isLoading, isError, refetch, filters } = Sdk.hooks.useResourceDataTable({
   *   resource: students,
   *   initialFields: [students.fields.email, students.fields.family, students.fields.status],
   *   fieldOverrides: {
   *     family: {
   *       cell: (row) => <strong>{row.family.name}</strong>
   *     }
   *   },
   *   filters: { operator: 'and', conditions: [{ field: 'status', operator: '=', value: 'active' }] }
   * });
   *
   * return (
   *   <Sdk.Components.DataTable
   *     table={table}
   *     isLoading={isLoading}
   *     isError={isError}
   *     onRetry={refetch}
   *     searchable
   *     filters={filters}
   *     onRowClick={(row) => Sdk.router.visit(students.urls.show(row.id))}
   *   />
   * );
   * ```
   */
  useResourceDataTable<
    TResource extends ResourceDataTableResource,
    const TRelations extends readonly SdkResourceRelationPath<NoInfer<TResource>>[] = readonly []
  >(
    options: UseResourceDataTableOptions<TResource, TRelations>
  ): UseResourceDataTableResult<SdkResourceRecord<TResource, TRelations>>;

  /**
   * Manages calendar view mode, focal date, the visible window, title, and navigation.
   * Pair its `windowStart` and `windowEnd` with `useCalendarEvents`.
   *
   * @example
   * ```tsx
   * const cal = Sdk.hooks.useCalendarView({ initialView: 'month' });
   * return <Sdk.Components.Calendar events={events} view={cal.view} date={cal.date} />;
   * ```
   */
  useCalendarView(options?: {
    initialView?: 'month' | 'week' | 'day';
    initialDate?: Date;
  }): UseCalendarViewResult;

  /**
   * Fetches calendar events for a resource and normalises them to the canonical event shape.
   * Pass a stable `from`/`to` window, such as `useCalendarView`'s `windowStart`/`windowEnd`.
   * A window built from `new Date()` during render changes on every render and makes the
   * hook request without end.
   *
   * A resource whose relations point at Schedulable records returns one event for each
   * related Schedulable record, with the same `id`. A booking of a therapist, a room, and a
   * client is three events, one for each calendar, and `event.record.$calendar.id` names the
   * calendar. Show one calendar at a time with a selector, or keep the first event of each
   * `id`.
   *
   * @example
   * ```tsx
   * const { events, isLoading } = Sdk.hooks.useCalendarEvents({
   *   resourceId: 'YOUR_RESOURCE_ID',
   *   from: cal.windowStart,
   *   to: cal.windowEnd
   * });
   * const seen = new Set<string>();
   * const oneEventPerBooking = events.filter((event) => {
   *   if (seen.has(event.id)) return false;
   *   seen.add(event.id);
   *   return true;
   * });
   * ```
   */
  useCalendarEvents<
    TResourceId extends string,
    const TRelations extends readonly SdkCatalogRelationPath<TResourceId>[] = readonly []
  >(
    options: Omit<UseCalendarEventsOptions, 'relations' | 'resourceId'> & {
      resourceId: TResourceId;
      relations?: TRelations;
    }
  ): UseCalendarEventsResult<SdkCatalogRecord<TResourceId, TRelations>>;

  /**
   * Manages an optimistic placeholder event while a calendar create flow is open.
   *
   * @example
   * ```tsx
   * const newEvent = Sdk.hooks.useNewCalendarEvent({ placeholderTitle: 'New Lesson' });
   * return <Sdk.Components.Calendar events={newEvent.mergeEvents(events)} />;
   * ```
   */
  useNewCalendarEvent(options?: UseNewCalendarEventOptions): UseNewCalendarEventResult;

  /**
   * Hook that loads the schedulable resources ("calendars" — staff, rooms, users) tied to an
   * eventable resource, owns the multi-select state, and composes the selection into a `filters`
   * object ready to hand to `Sdk.hooks.useCalendarEvents`.
   *
   * @remarks
   * Returns `groups` (the selectable calendars, grouped by schedulable resource), `isLoading`,
   * `selectedIds`, `setSelectedIds`, and `filters`. By default every calendar is selected.
   * Pass `groups`/`isLoading`/`selectedIds`/`setSelectedIds` to `Sdk.Components.CalendarSelector`
   * and pass `filters` straight to `Sdk.hooks.useCalendarEvents({ filters })` — the OR-group fan-out
   * across each calendar's relation paths is handled for you. Selection is in-memory and resets
   * on reload. Provide your own base `filters` to AND additional constraints onto the query.
   *
   * @example
   * ```tsx
   * const cal = Sdk.hooks.useCalendarView();
   * const calendars = Sdk.hooks.useCalendarSelection({ resourceId: 'SESSIONS_ID' });
   * const { events } = Sdk.hooks.useCalendarEvents({
   *   resourceId: 'SESSIONS_ID',
   *   from: cal.windowStart,
   *   to: cal.windowEnd,
   *   filters: calendars.filters
   * });
   *
   * return (
   *   <div>
   *     <Sdk.Components.CalendarSelector
   *       groups={calendars.groups}
   *       isLoading={calendars.isLoading}
   *       selectedIds={calendars.selectedIds}
   *       onChange={calendars.setSelectedIds}
   *     />
   *     <Sdk.Components.Calendar events={events} view={cal.view} date={cal.date} />
   *   </div>
   * );
   * ```
   */
  useCalendarSelection(options: UseCalendarSelectionOptions): UseCalendarSelectionResult;

  /**
   * Reactive workspace field values for the given keys. Returns one object keyed by the
   * requested keys; an unknown key is `undefined`. Values update after a
   * `WorkspaceFieldsForm` save on this page. Prefer this over `Sdk.workspace.values`,
   * which is a load-time snapshot.
   *
   * The page type check gives each requested value its workspace field's type from the
   * workspace field catalog (`index_workspace_fields` lists the keys and types): for
   * example, a text or email field reads as a `string`. Use the value as that type. Do not
   * add `typeof` checks; the `unknown` in the signature applies only to keys outside the
   * catalog.
   *
   * @example
   * ```tsx
   * const { registration_mode, admin_notification_email } = Sdk.hooks.useWorkspaceValues([
   *   'registration_mode',
   *   'admin_notification_email'
   * ]);
   * ```
   */
  useWorkspaceValues<K extends string>(keys: readonly K[]): Record<K, unknown>;

  /**
   * Reads the sidebar shell state from the nearest `Sdk.Components.SidebarProvider`.
   * Returns the expanded/collapsed `state`, mobile flags, and `toggleSidebar` for
   * building collapse controls. Throws when called outside a SidebarProvider.
   *
   * @example
   * ```tsx
   * function CollapseButton() {
   *   const { state, toggleSidebar } = Sdk.hooks.useSidebar();
   *   const collapsed = state === 'collapsed';
   *   return (
   *     <Sdk.Components.SidebarMenuButton onClick={toggleSidebar}>
   *       <Sdk.Components.Icon name={collapsed ? 'PanelLeftOpen' : 'PanelLeftClose'} className="size-4" />
   *       <span>{collapsed ? 'Expand' : 'Collapse'}</span>
   *     </Sdk.Components.SidebarMenuButton>
   *   );
   * }
   * ```
   */
  useSidebar(): {
    state: 'expanded' | 'collapsed';
    open: boolean;
    setOpen: (open: boolean) => void;
    openMobile: boolean;
    setOpenMobile: (open: boolean) => void;
    isMobile: boolean;
    toggleSidebar: () => void;
  };

  /**
   * Headless state machine for multi-step / wizard forms. Tracks the active step and exposes
   * free navigation (`goTo`/`next`/`back`) — no validation or gating. Use this for custom step
   * chrome; for the default indicator + Previous/Next UI, use `Sdk.Components.Stepper` instead.
   *
   * @aiHidden Kept on the runtime SDK for custom-chrome pages, but omitted from the generated
   * SDK reference — the code-builder agent should use the `steps` prop on
   * `Sdk.Components.CreateResourceRecordForm` / `UpdateResourceRecordForm` for multi-step
   * record forms, or `Sdk.Components.Stepper` for wizards that are not record forms.
   *
   * @example
   * ```tsx
   * const { step, steps, label, next, back, isFirst, isLast } = Sdk.hooks.useStepper([
   *   'General', 'Education', 'Review'
   * ]);
   * ```
   */
  useStepper(steps: readonly string[], options?: UseStepperOptions): UseStepperResult;

  /**
   * Persist JSON-safe UI state in the browser. Returns `[value, setter]`. The setter
   * accepts a value or functional update; setting `null` removes the stored value.
   * Persistence and synchronization follow the repository's standard local-storage hook.
   *
   * The supplied key is used directly. Choose a stable, page-specific key to avoid
   * collisions with other pages or application features.
   *
   * Use only for non-sensitive UI preferences such as dismissed notices, selected tabs,
   * filters, and display density. Never store secrets, authentication data or tokens,
   * PII, resource records, or sensitive drafts in local storage. Logout clears all stored
   * values.
   *
   * @example
   * ```tsx
   * const [density, setDensity] = Sdk.hooks.useLocalStorage('table-density', 'comfortable');
   *
   * setDensity('compact');
   * setDensity((current) => current === 'compact' ? 'comfortable' : 'compact');
   * setDensity(null); // Remove the stored value.
   * ```
   */
  useLocalStorage(
    key: string,
    initial: string
  ): readonly [value: string | null, setter: SdkLocalStorageSetter<string>];
  useLocalStorage(
    key: string,
    initial: boolean
  ): readonly [value: boolean | null, setter: SdkLocalStorageSetter<boolean>];
  useLocalStorage(
    key: string,
    initial: number
  ): readonly [value: number | null, setter: SdkLocalStorageSetter<number>];
  useLocalStorage<TValue extends SdkLocalStorageValue>(
    key: string,
    initial: TValue
  ): SdkLocalStorageResult<TValue>;

  /**
   * Read and control the app theme (system / light / dark). Returns the resolved `theme`
   * currently applied — reactive, so the component re-renders when it changes — plus
   * the saved `mode` preference (`'system'` follows the device setting) and the
   * `setTheme` / `toggleTheme` setters. `toggleTheme` cycles system → dark → light.
   * The choice persists across visits and applies to the whole app, not just the current
   * page. For the standard toggle button, use `Sdk.Components.ThemeToggle` — reach for
   * this hook when the page needs custom theme UI.
   *
   * @example
   * ```tsx
   * function ThemeToggle() {
   *   const { mode, toggleTheme } = Sdk.hooks.useTheme();
   *
   *   return (
   *     <Sdk.Components.Button variant="ghost" onClick={toggleTheme}>
   *       Theme: {mode}
   *     </Sdk.Components.Button>
   *   );
   * }
   * ```
   */
  useTheme(): UseThemeResult;
};

export type ApiError = Error & {
  readonly status: number;
  readonly body: unknown;
};

export const ApiError = class ApiError extends Error {
  readonly status: number;
  readonly body: unknown;

  constructor(status: number, body: unknown, message?: string) {
    super(message ?? `Request failed with status ${status}`);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
};
